/**
 * components/wizard/AnchorCollectMode.jsx — 🆕 v1 (2026-07).
 *
 * Anker-Sammelmodus (Data-Flywheel): schneller Loop, um 5–10
 * Standalone-Anker pro Match zu setzen. Jeder Anker = (Zeitpunkt,
 * 4 Klicks eines FLÄCHEN-Presets) → eigene Homographie via DLT →
 * persistiert als `extra_zone` mit status="joint",
 * source="anchor_collect". Der Pseudo-Label-Export nimmt jeden
 * Anker automatisch als gelabeltes Real-Trainingssample auf.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Anchor, Check, ChevronLeft, ChevronRight, Crosshair, Pencil, RotateCcw, Ruler, Trash2, UploadCloud,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import AnchorWorklist from "@/components/wizard/AnchorWorklist";
import PipelinePanel from "@/components/common/PipelinePanel";
import KonsensPanel from "@/components/common/KonsensPanel";
import FieldFitVorschlag from "@/components/common/FieldFitVorschlag";
import NetzSelfcalVorschlag from "@/components/common/NetzSelfcalVorschlag";
// 🆕 2026-08-31 · siehe lib/videoTime.js — Sekunden UND Minuten in allen
// drei Tabellen, aus einer Quelle.
import { bothSpans, bothTimes } from "@/lib/videoTime";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  anchorPreviewUrl, createAnchor, deleteAnchor, exportPseudoLabels, fitFieldDims, frameUrl, getP3PLandmarks,
  getShots, listAnchors, listLineCombos, pollJob, resolveSegOnlyAnchors, updateCameraConfig,
  detectShots, adaptFieldDims, getShotSelfcal, runShotSelfcal, getAnchorCoverage,
  classifyShots, setShotClass, trackCamera, confirmReplayLink, autoCalibrateAll,
  splitShot, unsplitShot, mergeShotWithPrev, unmergeShot,
  scanCutsInShot, reprojectSegments, lineProposal,
  scanShotAnchorCandidates,
  fehlerText,
} from "@/lib/api";
import { FIELD_PRESETS } from "@/lib/fieldPresets";
import { clickGuidanceFor } from "@/lib/clickGuidance";
import {
  FreeDrawOverlay, FREE_LINE_CLASSES, HOTKEY_CYCLE, LINE_CLASS_META,
  LineClassPitch, VERTICAL_CLASSES,
} from "./FreeDrawTools";
import { P3PLandmarkChips, P3POverlay, fovSourceLabel } from "./P3PTools";
import CameraCoverageBar from "./CameraCoverageBar";
import { FrameLoupe, FrameZoomControls, useFrameZoom } from "./FrameZoom";
import { PreviewTile } from "@/components/common/PreviewTile";
import KlicklisteLeiste from "./KlicklisteLeiste";
import {
  LinienpruefungEbene, LinienpruefungLeiste, useLinienpruefung,
} from "./Linienpruefung";
import {
  ampelMeldung, leerenBeiPunktwechsel, selfcalWartenS, vorschauLoesen,
} from "@/lib/klickliste";

const LABELS_WIDTH = [
  "① Ecke · OBEN LINKS", "② Ecke · OBEN RECHTS",
  "③ Ecke · UNTEN RECHTS", "④ Ecke · UNTEN LINKS",
];

// Linienfarbe schaltet nach jeweils 2 Klicks um (Spec v1.2)
const LINE_COLORS = ["#22d3ee", "#fbbf24", "#a3e635"];

const TARGET_ANCHORS = 10;

// 🆕 2026-08-23 · Spiegelt MIN_PART_S aus backend/modules/shot_split.py.
// Zehn Frames bei 25 fps — darunter ist es kein Shot, sondern ein
// Klickfehler. Hier nur, damit der Knopf aus ist, statt nach dem Klick
// eine 422 zu zeigen; die verbindliche Prüfung macht das Backend.
const MIN_SPLIT_PART_S = 0.4;

// 🆕 2026-08 · Shot-Classifier Prototyp: Anzeige-Metadaten pro Klasse
const SHOT_CLASS_META = {
  wide:     { label: "Wide",     dot: "🟢", cls: "text-emerald-300 border-emerald-400/50 bg-emerald-400/10" },
  close:    { label: "Close",    dot: "🟡", cls: "text-amber-300 border-amber-300/50 bg-amber-300/10" },
  replay:   { label: "Replay",   dot: "🔴", cls: "text-rose-300 border-rose-400/50 bg-rose-400/10" },
  unsicher: { label: "Unsicher", dot: "⚪", cls: "text-zinc-300 border-zinc-400/50 bg-zinc-400/10" },
  ads:      { label: "Ads",      dot: "🟣", cls: "text-purple-300 border-purple-400/50 bg-purple-400/10" },
  np:       { label: "No Play",  dot: "⏸", cls: "text-slate-300 border-slate-400/50 bg-slate-400/10" },
  // 🆕 20.10.2026 · Auftrag 65, L2: weite Kamera, Scheibe nicht im Spiel (Anker-Soll/Netz-Ernte zählen nur "wide")
  wide_np:  { label: "Wide ohne Spiel", dot: "◐", cls: "text-teal-300 border-teal-400/50 bg-teal-400/10" },
  // 🆕 04.09. · zweite Kamera (Luftbild) — bleibt aus der Stativ-
  // Kalibrierung (Anker-Soll, Netz-Ernte) heraus; Kameragruppen-
  // Kalibrierung ist Fahrplan-Material.
  drone:    { label: "Drohne",   dot: "🚁", cls: "text-sky-300 border-sky-400/50 bg-sky-400/10" },
};

// Verbindliche Klassen-Definition (04.09., mit Alexander festgelegt am
// Fall Shot 33: Nahaufnahme nach Turnover, Scheibe noch nicht wieder im
// Spiel -> np, nicht close). Entscheidungsreihenfolge im Konfliktfall:
// 1. Wiederholung?  -> replay   2. Werbung/Grafik? -> ads
// 3. Scheibe NICHT im Spiel (Aufstellung, nach Turn bis Pickup, nach
//    Punkt, Timeout, Verletzung, Publikum)? -> np, egal welche Kamera
// 4. erst DANN entscheidet die Kameraführung: wide oder close.
const KLASSEN_DEFINITION = {
  wide: "weite Feldkamera (Stativ), Scheibe im Spiel, Feldlinien sichtbar (Anker/Meter/Taktik-Material)",
  close: "Scheibe im Spiel, aber nahe Kamera — Spielgeschehen ohne Feldüberblick",
  replay: "Wiederholung/Zeitlupe — nicht live, egal wie das Bild aussieht",
  ads: "Werbung oder Vollbild-Grafik",
  np: "Scheibe NICHT im Spiel: Aufstellung vor dem Pull, nach Turnover bis zum Pickup, nach dem Punkt, Timeout, Verletzung — egal welche Kamera",
  drone: "Luftbild von der Drohne (2. Kamera) — gehört NICHT in die Stativ-Kalibrierung, auch wenn das Feld weit zu sehen ist",
  wide_np: "weite Feldkamera (Stativ), Scheibe NICHT im Spiel (Aufstellung, Pause, Timeout) — kein Spiel, zählt nicht als wide für Anker-Soll und Netz-Ernte",
};

// 🆕 2026-08-22 · Klartext für die Prüfliste (shot_label_quarantine)
const REVIEW_FIELD_LABEL = {
  shot_class_overrides: "Shot-Klasse",
  shot_fov_overrides: "Blickwinkel (FOV)",
  replay_confirmations: "Bestätigter Replay-Link",
};

function shotClassTooltip(s) {
  const a = s.class_auto;
  if (!a) return "Noch nicht klassifiziert — »Shots klassifizieren« ausführen";
  const f = a.features || {};
  const parts = [
    `Auto: ${SHOT_CLASS_META[a.label]?.label || a.label} (Konfidenz ${Math.round((a.confidence || 0) * 100)} %)`,
    `Feldgrün ${Math.round((f.green_ratio || 0) * 100)} % · Feldlinien ${f.long_lines ?? 0}`,
    f.person_h_med != null
      ? `Personen-Höhe median ${Math.round(f.person_h_med * 100)} % Frame (${f.n_persons_med} Pers.)`
      : "keine Personen erkannt",
    `Zeitlupen-Duplikate ${Math.round((f.dup_ratio || 0) * 100)} %`,
  ];
  // 🆕 Auto-NP: automatisch als Steh-Phase erkannt (kein Pass-Release +
  // geringe Bewegung + Mindestdauer) — Dropdown-Override gewinnt immer.
  if (s.np_suggestion) {
    parts.unshift(`⏸ Auto-NP erkannt: keine Pässe im Zeitfenster · geringe Bewegung (${s.np_suggestion.motion_med}) · ${s.np_suggestion.duration_s} s — automatisch „np" (vorher: ${s.np_suggestion.prev_label}; Dropdown überschreibt)`);
  }
  if (s.class_manual) parts.unshift("✋ Manuelles Override aktiv — gewinnt gegen Auto");
  return parts.join("\n");
}

// 🆕 Handlungs-Hinweise pro Ampelstufe (siehe /app/backend/modules/pose_quality.py).
// Zurück: [{text, priority}] — priority 1 = wichtigste Aktion. UI zeigt max. 3.
function qualityHints(quality, ctx = {}) {
  if (!quality) return [];
  const { grade, pose_consistency_px: px, fov_source, gate_green_px } = quality;
  const { nConstraints = 0, selfcal = null } = ctx;
  if (grade === "gruen") return [];
  const hints = [];
  // 🆕 Gate v2: Selfcal-Inlier ⇒ 3D läuft über die Shot-Kamera,
  // auch wenn die Einzel-H nicht metrisch ist.
  if (selfcal?.three_d_ready) {
    hints.push(`3D bereits via Shot-Selbstkalibrierung abgedeckt (Inlier, ${selfcal.residual_rms_px} px Klick-Residuum) — die Ampel bewertet nur die Einzel-H`);
  }
  // 🆕 2026-08: exakt bestimmter 3-Linien-Anker mit degenerierter Lösung
  // (Kamera in unrealistischer Höhe) — Selfcal-Kamera übernimmt.
  if (quality.camera_height_implausible) {
    hints.push(`Unplausible Kamerahöhe (${Math.round(quality.camera_height_m)} m) — 3-Linien-Anker ist geometrisch unterbestimmt (FOV-Override prüfen); die Selfcal-Kamera übernimmt die Feld-Projektion`);
  }
  if (px == null) {
    hints.push("Klicks liegen physikalisch inkonsistent — Feldmaße & Ecken-Zuordnung prüfen (wirklich derselbe Corner-Cone?)");
  }
  if (grade === "rot" && px != null && px > 30) {
    hints.push("Klick-Präzision: sub-pixelgenau auf eindeutige Marker (Ecken, Kegelspitzen, Linien-Kreuzungen) — Klick-Rauschen im Fernfeld verbiegt die H am stärksten");
    hints.push("Punkte über die GANZE Feldtiefe verteilen (nicht nur nahe/nur fern) — Perspektiv-Streuung ist Pflicht für metrische H");
  }
  if (grade === "gelb") {
    hints.push(`Fast metrisch (${px} px, Gate ${gate_green_px} px) — meist reicht 1 weiterer Punkt/Linie oder eine sub-pixelgenaue Nach­justierung`);
  }
  if (nConstraints > 0 && nConstraints < 5) {
    hints.push(`Nur ${nConstraints} Constraint(s) — 5+ Punkte/Linien geben Redundanz gegen Klick-Rauschen`);
  }
  if (fov_source === "fallback_default") {
    hints.push("FOV kommt aus dem Fallback — Kamera-Profil (Brennweite/Zoom) im Match setzen oder Selbstkalibrierung nutzen");
  }
  hints.push("Shot Selbstkalibrieren: löst alle Anker des Shots bündig — hebt gelb oft auf grün, wenn mind. 3 Anker im Shot vorhanden sind");
  return hints.slice(0, 3);
}

// 🆕 Auftrag 29, R1/R3 · `zielT`: Sprung ins Bild aus der URL
// (`?anker=1&t=…`); `klickliste`: `{name, nr}` für die Leiste oben.
// Ohne beide ist der Modus unverändert.
// 🆕 Auftrag LP1, R2 · `linienpruefung`: `{liste, nr}` für die Linienprüfung
// (Leiste oben, Ebene über dem Frame). Ohne sie ist der Modus unverändert.
export default function AnchorCollectMode({
  sequenceId, sequence, onAnchorsChanged, zielT = null, klickliste = null,
  linienpruefung = null,
}) {
  const fmt = sequence?.game_config?.format || "Outdoor";
  const duration = sequence?.duration_s || 0;
  const fps = sequence?.fps || 30;
  const areaPresets = useMemo(() => {
    const list = FIELD_PRESETS[fmt] || FIELD_PRESETS.Outdoor;
    return list.filter((p) => (p.kind || "area") === "area");
  }, [fmt]);

  // 🆕 04.09. (Nutzerwunsch): Freies Zeichnen ist der Standard-Modus —
  // es ist der meistgenutzte Weg (2–3 sichtbare Linien nachziehen) und
  // funktioniert mit und ohne Basis-Kalibrierung; vorher startete der
  // Modus im ersten Flächen-Preset und man musste jedes Mal wechseln.
  const [presetId, setPresetId] = useState("free_draw");
  const [combos, setCombos] = useState([]);
  const isCombo = presetId.startsWith("combo:");
  // 🆕 Phase 4 · Freies Zeichnen (Click-and-Drag + Hotkeys S/G/B)
  const isFreeDraw = presetId === "free_draw";
  // 🆕 Phase 4 · 3-Punkt-Initialisierung (P3P — funktioniert ohne Basis-Kal)
  const isP3P = presetId === "p3p";
  const [p3pLandmarks, setP3pLandmarks] = useState([]);
  const [p3pPoints, setP3pPoints] = useState([]);
  const [p3pActive, setP3pActive] = useState("");
  // 🆕 FOV-Kalibrier-Check: effektive Kamera (Kaskade) + Freitext-Override
  const [p3pCamera, setP3pCamera] = useState(null);
  const [fovInput, setFovInput] = useState("");
  // 🆕 Shot-FOV (2026-07-26): Scope-Wahl + Shot-/FOV-Status der Sequenz
  const [fovScope, setFovScope] = useState("shot");
  const [shotsInfo, setShotsInfo] = useState(null);
  // 🆕 Replay-Vorschau: Shot-ID, deren Live-Pass-Standbild gerade gehovert wird
  const [replayPreviewSid, setReplayPreviewSid] = useState(null);
  const loadShots = useCallback(async () => {
    try {
      setShotsInfo(await getShots(sequenceId));
    } catch (e) {
      // Shots optional — Sequenz evtl. nie gescannt; Fehler sichtbar loggen
      console.error("[shots] load failed", e?.response?.status, e?.message);
    }
  }, [sequenceId]);
  useEffect(() => { loadShots(); }, [loadShots]);
  // 🆕 P3P-Ambiguität: gewählte Lösung (Grid-Toggle bei >1 Kandidaten)
  const [p3pSolIdx, setP3pSolIdx] = useState(0);
  // 🆕 Grid-Linien-Ziehen: aktive Linien-Constraints [{cls,x1,y1,x2,y2}]
  const [p3pLines, setP3pLines] = useState([]);
  const [p3pLineDrag, setP3pLineDrag] = useState(null);
  // 🆕 Feldmaß-Selbstkalibrierung („Maß-Fit")
  const [fieldFit, setFieldFit] = useState(null);
  const [fieldFitBusy, setFieldFitBusy] = useState(false);
  const [fieldFitAll, setFieldFitAll] = useState(false);
  // 🆕 2026-08 · Adaptives Feldmodell (Grid-Search + Median-Konsens)
  // — nur für Adult_Outdoor sinnvoll (variable W/L_mid). Zustände:
  //   adaptResult  = Job-Result (dry-run) oder null
  //   adaptBusy    = Job läuft (Button disabled)
  //   adaptProg    = {pct, message} — Live-Progress aus pollJob
  //   adaptApplying= zweiter Job mit apply=true läuft
  const [adaptResult, setAdaptResult] = useState(null);
  const [adaptBusy, setAdaptBusy] = useState(false);
  const [adaptProg, setAdaptProg] = useState(null);
  const [adaptApplying, setAdaptApplying] = useState(false);
  const [resolvingSegOnly, setResolvingSegOnly] = useState(false);
  // 🆕 Shot-Selbstkalibrierung (Bündelausgleich, VISION.md Prinzip 6):
  // selfcal = persistierte gelöste Shots {shotId: entry}, Reasons vom
  // letzten Lauf getrennt (nur-informativ, nicht persistiert).
  const [selfcal, setSelfcal] = useState(null);
  const [selfcalBusy, setSelfcalBusy] = useState(false);
  const [selfcalReasons, setSelfcalReasons] = useState(null);
  // 🆕 Selfcal-Auto-Lauf: Das Backend löst nach jeder Anker-Mutation
  // den Shot debounced im Hintergrund neu — hier den Status pollen und
  // Ampel + 3D✓-Badges aktualisieren, sobald der Lauf fertig ist.
  const [selfcalAuto, setSelfcalAuto] = useState(null);
  // 🆕 2026-08-30 · Kamera-Konsens der Shots (siehe pose_quality.kamera_konsens)
  const [kameraKonsens, setKameraKonsens] = useState(null);
  const selfcalPollRef = useRef(null);
  const pollSelfcalAuto = (attempt = 0, seenRunning = false) => {
    clearTimeout(selfcalPollRef.current);
    selfcalPollRef.current = setTimeout(async () => {
      try {
        const d = await getShotSelfcal(sequenceId);
        const auto = d.auto || null;
        setSelfcalAuto(auto);
        if (auto?.status === "running") {
          if (attempt < 40) pollSelfcalAuto(attempt + 1, true);
          return;
        }
        if (seenRunning || attempt >= 4) {
          setSelfcal(d.shots || {});
          setKameraKonsens(d.kamera_konsens || null);
          refresh(); // Ampel + 3D✓-Badges nachziehen
          return;
        }
        pollSelfcalAuto(attempt + 1, false);
      } catch { /* Sequenz evtl. gelöscht — Polling endet */ }
    }, 2500);
  };
  useEffect(() => () => clearTimeout(selfcalPollRef.current), []);
  useEffect(() => {
    getShotSelfcal(sequenceId)
      .then((d) => {
        setSelfcal(d.shots || {});
        setKameraKonsens(d.kamera_konsens || null);
        setSelfcalAuto(d.auto || null);
        setFovHint(d.fov_hint || null);
        if (d.auto?.status === "running") pollSelfcalAuto(0, true);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sequenceId]);
  // 🆕 2026-08 · FOV-Abgleich (Override vs. Selfcal-Median) + Anker-
  // Feld-Abdeckung (5×3-Raster). Beides lebt am Selfcal-Panel.
  const [fovHint, setFovHint] = useState(null);
  const [fovAccepting, setFovAccepting] = useState(false);
  const [coverage, setCoverage] = useState(null);
  const loadCoverage = () => {
    getAnchorCoverage(sequenceId)
      .then((d) => setCoverage(d))
      .catch(() => setCoverage(null));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadCoverage(); }, [sequenceId]);
  const acceptSelfcalFov = async () => {
    if (!fovHint) return;
    setFovAccepting(true);
    try {
      await updateCameraConfig(sequenceId, {
        camera_fov_override_deg: fovHint.selfcal_median_deg,
      });
      toast.success(
        `FOV-Override auf ${fovHint.selfcal_median_deg}° gesetzt — Selbstkalibrierung läuft neu…`,
      );
      await runSelfcal();
      loadCoverage();
    } catch (e) {
      toast.error(fehlerText(e) || "FOV-Übernahme fehlgeschlagen");
    } finally {
      setFovAccepting(false);
    }
  };
  const runSelfcal = async () => {
    setSelfcalBusy(true);
    setSelfcalReasons(null);
    try {
      const d = await runShotSelfcal(sequenceId, { apply: true });
      setFovHint(d.fov_hint || null);
      const entries = Object.entries(d.shots || {});
      const solved = entries.filter(([, e]) => !e.reason);
      const failed = entries.filter(([, e]) => e.reason);
      if (solved.length) {
        setSelfcal((s) => ({ ...(s || {}), ...Object.fromEntries(solved) }));
        toast.success(
          `Selbstkalibrierung: ${solved.length} Shot(s) gelöst · ` +
          solved.map(([sid, e]) => {
            const rms = e.rms_inlier_px ?? e.rms_px;
            const nIn = e.n_inliers ?? e.n_anchors;
            return `S${sid}: RMS ${rms} px (${nIn}/${e.n_anchors} Inlier)`;
          }).join(" · "),
        );
      } else {
        toast.info(
          failed[0]?.[1]?.reason ||
          "Kein Shot lösbar — min. 3 joint-Anker pro Shot nötig.",
        );
      }
      if (failed.length) {
        setSelfcalReasons(failed.map(([sid, e]) => `Shot ${sid}: ${e.reason}`));
      }
    } catch (e) {
      toast.error(fehlerText(e) || "Selbstkalibrierung fehlgeschlagen");
    } finally {
      setSelfcalBusy(false);
    }
  };
  const [hasAnyH, setHasAnyH] = useState(true);
  const [segments, setSegments] = useState([]);
  const [activeCls, setActiveCls] = useState("sideline_bottom");
  const [drag, setDrag] = useState(null);
  const combo = isCombo
    ? combos.find((c) => `combo:${c.id}` === presetId) || null
    : null;
  const preset = isCombo || isFreeDraw
    ? null
    : areaPresets.find((p) => p.id === presetId) || null;
  const expectedClicks = combo ? combo.n_clicks : 4;
  const requiresSide = !isCombo
    && (!!preset?.requiresSide || preset?.pt0PtAxis === "width");
  const [side, setSide] = useState("");
  const [time, setTime] = useState(0);
  const [loadTime, setLoadTime] = useState(0);
  const [points, setPoints] = useState([]);
  const [reprojCheck, setReprojCheck] = useState(null);
  const [saving, setSaving] = useState(false);
  const [anchors, setAnchors] = useState([]);
  const [nDubletten, setNDubletten] = useState(0);
  const [nEligible, setNEligible] = useState(0);
  const [exporting, setExporting] = useState(false);
  // 🆕 Anker-Galerie: Listen- oder Raster-Ansicht der gesetzten Anker
  const [anchorView, setAnchorView] = useState("liste");
  // 🆕 Galerie-Sortierung: schlechteste Anker zuerst (Fehler-px / Ampel)
  const [gallerySort, setGallerySort] = useState("zeit");
  const GRADE_RANK = { rot: 0, gelb: 1, gruen: 2 };
  const galleryList = [...anchors.filter((a) => a.id && a.has_homography)];
  if (gallerySort === "fehler") {
    galleryList.sort((a, b) => (b.reproj_error_px ?? -1) - (a.reproj_error_px ?? -1));
  } else if (gallerySort === "ampel") {
    galleryList.sort((a, b) => {
      const ra = GRADE_RANK[a.quality?.grade] ?? 3;
      const rb = GRADE_RANK[b.quality?.grade] ?? 3;
      if (ra !== rb) return ra - rb;
      return (b.reproj_error_px ?? -1) - (a.reproj_error_px ?? -1);
    });
  } else {
    galleryList.sort((a, b) => (Number(a.at_time_s) || 0) - (Number(b.at_time_s) || 0));
  }
  const [exportMsg, setExportMsg] = useState("");
  const [imgDims, setImgDims] = useState({ w: 1, h: 1 });
  const imgRef = useRef(null);
  const debounceRef = useRef(null);
  // 🆕 Geister-Nachfuehrung: der Effekt braucht den Zeitpunkt DAVOR und
  // die Segmente, ohne beides in seine Abhängigkeiten zu nehmen — sonst
  // liefe er bei jedem gezeichneten Strich erneut.
  const prevLoadRef = useRef(0);
  const segmentsRef = useRef([]);
  segmentsRef.current = segments;
  // { laeuft } | { verschoben, grund, verworfen } | null
  const [ghostInfo, setGhostInfo] = useState(null);
  // 🆕 Nachtrag 6 (Befund F4): die 3D-Ampel des gerade gespeicherten Ankers
  // in der Klickliste — ein eigener Zustand, NICHT `reprojCheck` (den leert
  // der Vorschau-Effekt bei jeder Segmentänderung, auch beim Umwandeln in
  // Geister direkt nach dem Speichern). Trägt `nr`: sie steht nie auf einem
  // anderen Punkt.
  const [klickAmpel, setKlickAmpel] = useState(null);

  // 🆕 2026-08-23 · Zoom/Verschieben/Lupe im Rahmen. Der Zustand liegt im
  // Hook, hier bleibt nur die Cursor-Verfolgung für die Lupe — und die
  // läuft NUR, wenn die Lupe an ist (sonst setState bei jeder Mausbewegung).
  const viewportRef = useRef(null);
  const zoomView = useFrameZoom(viewportRef, { enabled: true });
  // Stabile Referenz fuer den Tasten-Effekt weiter unten: zoomView ist bei
  // jedem Rendern ein neues Objekt, als Abhaengigkeit wuerde es den
  // Listener jedes Mal neu binden. reset selbst ist ein useCallback mit
  // leerer Abhaengigkeitsliste, also stabil.
  const resetZoom = zoomView.reset;
  const [loupeOn, setLoupeOn] = useState(false);
  const [cursorPos, setCursorPos] = useState(null);
  const trackCursor = (e) => {
    if (!loupeOn) return;
    const img = imgRef.current;
    const vp = viewportRef.current;
    if (!img || !vp) return;
    const ir = img.getBoundingClientRect();
    const vr = vp.getBoundingClientRect();
    setCursorPos({
      natural: {
        x: ((e.clientX - ir.left) / ir.width) * imgDims.w,
        y: ((e.clientY - ir.top) / ir.height) * imgDims.h,
      },
      view: { x: e.clientX - vr.left, y: e.clientY - vr.top },
      viewport: { w: vr.width, h: vr.height },
    });
  };


  // 🆕 Shot am aktuellen Zeitpunkt (für Shot-FOV-Panel + Vorschläge)
  const shotList = shotsInfo?.shots || [];
  const currentShot = shotList.find(
    (s) => time >= s.start_s - 1e-6 && time < s.end_s,
  ) || (shotList.length && time >= shotList[shotList.length - 1].end_s - 1e-6
    ? shotList[shotList.length - 1] : null);
  // 🆕 2026-08-23 · Teilen am Abspielkopf. MIN_SPLIT_PART_S spiegelt
  // MIN_PART_S aus modules/shot_split.py — der Knopf soll aus sein, statt
  // erst nach dem Klick eine 422 zu zeigen.
  const canSplitHere = !!currentShot
    && time - currentShot.start_s >= MIN_SPLIT_PART_S
    && currentShot.end_s - time >= MIN_SPLIT_PART_S;

  const onSplitHere = async (shot) => {
    setSplitBusy(true);
    try {
      const d = await splitShot(sequenceId, shot.shot_id, Number(time.toFixed(3)));
      await loadShots();
      toast.success(
        `Shot ${shot.shot_id} bei ${Number(d.at_time_s).toFixed(2)} s geteilt — jetzt ${d.n_shots} Shots.`
        + (d.n_review ? ` Die alte Klasse wartet in der Prüfliste (⚠ zu prüfen).` : "")
        + (d.autocal_stale ? " Auto-Kalibrierungs-Bericht ist damit veraltet." : ""),
      );
    } catch (e) {
      toast.error(fehlerText(e) || "Teilen fehlgeschlagen");
    } finally {
      setSplitBusy(false);
    }
  };

  const onUnsplit = async (shot) => {
    setSplitBusy(true);
    try {
      const d = await unsplitShot(sequenceId, shot.shot_id);
      await loadShots();
      toast.success(`Teilung bei ${Number(d.removed_at_time_s).toFixed(2)} s zurückgenommen — jetzt ${d.n_shots} Shots.`);
    } catch (e) {
      toast.error(fehlerText(e) || "Zurücknehmen fehlgeschlagen");
    } finally {
      setSplitBusy(false);
    }
  };

  // 🆕 2026-08-31 · Gegenstück zum Teilen. Der Detektor uebersieht nicht
  // nur Schnitte, er erfindet auch welche: im Video Puntke_5-8 stand ein
  // Shot mit 1,13 s mitten in einer durchgehenden Einstellung. Ein
  // solcher Splitter bekommt eine eigene Selfcal, obwohl 1,13 s zu wenig
  // Parallaxe fuer eine eigene Kamera liefern.
  const onMergePrev = async (shot) => {
    setSplitBusy(true);
    try {
      const d = await mergeShotWithPrev(sequenceId, shot.shot_id);
      await loadShots();
      toast.success(
        `Grenze bei ${Number(d.merged_at_time_s).toFixed(2)} s gestrichen — jetzt ${d.n_shots} Shots.`
        + (d.n_review ? " Eine alte Klasse wartet in der Prüfliste (⚠ zu prüfen)." : "")
        + (d.autocal_stale ? " Auto-Kalibrierungs-Bericht ist damit veraltet." : ""),
      );
    } catch (e) {
      toast.error(fehlerText(e) || "Zusammenlegen fehlgeschlagen");
    } finally {
      setSplitBusy(false);
    }
  };

  const onUnmerge = async (shot) => {
    setSplitBusy(true);
    try {
      const d = await unmergeShot(sequenceId, shot.shot_id);
      await loadShots();
      toast.success(`Grenze bei ${Number(d.restored_at_time_s).toFixed(2)} s ist zurück — jetzt ${d.n_shots} Shots.`);
    } catch (e) {
      toast.error(fehlerText(e) || "Zurücknehmen fehlgeschlagen");
    } finally {
      setSplitBusy(false);
    }
  };

  const onScanCuts = async (shot) => {
    setSplitBusy(true);
    try {
      const { job_id } = await scanCutsInShot(sequenceId, shot.shot_id);
      toast.info(`Suche verborgene Schnitte in Shot ${shot.shot_id} (${Number(shot.start_s).toFixed(2)}–${Number(shot.end_s).toFixed(2)} s) …`);
      const res = await pollJob(job_id);
      await loadShots();
      const n = res?.result?.n_new ?? 0;
      if (n > 0) {
        toast.success(`${n} Kandidat${n === 1 ? "" : "en"} gefunden (Schwelle ${res.result.threshold}) — als ⚑ am Shot-Balken. Vorschläge, keine Befunde.`);
      } else {
        toast.info(`Kein weiterer Kandidat bei Schwelle ${res?.result?.threshold ?? "0.05"}. Das heißt „gesucht und nichts gefunden“, nicht „garantiert schnittfrei“.`);
      }
    } catch (e) {
      toast.error(fehlerText(e) || e?.message || "Suche fehlgeschlagen");
    } finally {
      setSplitBusy(false);
    }
  };

  const acceptShotFovSuggestion = async (shot) => {
    try {
      const d = await updateCameraConfig(sequenceId, {
        shot_fov_deg: shot.suggestion_deg,
        shot_at_time_s: (shot.start_s + shot.end_s) / 2,
      });
      setP3pCamera(d.camera);
      loadShots();
      toast.success(`Shot ${shot.shot_id}: FOV ${shot.suggestion_deg}° übernommen (Median-Vorschlag)`);
      setP3pPoints((p) => [...p]);
    } catch (e) {
      toast.error(fehlerText(e) || "Vorschlag konnte nicht übernommen werden");
    }
  };
  const onDetectShots = async () => {
    try {
      const d = await detectShots(sequenceId);
      toast.info("Shot-Erkennung läuft (PySceneDetect)…");
      await pollJob(d.job_id);
      await loadShots();
      globalThis.dispatchEvent(new Event("anchors-changed"));
      toast.success("Shots erkannt, klassifiziert & getrackt");
    } catch (e) {
      toast.error(fehlerText(e) || "Shot-Erkennung fehlgeschlagen");
    }
  };

  // 🆕 2026-08 · Shot-Classifier Prototyp (VISION Stufe 2)
  const [classifyBusy, setClassifyBusy] = useState(false);
  const onClassifyShots = async () => {
    setClassifyBusy(true);
    try {
      const d = await classifyShots(sequenceId);
      toast.info("Shot-Klassifikation läuft (Keyframes + YOLOv8n)…");
      await pollJob(d.job_id);
      await loadShots();
      toast.success("Shots klassifiziert");
    } catch (e) {
      toast.error(fehlerText(e) || "Klassifikation fehlgeschlagen");
    } finally {
      setClassifyBusy(false);
    }
  };
  const onOverrideShotClass = async (sid, label) => {
    try {
      await setShotClass(sequenceId, sid, label || null);
      await loadShots();
      toast.success(label
        ? `Shot ${sid}: manuell als ${SHOT_CLASS_META[label]?.label || label} markiert`
        : `Shot ${sid}: zurück auf Auto-Klassifikation`);
    } catch (e) {
      toast.error(fehlerText(e) || "Override fehlgeschlagen");
    }
  };

  // 🆕 NP-Sammel-Aktion: alle Auto-NP-Funde gebündelt prüfen —
  // ✓/✕ setzt ein manuelles GOLD-Label (überlebt Re-Läufe, füttert
  // den CNN-Trainingspool). „Alle bestätigen" arbeitet die offenen
  // Funde in einem Rutsch ab.
  // 🆕 04.09. · Funde ausblenden, die der Mensch schon ANDERS entschieden
  // hat (z. B. Drohne: aus der Luft wirken Spieler winzig → die
  // Bewegungs-Heuristik hält die Draufsicht fälschlich für Timeout/
  // Aufstellung — WM-Video Shot 66). np-Bestätigte bleiben als ✓ sichtbar.
  const npFinds = shotList.filter((s) => s.np_suggestion
    && (!s.class_manual || s.class_manual === "np"));
  const npPending = npFinds.filter((s) => !s.class_manual);
  const [npBusy, setNpBusy] = useState(false);
  const confirmNp = async (sid, label) => {
    setNpBusy(true);
    try {
      await setShotClass(sequenceId, sid, label);
      await loadShots();
      toast.success(label === "np"
        ? `Shot ${sid}: NP bestätigt ✓ (Gold-Label)`
        : `Shot ${sid}: kein NP — als ${label} markiert (Gold-Label)`);
    } catch (e) {
      toast.error(fehlerText(e) || "Bestätigung fehlgeschlagen");
    } finally {
      setNpBusy(false);
    }
  };
  const confirmAllNp = async () => {
    setNpBusy(true);
    try {
      for (const s of npPending) {
        await setShotClass(sequenceId, s.shot_id, "np");
      }
      await loadShots();
      toast.success(`${npPending.length} Auto-NP-Fund${npPending.length === 1 ? "" : "e"} bestätigt ✓ (Gold-Labels)`);
    } catch (e) {
      toast.error(fehlerText(e) || "Sammel-Bestätigung fehlgeschlagen");
    } finally {
      setNpBusy(false);
    }
  };

  // 🆕 2026-08 · Visueller Frame-Tracker (Phase 5): Hintergrund-Job,
  // Request-Pfad liest nur aus dem Cache — nichts blockiert.
  const [trackBusy, setTrackBusy] = useState(false);
  // 🆕 2026-08-23 · sperrt Teilen/Zurücknehmen/Suchen gegeneinander —
  // alle drei schreiben dieselbe Shot-Liste.
  const [splitBusy, setSplitBusy] = useState(false);
  const onTrackCamera = async () => {
    setTrackBusy(true);
    try {
      const d = await trackCamera(sequenceId);
      toast.info("Kamera-Tracking läuft (Optical Flow, Wide-Shots)…");
      const res = await pollJob(d.job_id);
      await loadShots();
      globalThis.dispatchEvent(new Event("anchors-changed"));
      const n = res?.result?.n_tracked ?? 0;
      const npv = res?.result?.n_pass_reprojected ?? 0;
      toast.success(`Kamera getrackt: ${n} Shot${n === 1 ? "" : "s"} mit Spur · ${npv} Pass-Vektor${npv === 1 ? "" : "en"} neu berechnet`);
    } catch (e) {
      toast.error(fehlerText(e) || "Kamera-Tracking fehlgeschlagen");
    } finally {
      setTrackBusy(false);
    }
  };

  // 🆕 Auto-Nachtracken: Anker in rotem/gelbem Track-Segment gesetzt →
  // Tracking läuft automatisch neu (Multi-Referenz nutzt den frischen
  // Anker). Guard: nie doppelt, wenn schon ein Track-Job läuft.
  const findWeakSegmentAt = (t) => {
    for (const s of (shotsInfo?.shots || [])) {
      for (const g of (s.track?.segments || [])) {
        if (Array.isArray(g)) continue;
        if ((g.grade === "rot" || g.grade === "gelb")
            && t >= Number(g.t0) && t <= Number(g.t1)) {
          return { ...g, shot_id: s.shot_id };
        }
      }
    }
    return null;
  };
  const maybeAutoRetrack = async (anchorT) => {
    const seg = findWeakSegmentAt(anchorT);
    if (!seg) return;
    if (trackBusy) {
      toast.info("Tracking läuft bereits — der neue Anker fließt in den nächsten Lauf ein.");
      return;
    }
    toast.info(`Anker im ${seg.grade}en Track-Segment (${Number(seg.t0).toFixed(1)}–${Number(seg.t1).toFixed(1)} s) — Kamera-Tracking läuft automatisch neu…`);
    setTrackBusy(true);
    try {
      const d = await trackCamera(sequenceId);
      await pollJob(d.job_id);
      const fresh = await getShots(sequenceId);
      setShotsInfo(fresh);
      globalThis.dispatchEvent(new Event("anchors-changed"));
      let after = null;
      for (const s of (fresh?.shots || [])) {
        for (const g of (s.track?.segments || [])) {
          if (!Array.isArray(g) && anchorT >= Number(g.t0) && anchorT <= Number(g.t1)) after = g;
        }
      }
      if (after) {
        const msg = `Auto-Nachtracken: Segment ${Number(after.t0).toFixed(1)}–${Number(after.t1).toFixed(1)} s jetzt ${after.grade}${after.med_inliers != null ? ` (${after.med_inliers} Inlier)` : ""}`;
        if (after.grade === "grün") toast.success(msg);
        else toast.warning(`${msg} — evtl. weiteren Anker setzen.`);
      } else {
        toast.success("Auto-Nachtracken abgeschlossen.");
      }
    } catch (e) {
      toast.error(fehlerText(e) || "Auto-Nachtracken fehlgeschlagen");
    } finally {
      setTrackBusy(false);
    }
  };

  // 🆕 „✓ geprüft“: Replay-Link nach manueller Prüfung bestätigen —
  // überlebt Re-Läufe und fließt als Gold-Label in den CNN-Trainingspool.
  const onConfirmReplay = async (sid, confirmed) => {
    try {
      await confirmReplayLink(sequenceId, sid, confirmed);
      await loadShots();
      toast.success(confirmed
        ? "Replay-Link bestätigt ✓ — fließt als Gold-Label in den Trainingspool"
        : "Bestätigung zurückgenommen — Link wird wieder automatisch berechnet");
    } catch (e) {
      toast.error(fehlerText(e) || "Bestätigung fehlgeschlagen");
    }
  };

  // 🆕 Sprung-Helfer: Seek + sanft zum Video scrollen + Hinweis-Toast —
  // Sprung-Links weiter unten landen sonst außerhalb des Sichtbereichs.
  const jumpToVideo = (t, msg) => {
    setT(t, true);
    document.querySelector('[data-testid="anchor-collect-frame"]')
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
    if (msg) toast.info(msg);
  };

  // 🆕 Anker-Navigation ◀⚓▶: direkt zum vorherigen/nächsten gesetzten
  // Anker springen, ohne in die Anker-Liste scrollen zu müssen.
  const seekAnchor = (dir) => {
    const ts = [...new Set(anchors
      .map((a) => Number(a.at_time_s))
      .filter((t) => Number.isFinite(t)))].sort((a, b) => a - b);
    if (!ts.length) {
      toast.info("Noch keine Anker gesetzt.");
      return;
    }
    const target = dir > 0
      ? ts.find((t) => t > time + 0.05)
      : [...ts].reverse().find((t) => t < time - 0.05);
    if (target == null) {
      toast.info(dir > 0 ? "Kein späterer Anker." : "Kein früherer Anker.");
      return;
    }
    const idx = ts.indexOf(target) + 1;
    setT(target, true);
    const a = anchors.find((x) => Math.abs(Number(x.at_time_s) - target) < 0.05);
    toast.info(`Anker ${idx}/${ts.length} @ ${target.toFixed(1)} s${a?.preset_id ? ` · ${a.preset_id}` : ""}`);
  };

  // 🆕 Shot-Navigation ◀S/S▶: durch ALLE Shots springen (auch ohne
  // Anker) — zum Prüfen und Bestätigen jeder einzelnen Einstellung.
  const seekShot = (dir) => {
    if (!shotList.length) {
      toast.info("Keine Shots erkannt.");
      return;
    }
    const idx = shotList.findIndex((s) =>
      time >= Number(s.start_s) && time < Number(s.end_s));
    const ni = idx < 0
      ? (dir > 0 ? 0 : shotList.length - 1)
      : idx + dir;
    if (ni < 0 || ni >= shotList.length) {
      toast.info(dir > 0 ? "Letzter Shot." : "Erster Shot.");
      return;
    }
    const s = shotList[ni];
    setT((Number(s.start_s) + Number(s.end_s)) / 2, true);
    const auto = s.class_auto?.label
      ? `${s.class_auto.label} ${Math.round((s.class_auto.confidence || 0) * 100)} %`
      : "unklassifiziert";
    toast.info(`Shot ${s.shot_id}/${shotList.length} · ${s.class_manual ? `${s.class_manual} ✋` : auto}`);
  };

  // 🆕 Ein-Klick-Auto-Kalibrierung: klassifizieren → DL-Linien-Anker
  // pro Wide-Shot (5 Kandidaten-Frames) → Tracking → Pass-Reprojektion.
  const [autoCalBusy, setAutoCalBusy] = useState(false);
  const [autoCalProg, setAutoCalProg] = useState(null);
  const onAutoCalibrate = async () => {
    setAutoCalBusy(true);
    setAutoCalProg("startet…");
    try {
      const d = await autoCalibrateAll(sequenceId);
      const job = await pollJob(d.job_id, {
        onProgress: ({ message }) => setAutoCalProg(message || "läuft…"),
      });
      const r = job.result || {};
      await loadShots();
      await refresh();
      globalThis.dispatchEvent(new Event("anchors-changed"));
      toast.success(
        `Auto-Kalibrierung: ${r.n_calibrated ?? 0}/${r.n_wide ?? 0} Wide-Shots kalibriert · ${r.n_dl_anchors ?? 0} DL-Anker gesetzt${r.n_dl_rejected > 0 ? ` · ${r.n_dl_rejected} inkonsistente verworfen` : ""} · ${r.n_skipped ?? 0} übersprungen (Close/Replay) · ${r.n_open ?? 0} brauchen dich`,
      );
    } catch (e) {
      toast.error(fehlerText(e) || "Auto-Kalibrierung fehlgeschlagen");
    } finally {
      setAutoCalBusy(false);
      setAutoCalProg(null);
    }
  };

  const refresh = async () => {
    try {
      const d = await listAnchors(sequenceId);
      setAnchors(d.anchors || []);
      setNDubletten(d.n_dubletten || 0);
      setNEligible(d.n_training_eligible || 0);
      loadShots(); // 🆕 FOV-Kurve kann sich mit jedem Anker ändern
      loadCoverage(); // 🆕 Feld-Abdeckung folgt jedem Anker-Save/-Delete
      // 🆕 2026-08 · Wizard-State synchron halten: ohne diesen Callback
      // prüft das Sequence Lab („Edit Passes") einen VERALTETEN
      // calibration-Stand und blockt Pässe trotz gesetzter Anker.
      onAnchorsChanged?.();
      // 🆕 Broadcast-Workflow: kein Backend-Gate mehr — ohne H-Anker
      // »Freies Zeichnen« vorwählen (Broadcasts zeigen selten 4 Ecken).
      const anyH = (d.anchors || []).some((a) => a.has_homography);
      setHasAnyH(anyH);
      if (!anyH) setPresetId("free_draw");
      // 🆕 Coverage-Leiste (SequenceLab) über Anker-Änderungen informieren
      globalThis.dispatchEvent(new CustomEvent("anchors-changed"));
    } catch { /* Sequenz evtl. noch ohne Anker */ }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [sequenceId]);
  useEffect(() => {
    listLineCombos().then((d) => setCombos(d.combos || [])).catch(() => {});
  }, []);

  // 🆕 Fremd-Mittellinie: 7. Zeichen-Klasse nur wenn im Wizard aktiviert
  const auxMidline = !!sequence?.game_config?.aux_midline_visible;
  const drawClasses = auxMidline
    ? [...FREE_LINE_CLASSES, "line_midfield"]
    : FREE_LINE_CLASSES;

  // 🆕 Freies Zeichnen · Hotkeys: S/G/B wählen die Klasse, erneutes
  // Drücken toggelt die Seite (M = Mittellinie, falls freigeschaltet).
  // Backspace löscht das letzte Segment, Esc bricht den Drag ab.
  useEffect(() => {
    if (!isFreeDraw) return;
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (["input", "textarea", "select"].includes(tag)) return;
      const k = e.key.toLowerCase();
      if (k === "m" && !auxMidline) return;
      if (HOTKEY_CYCLE[k]) {
        e.preventDefault();
        const [a, b] = HOTKEY_CYCLE[k];
        setActiveCls((cur) => (cur === a ? b : a));
      } else if (e.key === "Backspace") {
        e.preventDefault();
        setSegments((s) => s.slice(0, -1));
      } else if (e.key === "Escape") {
        setDrag(null);
        setEditDrag(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isFreeDraw, auxMidline]);

  // 🆕 2026-08-23 · Lupe und Zoom-Zurücksetzen. Eigener Effekt, weil das
  // in ALLEN Zeichenmodi gilt (freies Zeichnen, P3P, Combos) — der
  // Hotkey-Effekt darüber gilt nur fürs freie Zeichnen. Leertaste und
  // Mausrad liegen im Hook (FrameZoom.jsx).
  useEffect(() => {
    const onKey = (e) => {
      if (["input", "textarea", "select"].includes(
        (e.target?.tagName || "").toLowerCase())) return;
      if (e.key === "l" || e.key === "L") {
        e.preventDefault();
        setLoupeOn((v) => !v);
      } else if (e.key === "0") {
        e.preventDefault();
        resetZoom();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [resetZoom]);

  // 🆕 Geister-Overlay („Frame-Sprung + Zeichnen behalten"): beim
  // Frame-Wechsel bleiben die Segmente sichtbar, werden aber als
  // Geister markiert — Nachrücken (Anfassen) macht sie wieder frisch.
  //
  // 🆕 2026-08-25 · Und sie werden MITGEZOGEN, wo das rechenbar ist:
  // Bildpunkt → Welt über die Pose des alten Frames, Welt → Bild über die
  // des neuen (`POST /segments/reproject`). Vorher blieben sie stur auf
  // ihren alten Bildkoordinaten liegen und lagen nach „+1s" regelmäßig
  // neben der Linie, die sie meinen — als Schablone damit wertlos.
  // Ohne belastbare Pose bleiben sie stehen; geraten wird nicht.
  useEffect(() => {
    const tVon = prevLoadRef.current;
    prevLoadRef.current = loadTime;
    if (!isFreeDraw) return;
    setSegments((s) => (s.some((x) => !x.ghost)
      ? s.map((x) => ({ ...x, ghost: true })) : s));
    setDrag(null);
    setEditDrag(null);

    const vorher = segmentsRef.current;
    if (!vorher.length || tVon === loadTime || imgDims.w <= 2) {
      setGhostInfo(null);
      return;
    }
    let alive = true;
    setGhostInfo({ laeuft: true });
    reprojectSegments(sequenceId, {
      t_von: tVon, t_nach: loadTime,
      // `i` läuft mit: der Server darf Segmente verwerfen, die aus dem
      // Bild laufen — dann stimmt die Reihenfolge nicht mehr und eine
      // Zuordnung über die Position würde Linien vertauschen.
      segments: vorher.map((x, i) => ({
        i, cls: x.cls, x1: x.x1, y1: x.y1, x2: x.x2, y2: x.y2,
      })),
      img_ref: { w: imgDims.w, h: imgDims.h },
    })
      .then((d) => {
        if (!alive) return;
        setGhostInfo({ verschoben: !!d.verschoben, grund: d.grund || "",
                       verworfen: d.verworfen || 0 });
        if (!d.verschoben) return;
        setSegments((s) => {
          // Zwischenzeitlich gezeichnet oder angefasst? Dann gehört das
          // Bild dem Nutzer, nicht mehr der Antwort von vorhin.
          if (s.length !== vorher.length || s.some((x) => !x.ghost)) return s;
          const nachIndex = new Map(d.segments.map((n) => [n.i, n]));
          return s
            .map((x, i) => {
              const n = nachIndex.get(i);
              // Nicht zurückgekommen = läuft aus dem Bild, fällt weg.
              return n ? { ...x, x1: n.x1, y1: n.y1, x2: n.x2, y2: n.y2,
                           gezogen: true } : null;
            })
            .filter(Boolean);
        });
      })
      .catch(() => { if (alive) setGhostInfo(null); });
    return () => { alive = false; };
    /* eslint-disable-next-line */
  }, [loadTime]);

  // 🆕 P3P · Landmarken-Registry + effektive Kamera laden
  useEffect(() => {
    if (!isP3P || p3pLandmarks.length) return;
    getP3PLandmarks(sequenceId)
      .then((d) => {
        setP3pLandmarks(d.landmarks || []);
        setP3pActive((d.landmarks || [])[0]?.id || "");
        setP3pCamera(d.camera || null);
      })
      .catch(() => {});
    /* eslint-disable-next-line */
  }, [isP3P]);

  // 🆕 FOV-Kalibrier-Check: Freitext-Override setzen (leer = löschen) →
  // Kaskade neu auflösen + laufenden P3P-Solve mit neuem K wiederholen.
  const applyFov = async () => {
    const v = parseFloat(fovInput);
    try {
      // 🆕 Shot-Scope: Override wirkt nur auf den Shot am aktuellen Zeitpunkt
      const payload = fovScope === "shot"
        ? (Number.isNaN(v)
          ? { clear_shot_fov: true, shot_at_time_s: time }
          : { shot_fov_deg: v, shot_at_time_s: time })
        : (Number.isNaN(v)
          ? { clear_fov_override: true }
          : { camera_fov_override_deg: v });
      const d = await updateCameraConfig(sequenceId, payload);
      setP3pCamera(d.camera);
      loadShots();
      toast.success(
        fovScope === "shot" && d.shot_id != null
          ? `Shot ${d.shot_id}: FOV ${d.camera.fov_deg}° (${fovSourceLabel(d.camera.source)})`
          : `FOV aktiv: ${d.camera.fov_deg}° (${fovSourceLabel(d.camera.source)})`,
      );
      setP3pPoints((p) => [...p]); // re-solve mit neuem K
    } catch (e) {
      toast.error(fehlerText(e) || "FOV konnte nicht gesetzt werden");
    }
  };

  // 🆕 Reset: verpatzte Selbstkalibrierung + manuellen Override löschen
  // → Kaskade fällt zurück auf EXIF/Profil-Standard.
  const resetFov = async () => {
    try {
      const d = await updateCameraConfig(sequenceId, {
        clear_fov_override: true, clear_fov_estimated: true,
      });
      setP3pCamera(d.camera);
      setFovInput("");
      toast.success(`FOV zurückgesetzt → ${d.camera.fov_deg}° (${fovSourceLabel(d.camera.source)})`);
      setP3pPoints((p) => [...p]); // re-solve mit Standard-K
    } catch (e) {
      toast.error(fehlerText(e) || "Reset fehlgeschlagen");
    }
  };

  // 🆕 P3P · Frame-Sprung: Landmarken-Klicks sind frame-spezifisch —
  // KEINE Geister-Semantik (exakte Punkte), Klicks werden verworfen.
  useEffect(() => {
    if (!isP3P) return;
    setP3pPoints([]);
    setP3pLines([]);
    setP3pLineDrag(null);
    setReprojCheck(null);
    /* eslint-disable-next-line */
  }, [loadTime]);

  // 🆕 P3P · Backspace löscht den letzten Landmarken-Klick
  useEffect(() => {
    if (!isP3P) return;
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (["input", "textarea", "select"].includes(tag)) return;
      if (e.key === "Backspace") {
        e.preventDefault();
        setP3pPoints((p) => p.slice(0, -1));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isP3P]);

  // 🆕 P3P · dry_run sobald ≥3 Landmarken geklickt sind → Grid + Badge.
  // 3 Punkte = P3P (FOV-abhängig) · 4+ = DLT (FOV-frei) · mit gezogenen
  // Grid-Linien: gemischter Punkt+Linien-DLT ab (Punkte+Linien) ≥ 4.
  // Debounced: beim Nachziehen (Drag) wandert das Grid live mit.
  const p3pSolvable = p3pLines.length >= 1
    ? p3pPoints.length + p3pLines.length >= 4
    : p3pPoints.length >= 3;
  useEffect(() => {
    if (!isP3P) return;
    if (!p3pSolvable) {
      setReprojCheck(null);
      return;
    }
    let alive = true;
    const timer = setTimeout(() => {
      createAnchor(sequenceId, {
        p3p_points: p3pPoints,
        p3p_lines: p3pLines.map((s) => ({
          class_id: s.cls, x1: s.x1, y1: s.y1, x2: s.x2, y2: s.y2,
        })),
        at_time_s: time, dry_run: true,
        img_ref: imgDims.w > 2 ? { w: imgDims.w, h: imgDims.h } : undefined,
      })
        .then((d) => {
          if (!alive) return;
          setReprojCheck(d.solver_error ? { ...d, error: d.solver_error } : d);
          setP3pSolIdx(d.p3p?.chosen_index ?? 0);
        })
        .catch((e) => {
          if (alive) setReprojCheck({
            error: fehlerText(e) || "P3P-Solve fehlgeschlagen",
          });
        });
    }, 300);
    return () => { alive = false; clearTimeout(timer); };
    /* eslint-disable-next-line */
  }, [p3pPoints, p3pLines, isP3P]);

  const segPayload = (s) => ({
    class_id: s.cls, x1: s.x1, y1: s.y1, x2: s.x2, y2: s.y2,
  });
  // 🆕 Touch-to-Activate: Geister (ghost:true) sind PASSIV — reines
  // visuelles Overlay. NUR aktive Segmente gehen in Solver, dry_run
  // und Save. Anfassen (mousedown auf Griff/Linie) aktiviert.
  const activeSegments = segments.filter((s) => !s.ghost);
  const ghostCount = segments.length - activeSegments.length;
  const hasVertical = activeSegments.some((s) => VERTICAL_CLASSES.includes(s.cls));
  const hasSideline = activeSegments.some((s) => !VERTICAL_CLASSES.includes(s.cls));

  // 🆕 Freies Zeichnen · Live-Reproj: dry_run sobald die Geometrie
  // lösbar ist (≥1 Quer-Linie + ≥1 Seitenlinie — NUR aktive Segmente).
  useEffect(() => {
    if (!isFreeDraw) return;
    // 🆕 Nachtrag 4 (T2): in der Klickliste keine Vorschau-Lösung — sie
    // rechnete eine volle Lösung vor dem Speichern (gemessen 12 s).
    if (!vorschauLoesen(klickliste)
        || activeSegments.length < 2 || !hasVertical || !hasSideline) {
      setReprojCheck(null);
      return;
    }
    let alive = true;
    // Debounce: beim Nachrücken (Move-Drag) feuern viele Segment-Updates
    const timer = setTimeout(() => {
      createAnchor(sequenceId, {
        free_lines: activeSegments.map(segPayload), at_time_s: time, dry_run: true,
        img_ref: imgDims.w > 2 ? { w: imgDims.w, h: imgDims.h } : undefined,
      })
        .then((d) => {
          if (alive) setReprojCheck(d.solver_error ? { ...d, error: d.solver_error } : d);
        })
        .catch((e) => {
          if (alive) setReprojCheck({
            error: fehlerText(e) || "Solve fehlgeschlagen",
          });
        });
    }, 450);
    return () => { alive = false; clearTimeout(timer); };
    /* eslint-disable-next-line */
  }, [segments, isFreeDraw]);

  // 🆕 v1.2 · Echtzeit-Reprojektions-Fehler: nach dem letzten Klick
  // einer Linien-Kombination dry_run-Solve gegen das Backend.
  useEffect(() => {
    if (!combo || points.length !== expectedClicks) {
      setReprojCheck(null);
      return;
    }
    let alive = true;
    createAnchor(sequenceId, {
      combo_id: combo.id, at_time_s: time,
      image_points: points, dry_run: true,
      img_ref: imgDims.w > 2 ? { w: imgDims.w, h: imgDims.h } : undefined,
    })
      .then((d) => { if (alive) setReprojCheck(d); })
      .catch((e) => {
        if (alive) setReprojCheck({
          error: fehlerText(e) || "Solve fehlgeschlagen",
        });
      });
    return () => { alive = false; };
    /* eslint-disable-next-line */
  }, [points, combo, expectedClicks]);

  const setT = (t, immediate = false) => {
    // Frame-Raster: hart auf echte Frame-Indizes einrasten — der
    // gespeicherte Anker-Zeitstempel = exakt der angezeigte Frame.
    const clamped = Math.max(0, Math.min(duration, +t));
    const v = Math.max(0, Math.min(duration, Math.round(clamped * fps) / fps));
    setTime(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (immediate) setLoadTime(v);
    else debounceRef.current = setTimeout(() => setLoadTime(v), 120);
  };

  // 🆕 Auftrag 29, R1 · Sprung ins Bild: eingerastet über setT(t, true),
  // erst wenn die Sequenz DIESER Kennung geladen ist — vorher ist
  // `duration` 0 (oder die des vorigen Videos) und setT klemmte auf 0.
  const sprungBereit = duration > 0 && sequence?.id === sequenceId;
  const zumZielbild = (t) => {
    setT(t, true);
    document.querySelector('[data-testid="anchor-collect-frame"]')
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
  };
  // 🆕 Auftrag LP1, R2 · Linienprüfung; ohne `linienpruefung` liefert der
  // Hook `null` und lädt nichts.
  const lp = useLinienpruefung({
    aktiv: !!linienpruefung, liste: linienpruefung?.liste, nr: linienpruefung?.nr,
    sequenceId, onZielbild: zumZielbild,
  });
  // 🆕 Nachtrag 2 (Befund F2): ein anderer Punkt der Klickliste beginnt
  // leer — Segmente samt Geistern, Klicks, P3P-Entwurf, Geister-Auskunft.
  // Im selben Zug wie der Sprung: `segmentsRef` ist beim nächsten Rendern
  // leer, der Geister-Effekt beim Bildwechsel zieht dann nichts mit.
  const letzterPunktRef = useRef(null);
  useEffect(() => {
    if (zielT == null || !sprungBereit) return;
    const jetzt = klickliste ? { nr: klickliste.nr, t: zielT } : null;
    if (leerenBeiPunktwechsel(letzterPunktRef.current, jetzt)) {
      resetDraft();
      setGhostInfo(null);
      setKlickAmpel(null);
    }
    letzterPunktRef.current = jetzt;
    zumZielbild(zielT);
    /* eslint-disable-next-line */
  }, [zielT, sprungBereit, klickliste?.nr]);
  // Ungespeicherte Linien/Klicks: vor einem Wechsel fragt die Leiste nach.
  const entwurfOffen = points.length > 0 || p3pPoints.length > 0
    || p3pLines.length > 0 || segments.some((x) => !x.ghost);
  // F4: eine aktive Linie wird neu gezogen oder verändert → die Ampel des
  // vorigen Ankers gilt nicht mehr für das, was im Bild steht.
  useEffect(() => {
    if (entwurfOffen) setKlickAmpel(null);
  }, [entwurfOffen]);

  const onImgLoad = () => {
    const img = imgRef.current;
    if (!img) return;
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    // Jeder Frame DERSELBEN Sequenz hat dieselben Maße. Ein neues Objekt
    // mit identischen Werten ist für React trotzdem ein Zustandswechsel
    // und rendert das ganze Panel (~2400 DOM-Knoten) neu — gemessen eine
    // zweite Lang-Aufgabe von 51–200 ms bei JEDEM Frame-Wechsel, während
    // das JPEG selbst in 4–11 ms da ist. Nur bei echter Änderung setzen.
    setImgDims((d) => (d.w === w && d.h === h ? d : { w, h }));
  };

  const handleClick = (e) => {
    if (isFreeDraw) return;
    const img = imgRef.current;
    if (!img) return;
    const rect = img.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * imgDims.w;
    const y = ((e.clientY - rect.top) / rect.height) * imgDims.h;
    // 🆕 P3P: aktive Landmarke im Bild verorten + Auto-Advance
    if (isP3P) {
      // Feinjustierungs-Drag darf beim Loslassen KEINEN neuen Punkt setzen
      if (p3pGrabRef.current) {
        p3pGrabRef.current = false;
        return;
      }
      if (!p3pActive || p3pPoints.length >= 6) return;
      if (p3pPoints.some((p) => p.landmark_id === p3pActive)) return;
      const next = [...p3pPoints, { landmark_id: p3pActive, x, y }];
      setP3pPoints(next);
      const used = new Set(next.map((p) => p.landmark_id));
      const ids = p3pLandmarks.map((l) => l.id);
      const start = ids.indexOf(p3pActive);
      const nxt = [...ids.slice(start + 1), ...ids.slice(0, start + 1)]
        .find((id) => !used.has(id));
      setP3pActive(nxt || "");
      return;
    }
    if (points.length >= expectedClicks) return;
    setPoints([...points, { x, y }]);
  };

  // 🆕 Freies Zeichnen · Click-and-Drag (Naturalkoordinaten)
  const [editDrag, setEditDrag] = useState(null); // {i, mode:'p1'|'p2'|'move', last}
  const evtToNatural = (e) => {
    const img = imgRef.current;
    if (!img) return null;
    const rect = img.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * imgDims.w,
      y: ((e.clientY - rect.top) / rect.height) * imgDims.h,
    };
  };
  const distToSegment = (p, s) => {
    const dx = s.x2 - s.x1, dy = s.y2 - s.y1;
    const den = dx * dx + dy * dy;
    if (den < 1e-9) return Math.hypot(p.x - s.x1, p.y - s.y1);
    let t = ((p.x - s.x1) * dx + (p.y - s.y1) * dy) / den;
    t = Math.max(0, Math.min(1, t));
    return Math.hypot(p.x - (s.x1 + t * dx), p.y - (s.y1 + t * dy));
  };
  // Nachrücken (Geister-Overlay): Endpunkt-Griff oder Ganzlinien-Move
  const hitTestSegments = (p) => {
    const endTh = imgDims.w * 0.02;
    const lineTh = imgDims.w * 0.012;
    for (let i = segments.length - 1; i >= 0; i--) {
      const s = segments[i];
      if (Math.hypot(p.x - s.x1, p.y - s.y1) < endTh) return { i, mode: "p1" };
      if (Math.hypot(p.x - s.x2, p.y - s.y2) < endTh) return { i, mode: "p2" };
      if (distToSegment(p, s) < lineTh) return { i, mode: "move" };
    }
    return null;
  };
  const onFreeMouseDown = (e) => {
    if (e.button !== 0) return;
    const p = evtToNatural(e);
    if (!p) return;
    const hit = hitTestSegments(p);
    if (hit) {
      // 🆕 Touch-to-Activate: Anfassen aktiviert den Geist sofort
      setSegments((arr) => arr.map((s, idx) =>
        idx === hit.i ? { ...s, ghost: false } : s));
      setEditDrag({ ...hit, last: p });
      return;
    }
    setDrag({ x1: p.x, y1: p.y, x2: p.x, y2: p.y });
  };
  const onFreeMouseMove = (e) => {
    const p = evtToNatural(e);
    if (!p) return;
    if (editDrag) {
      setSegments((arr) => arr.map((s, idx) => {
        if (idx !== editDrag.i) return s;
        const out = { ...s, ghost: false }; // Nachrücken macht frisch
        if (editDrag.mode === "p1") { out.x1 = p.x; out.y1 = p.y; }
        else if (editDrag.mode === "p2") { out.x2 = p.x; out.y2 = p.y; }
        else {
          const ddx = p.x - editDrag.last.x, ddy = p.y - editDrag.last.y;
          out.x1 += ddx; out.y1 += ddy; out.x2 += ddx; out.y2 += ddy;
        }
        return out;
      }));
      setEditDrag((d) => (d ? { ...d, last: p } : d));
      return;
    }
    if (drag) setDrag((d) => (d ? { ...d, x2: p.x, y2: p.y } : d));
  };
  const onFreeMouseUp = () => {
    if (editDrag) {
      setEditDrag(null);
      return;
    }
    if (!drag) return;
    const len = Math.hypot(drag.x2 - drag.x1, drag.y2 - drag.y1);
    if (len >= Math.max(12, imgDims.w / 100)) {
      // 1 Segment pro Klasse — neues Zeichnen ersetzt das alte
      setSegments((s) => [
        ...s.filter((x) => x.cls !== activeCls),
        { cls: activeCls, ...drag, ghost: false },
      ]);
    }
    setDrag(null);
  };
  const onFreeContextMenu = (e) => {
    e.preventDefault();
    setSegments((s) => s.slice(0, -1));
  };
  // 🆕 P3P · Rechtsklick: auf einem Linien-Constraint → löscht DIESEN,
  // sonst den letzten Landmarken-Klick.
  const onP3PContextMenu = (e) => {
    e.preventDefault();
    const p = evtToNatural(e);
    if (p && p3pLines.length) {
      const th = imgDims.w * 0.02;
      const lineTh = imgDims.w * 0.012;
      const idx = p3pLines.findIndex((s) =>
        Math.hypot(p.x - s.x1, p.y - s.y1) < th
        || Math.hypot(p.x - s.x2, p.y - s.y2) < th
        || distToSegment(p, s) < lineTh);
      if (idx >= 0) {
        setP3pLines((arr) => arr.filter((_, i) => i !== idx));
        return;
      }
    }
    setP3pPoints((pp) => pp.slice(0, -1));
  };
  // 🆕 P3P · Feinjustierung: gesetzte Punkte per Drag nachziehen —
  // der debouncte dry_run löst neu und das Grid wandert live mit.
  const [p3pDrag, setP3pDrag] = useState(null);
  const p3pGrabRef = useRef(false);
  const onP3PMouseDown = (e) => {
    if (e.button !== 0) return;
    const p = evtToNatural(e);
    if (!p) return;
    const th = imgDims.w * 0.02;
    for (let i = p3pPoints.length - 1; i >= 0; i--) {
      if (Math.hypot(p.x - p3pPoints[i].x, p.y - p3pPoints[i].y) < th) {
        setP3pDrag(i);
        p3pGrabRef.current = true; // unterdrückt den Click nach dem Drag
        e.preventDefault();
        return;
      }
    }
    // 🆕 Grid-Linien-Ziehen: bestehende Constraints anfassen
    // (Endgriff = rotieren um den anderen Endpunkt, Mitte = verschieben)
    const lineTh = imgDims.w * 0.012;
    for (let i = p3pLines.length - 1; i >= 0; i--) {
      const s = p3pLines[i];
      let mode = null;
      if (Math.hypot(p.x - s.x1, p.y - s.y1) < th) mode = "p1";
      else if (Math.hypot(p.x - s.x2, p.y - s.y2) < th) mode = "p2";
      else if (distToSegment(p, s) < lineTh) mode = "move";
      if (mode) {
        setP3pLineDrag({ i, mode, last: p });
        p3pGrabRef.current = true;
        e.preventDefault();
        return;
      }
    }
    // 🆕 … oder eine projizierte Grid-Linie aufnehmen → wird Constraint
    // (gemischter Punkt+Linien-DLT zieht das Grid auf die Rasenlinie).
    // Hilfsgitter (aux_grid_*) ist NICHT greifbar — keine echte Rasenlinie.
    for (const gl of (p3pGridLines || [])) {
      if (gl.class_id.startsWith("aux_grid")) continue;
      if (p3pLines.some((s) => s.cls === gl.class_id)) continue;
      let hit = false;
      for (let k = 0; k + 1 < (gl.points?.length || 0); k++) {
        if (distToSegment(p, {
          x1: gl.points[k][0], y1: gl.points[k][1],
          x2: gl.points[k + 1][0], y2: gl.points[k + 1][1],
        }) < lineTh) { hit = true; break; }
      }
      if (!hit) continue;
      const a = gl.points[0];
      const b = gl.points[gl.points.length - 1];
      setP3pLines((arr) => [
        ...arr, { cls: gl.class_id, x1: a[0], y1: a[1], x2: b[0], y2: b[1] },
      ]);
      setP3pLineDrag({ i: p3pLines.length, mode: "move", last: p });
      p3pGrabRef.current = true;
      e.preventDefault();
      return;
    }
  };
  const onP3PMouseMove = (e) => {
    const p = evtToNatural(e);
    if (!p) return;
    if (p3pLineDrag) {
      setP3pLines((arr) => arr.map((s, idx) => {
        if (idx !== p3pLineDrag.i) return s;
        const out = { ...s };
        if (p3pLineDrag.mode === "p1") { out.x1 = p.x; out.y1 = p.y; }
        else if (p3pLineDrag.mode === "p2") { out.x2 = p.x; out.y2 = p.y; }
        else {
          const dx = p.x - p3pLineDrag.last.x;
          const dy = p.y - p3pLineDrag.last.y;
          out.x1 += dx; out.y1 += dy; out.x2 += dx; out.y2 += dy;
        }
        return out;
      }));
      if (p3pLineDrag.mode === "move") {
        setP3pLineDrag((d) => (d ? { ...d, last: p } : d));
      }
      return;
    }
    if (p3pDrag == null) return;
    setP3pPoints((arr) => arr.map((pt, idx) =>
      idx === p3pDrag ? { ...pt, x: p.x, y: p.y } : pt));
  };
  const onP3PMouseUp = () => { setP3pDrag(null); setP3pLineDrag(null); };

  // 🆕 Maß-Fit: W/E/B aus den aktuellen Constraints schätzen (L bleibt
  // Skalenreferenz). apply=true persistiert + löst alle Anker neu.
  // useAll=true nimmt zusätzlich ALLE gespeicherten Anker als Frames.
  const p3pConstraintCount = p3pPoints.length + p3pLines.length;
  const fovTrusted = !!p3pCamera?.source
    && !["dlt_selfcal", "fallback_default"].includes(p3pCamera.source);
  const fitMinConstraints = fovTrusted ? 4 : 5;
  const fittableAnchors = anchors.filter(
    (a) => a.zone_kind === "p3p_points" || a.zone_kind === "free_lines",
  ).length;
  // 🆕 seg_only-Anker mit den AKTUELLEN Kamera-/Feld-Werten neu lösen
  const segOnlyResolvable = anchors.filter(
    (a) => a.status === "seg_only" && a.zone_kind === "free_lines",
  ).length;
  const onResolveSegOnly = async () => {
    setResolvingSegOnly(true);
    try {
      const d = await resolveSegOnlyAnchors(sequenceId);
      if (d.n_upgraded > 0) {
        toast.success(
          `${d.n_upgraded}/${d.n_seg_only} Anker → joint gelöst (FOV dynamisch pro Shot/Anker)`,
        );
      } else {
        toast.info(
          `Kein Upgrade — ${d.n_seg_only} Anker bleiben seg_only. FOV (${d.fov_deg}°) oder Feldmaße prüfen, dann erneut.`,
        );
      }
      await refresh();
    } catch (e) {
      toast.error(fehlerText(e) || "Neu-Lösen fehlgeschlagen");
    } finally {
      setResolvingSegOnly(false);
    }
  };

  const runFieldFit = async (apply, useAll = fieldFitAll) => {
    setFieldFitBusy(true);
    try {
      const res = await fitFieldDims(sequenceId, {
        p3p_points: p3pPoints,
        p3p_lines: p3pLines.map((s) => ({
          class_id: s.cls, x1: s.x1, y1: s.y1, x2: s.x2, y2: s.y2,
        })),
        at_time_s: time,
        include_saved_anchors: !!useAll,
        apply,
      });
      if (apply) {
        toast.success(
          `Feldmaße übernommen · ${res.n_anchors_resolved} Anker neu gelöst · Fehler ${res.reproj_before_px} → ${res.reproj_after_px} px`,
        );
        setFieldFit(null);
        // Registry/Kamera/Grid mit den neuen Maßen aktualisieren
        getP3PLandmarks(sequenceId)
          .then((d) => {
            setP3pLandmarks(d.landmarks || []);
            setP3pCamera(d.camera || null);
          })
          .catch(() => {});
        setP3pPoints((p) => [...p]); // dry_run mit neuen Maßen re-triggern
        refresh();
      } else {
        setFieldFit(res);
        setFieldFitAll(!!useAll);
      }
    } catch (e) {
      toast.error(fehlerText(e) || "Maß-Fit fehlgeschlagen");
    } finally {
      setFieldFitBusy(false);
    }
  };

  // 🆕 2026-08 · Adaptives Feldmodell — Grid-Search-Job starten,
  // Fortschritt pollen, Job-Result im Panel rendern. `apply=false`:
  // Dry-Run (Konsens-Vorschlag). `apply=true`: Konsens wird persistiert
  // und ALLE Anker mit neuen Maßen neu gelöst.
  // `overrideFovDeg` (optional): atomarer FOV-Override — der Wert wird
  // vor dem Job in game_config.camera_fov_override_deg persistiert UND
  // im Solver direkt verwendet (bypass Shot-FOV-Kaskade). Vermeidet
  // Race Conditions zwischen separatem PATCH und Job-Start.
  const runAdaptFieldDims = async (apply = false, overrideFovDeg = null) => {
    if (apply) setAdaptApplying(true);
    else setAdaptBusy(true);
    setAdaptProg({
      pct: 0,
      message: apply
        ? "Konsens wird angewendet…"
        : overrideFovDeg != null
          ? `Grid-Search startet mit FOV ${overrideFovDeg}°…`
          : "Grid-Search startet…",
    });
    try {
      const payload = { apply };
      if (overrideFovDeg != null) payload.override_fov_deg = overrideFovDeg;
      const { job_id } = await adaptFieldDims(sequenceId, payload);
      const job = await pollJob(job_id, {
        onProgress: ({ progress_pct, message }) => {
          setAdaptProg({ pct: Math.round((progress_pct || 0) * 100), message: message || "läuft…" });
        },
      });
      const res = job.result || {};
      setAdaptResult(res);
      if (apply && res.applied) {
        const c = res.consensus || {};
        toast.success(
          `Feldmaße übernommen · W=${c.W} m · L=${c.L} m (L_mid=${c.L_mid} m) · ${res.n_anchors_resolved || 0} Anker neu gelöst`,
        );
        refresh();
      } else if (!apply) {
        if (res.consensus) {
          const c = res.consensus;
          toast.success(
            `Konsens gefunden · W=${c.W} m · L_mid=${c.L_mid} m aus ${res.n_suggestions_valid} Ankern`,
          );
        } else {
          toast.info(res.reason || "Kein Konsens — Details im Panel.");
        }
      }
    } catch (e) {
      toast.error(e?.detail || e?.message || "Feldmaß-Schätzung fehlgeschlagen");
    } finally {
      setAdaptBusy(false);
      setAdaptApplying(false);
      setAdaptProg(null);
    }
  };

  // 🆕 FOV-Schnellübernahme: atomarer Aufruf — der Grid-Search-Endpunkt
  // schreibt den FOV-Override VOR dem Job in die DB UND übergibt ihn
  // direkt an den Solver. Ein einziger Request → keine Race Conditions,
  // keine Shot-FOV-Kaskaden-Kollision mit stale DLT-Kurven.
  const acceptFovOverrideAndRerun = async (dltDeg) => {
    setAdaptResult(null);
    await runAdaptFieldDims(false, dltDeg);
    // Kamera-Anzeige aktualisieren (P3P-Panel spiegelt den DB-Override,
    // der jetzt persistent aus dem Job-Endpoint stammt).
    getP3PLandmarks(sequenceId)
      .then((d) => setP3pCamera(d.camera || null))
      .catch(() => {});
  };

  const reprojErr = reprojCheck?.reproj_error_px;
  // 🆕 Kamera-Zoom (FOV) live beim Zeichnen: Sweep-Treffer = Tele-Shot.
  const fovInfo = reprojCheck?.fov_sweep_deg
    ? ` · Kamera-Zoom (FOV) ≈ ${reprojCheck.fov_sweep_deg}° via Sweep — Tele-Shot erkannt`
    : reprojCheck?.camera_fov_deg
    ? ` · FOV ${Math.round(reprojCheck.camera_fov_deg)}°${reprojCheck.camera_fov_source ? ` (${reprojCheck.camera_fov_source})` : ""}`
    : "";
  // 🆕 Mixed-Ampel: Restfehler nach dem Linien-Ziehen (sanfte Warnstufen,
  // blockiert den Save NICHT) — grün <10px · gelb 10–30px · rot >30px.
  const mixedLevel = reprojCheck?.mixed && reprojErr != null
    ? (reprojErr > 30 ? "rot" : reprojErr >= 10 ? "gelb" : "gruen")
    : null;
  // 🆕 P3P: Lösungs-Auswahl (Ambiguitäts-Toggle) + Plausibilität der
  // AKTUELL gewählten Lösung (Server-Warnung gilt für chosen_index).
  const p3pSolutions = reprojCheck?.p3p?.solutions || [];
  const p3pSel = p3pSolutions[p3pSolIdx] || null;
  const p3pHeightSel = p3pSel?.camera_height_m ?? reprojCheck?.p3p?.camera_height_m;
  const p3pGridLines = p3pSel?.grid_lines ?? reprojCheck?.grid_lines;
  // 🆕 Gekippte Lösung: Grid (inkl. Hilfsgitter) komplett außerhalb
  const p3pGridEmpty = !!reprojCheck && !reprojCheck.error
    && (p3pGridLines || []).length === 0;
  // 🆕 Anti-„0.0 px"-Falle + Abdeckungs-Check (alles oben = Extrapolation)
  const p3pExact = !!reprojCheck?.exactly_determined && !reprojCheck?.error;
  const p3pAllYs = [
    ...p3pPoints.map((p) => p.y),
    ...p3pLines.flatMap((s) => [s.y1, s.y2]),
  ];
  const p3pTopHeavy = !!reprojCheck && !reprojCheck.error
    && p3pAllYs.length >= 3 && imgDims.h > 1
    && Math.max(...p3pAllYs) < imgDims.h * 0.55;
  // 🆕 Server-Plausibilitäts-Warnung greift jetzt AUCH bei DLT/Mixed;
  // beim P3P-Lösungs-Toggle lokaler Check (Warnung gilt je Lösung).
  // 🆕 stability_warning: fast-kollineare 4-Punkt-DLT (Grid kippt).
  const p3pWarnSel = (reprojCheck?.dlt || reprojCheck?.mixed)
    ? (reprojCheck?.stability_warning
        || reprojCheck?.plausibility_warning || null)
    : p3pHeightSel != null && (p3pHeightSel < 2 || p3pHeightSel > 40)
      ? (reprojCheck?.plausibility_warning
          || `Kamera-Höhe ≈ ${p3pHeightSel} m ist unplausibel (2–40 m) — FOV/Profil prüfen oder 4. Landmarke klicken (FOV-frei).`)
      : p3pGridEmpty
        ? "Grid liegt komplett außerhalb des Bildes — Lösung gekippt: Landmarken-Zuordnung (links/rechts) prüfen oder Punkte neu setzen."
        : null;
  // v1.3 · Dual-Pfad: Reproj-Fehler ist rein informativ — der Save ist
  // ab vollem Klick-Satz IMMER freigegeben (Pfad 1 exportiert die
  // rohen Klick-Segmente bedingungslos). P3P/DLT: Save erst wenn die
  // Pose gelöst ist (kein seg-Fallback).
  const canSave = isP3P
    ? p3pSolvable && !!reprojCheck && !reprojCheck.error && !saving
    : isFreeDraw
      ? activeSegments.length >= 1 && !saving
      : points.length === expectedClicks && !!presetId
        && (!requiresSide || !!side) && !saving;

  // 🆕 2026-08-23 · Punktstand und Reset einmal definiert, damit die
  // Werkzeugleiste am Bild und nicht zwei Stellen dasselbe bauen.
  // 🆕 Sagen, ob die Geister der Kamera gefolgt sind. Ein mitgezogener
  // Geist ist eine brauchbare Schablone, ein stehengebliebener nicht —
  // der Unterschied darf nicht geraten werden müssen.
  const ghostHinweis = ghostInfo?.laeuft
    ? ", ziehe mit …"
    : ghostInfo?.verschoben
      ? `, mitgezogen${ghostInfo.verworfen ? ` · ${ghostInfo.verworfen} aus dem Bild` : ""}`
      : ghostInfo?.grund
        ? `, stehengeblieben: ${ghostInfo.grund}`
        : "";

  const statusText = isP3P
    ? `${p3pPoints.length}/3–6 Landmarken${p3pLines.length ? ` + ${p3pLines.length} Linie${p3pLines.length > 1 ? "n" : ""}` : ""}${p3pPoints.length === 3 && !p3pLines.length ? " · Tipp: 4. Punkt = FOV-frei" : ""}${p3pActive && p3pPoints.length < 6 ? ` · aktiv: ${p3pLandmarks.find((l) => l.id === p3pActive)?.label_de || p3pActive}` : ""}`
    : isFreeDraw
      ? `${activeSegments.length} aktiv${ghostCount > 0 ? ` · ${ghostCount} Geist${ghostCount === 1 ? "" : "er"} (zählen nicht${ghostHinweis})` : ""} · aktiv: ${LINE_CLASS_META[activeCls].label}`
      : `${points.length}/${expectedClicks} Klicks${!combo ? ` · ${LABELS_WIDTH[Math.min(points.length, 3)]}` : ""}`;

  // 🆕 2026-08-25 · Vorschlag des Modells holen. Er landet als GEIST im
  // Bild — passiv, zählt weder im Solver noch beim Speichern, bis du eine
  // Linie anfasst. Damit wechselt die Rolle von zeichnen zu bestätigen,
  // ohne dass ungeprüfte Modellausgabe zu Gold wird. Der Modus
  // HOMOGRAPHY_SOURCE bleibt bewusst auf `manual`.
  const [vorschlagLaeuft, setVorschlagLaeuft] = useState(false);
  const holeVorschlag = async () => {
    if (imgDims.w <= 2) return;
    setVorschlagLaeuft(true);
    try {
      const d = await lineProposal(sequenceId, loadTime, imgDims.w, imgDims.h);
      if (!d.ok) {
        toast.error(`Kein Vorschlag: ${d.grund || d.status || "unbekannt"}`);
        return;
      }
      // 🆕 Der Vorschlag ersetzt, was im Bild liegt — das können gute,
      // mitgezogene Geister sein. Ein schlechter Vorschlag hat die schon
      // einmal zerstört (25.08., Rückmeldung des Nutzers). Deshalb ein
      // Rückweg: der vorige Stand wird gemerkt und ist einen Klick entfernt.
      const vorher = segmentsRef.current;
      setSegments(d.segments.map((n) => ({
        cls: n.cls, x1: n.x1, y1: n.y1, x2: n.x2, y2: n.y2,
        ghost: true, vorschlag: true,
      })));
      setGhostInfo(null);
      // Der Vergleich zur Shot-Kalibrierung wird GEMELDET, nicht still
      // gefiltert. Verglichen wird Linie gegen Linie, lotrecht — die alte
      // Zahl mittelte über die Feld-Keypoints IM Bild, bei einer
      // Teleaufnahme oft nur zwei. Am 25.08. meldete sie „7 px", während
      // eine Linie 902 px danebenlag und zwei Linien gezeichnet wurden, die
      // es im Bild gar nicht gibt.
      const v = d.vergleich;
      const guete = [
        d.variant,
        d.confidence != null ? `Güte ${Number(d.confidence).toFixed(2)}` : null,
        v?.max_px != null
          ? `schlechteste Linie ${Number(v.max_px).toFixed(0)} px daneben`
          : "kein Vergleich möglich",
        v?.phantome?.length
          ? `${v.phantome.length}× nicht im Bild (${v.phantome.join(", ")})`
          : null,
      ].filter(Boolean).join(" · ");
      const grob = (v?.max_px != null && Number(v.max_px) > 25)
        || !!v?.phantome?.length;
      (grob ? toast.warning : toast.success)(
        `${d.segments.length} Linien vorgeschlagen (${guete})`
        + ` — ${d.modell}, ${d.dauer_s} s`,
        vorher.length
          ? { action: { label: "Zurück", onClick: () => setSegments(vorher) },
              duration: 15000 }
          : undefined);
    } catch (e) {
      toast.error(fehlerText(e) || "Vorschlag fehlgeschlagen");
    } finally {
      setVorschlagLaeuft(false);
    }
  };

  // 🆕 2026-08-31 · Bester Frame pro Shot statt jeder Frame.
  //
  // Die Selbstkalibrierung braucht DREI Anker im Shot, nicht eine
  // Homographie pro Frame. Gemessen liefert das Modell an einem beliebigen
  // Frame in 42 % der Fälle etwas — aus zehn Proben ist damit mit 99,6 %
  // mindestens ein Treffer dabei. Der Scan sucht sie, sortiert nach Güte,
  // und du springst mit einem Klick hin. Gespeichert wird nichts.
  const [scanLaeuft, setScanLaeuft] = useState(false);
  const [scanMeldung, setScanMeldung] = useState("");
  const [scanErgebnis, setScanErgebnis] = useState(null);

  const findeBesteFrames = async () => {
    if (!currentShot) {
      toast.error("Kein Shot erkannt an dieser Stelle");
      return;
    }
    setScanLaeuft(true);
    setScanErgebnis(null);
    setScanMeldung("Scan gestartet …");
    try {
      const { job_id } = await scanShotAnchorCandidates(
        sequenceId, currentShot.shot_id,
        { n_proben: 10,
          img_ref: imgDims.w > 2 ? { w: imgDims.w, h: imgDims.h } : undefined },
      );
      const job = await pollJob(job_id, {
        onProgress: ({ message }) => setScanMeldung(message || "läuft …"),
      });
      const r = job.result || {};
      if (!r.ok) {
        toast.error(`Scan fehlgeschlagen: ${r.grund || "unbekannt"}`);
        return;
      }
      setScanErgebnis(r);
      const b = r.bilanz || {};
      (b.reicht ? toast.success : toast.warning)(
        `Shot ${r.shot_id}: ${b.text || "geprüft"}`);
    } catch (e) {
      toast.error(e?.detail || e?.message || "Scan fehlgeschlagen");
    } finally {
      setScanLaeuft(false);
      setScanMeldung("");
    }
  };

  const resetDraft = () => {
    setPoints([]); setSegments([]); setDrag(null);
    setP3pPoints([]); setP3pLines([]); setP3pLineDrag(null);
    setReprojCheck(null);
  };

  const onSave = async () => {
    setSaving(true);
    if (klickliste) setKlickAmpel(null);        // F4: beim nächsten Speichern
    try {
      const payload = isP3P
        ? {
            p3p_points: p3pPoints,
            p3p_lines: p3pLines.map((s) => ({
              class_id: s.cls, x1: s.x1, y1: s.y1, x2: s.x2, y2: s.y2,
            })),
            at_time_s: time, p3p_solution_index: p3pSolIdx,
          }
        : isFreeDraw
        // 🆕 Touch-to-Activate: NUR aktive Segmente werden gespeichert —
        // passive Geister (z.B. aus dem Bild gewanderte Linien) nie.
        ? { free_lines: activeSegments.map(segPayload), at_time_s: time }
        : combo
          ? { combo_id: combo.id, at_time_s: time, image_points: points }
          : {
              preset_id: presetId,
              side: requiresSide ? side : null,
              at_time_s: time,
              image_points: points,
            };
      const res = await createAnchor(sequenceId, {
        ...payload,
        // 🆕 Pixelraum-Vertrag: Anzeigeraum der Klicks mitschicken.
        img_ref: imgDims.w > 2 ? { w: imgDims.w, h: imgDims.h } : undefined,
        // 🆕 Nachtrag 4 (T3): in der Klickliste wartet die
        // Selbstkalibrierung 120 s nach dem letzten Anker.
        selfcal_warten_s: selfcalWartenS(klickliste),
      });
      // F4: in der Klickliste trägt die Meldung die Stufe der 3D-Ampel
      // (aus der Antwort, nichts wird nachgerechnet); sonst wie bisher.
      const meldung = klickliste ? ampelMeldung(res) : null;
      if (meldung) toast[meldung.art](meldung.text);
      else toast.success(
        isP3P
          ? `${res.mixed ? "Punkt+Linien" : res.dlt ? "DLT" : "P3P"}-Anker #${res.n_extra_zones} gespeichert · t=${time.toFixed(1)}s${
              res.mixed
                ? ` · ${res.mixed.n_points}P+${res.mixed.n_lines}L${res.mixed.fov_estimate_deg ? ` · FOV selbstkalibriert ≈ ${res.mixed.fov_estimate_deg}°` : " · FOV-frei"}`
                : res.dlt
                ? (res.dlt.fov_estimate_deg ? ` · FOV selbstkalibriert ≈ ${res.dlt.fov_estimate_deg}°` : " · FOV-frei")
                : ` · Kamera ≈ ${res.p3p?.camera_height_m ?? "?"} m hoch`
            }`
          : `Anker #${res.n_extra_zones} gespeichert · t=${time.toFixed(1)}s` +
            (combo || isFreeDraw
              ? ` · ${res.reproj_error_px != null ? `${res.reproj_error_px} px` : "seg_only"}`
              : ""),
      );
      setPoints([]);
      if (isP3P) {
        setP3pPoints([]);
        setP3pLines([]);
        setP3pLineDrag(null);
        setP3pActive(p3pLandmarks[0]?.id || "");
        // FOV kann durch DLT-Selbstkalibrierung neu sein → Kamera nachladen
        getP3PLandmarks(sequenceId)
          .then((d) => setP3pCamera(d.camera || null))
          .catch(() => {});
      }
      if (isFreeDraw) {
        // 🆕 Touch-to-Activate: alle Segmente werden passive Geister
        setSegments((s) => s.map((x) => ({ ...x, ghost: true })));
        toast.info("Segmente sind jetzt passive Geister — antippen/nachrücken aktiviert sie wieder.");
      } else {
        setSegments([]);
      }
      setDrag(null);
      setReprojCheck(null);
      // F4: nach dem Umwandeln in Geister (derselbe Zug), damit der
      // Leer-Effekt oben sie nicht gleich wieder wegnimmt.
      if (meldung) {
        setKlickAmpel({
          ...meldung, nr: klickliste.nr, anchorId: res.anchor?.id || null,
          quality: res.quality || res.anchor?.quality || null,
        });
      }
      await refresh();
      pollSelfcalAuto(); // 🆕 Auto-Selfcal des Backends abwarten
      maybeAutoRetrack(time); // 🆕 Anker in rotem/gelbem Segment → Auto-Nachtracken
    } catch (e) {
      toast.error(fehlerText(e) || "Anker konnte nicht gespeichert werden");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (anchorId) => {
    try {
      await deleteAnchor(sequenceId, anchorId);
      toast.success("Anker gelöscht");
      await refresh();
      pollSelfcalAuto(); // 🆕 Auto-Selfcal des Backends abwarten
    } catch (e) {
      toast.error(fehlerText(e) || "Löschen fehlgeschlagen");
    }
  };

  const onExport = async () => {
    setExporting(true);
    setExportMsg("Export gestartet…");
    try {
      const { job_id } = await exportPseudoLabels();
      const job = await pollJob(job_id, {
        onProgress: ({ message }) => setExportMsg(message || "läuft…"),
      });
      const r = job.result || {};
      const up = r.modal_upload || {};
      toast.success(
        `Trainingspool aktualisiert: ${r.n_written ?? "?"} Samples exportiert, ` +
        `${up.n_files_uploaded ?? "?"} Dateien → Modal-Volume /pseudo`,
      );
      setExportMsg("");
    } catch (e) {
      toast.error(e?.detail || e?.message || "Export fehlgeschlagen");
      setExportMsg("");
    } finally {
      setExporting(false);
    }
  };

  const guidance = preset ? clickGuidanceFor(preset, { side }) : null;
  const collected = anchors.filter((a) => a.source === "anchor_collect");
  const fmtTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toFixed(2).padStart(5, "0")}`;

  return (
    <>
    {klickliste && (
      <KlicklisteLeiste
        name={klickliste.name} nr={klickliste.nr} sequenceId={sequenceId}
        dirty={entwurfOffen} onVerwerfen={resetDraft} onZielbild={zumZielbild}
      />
    )}
    {lp && <LinienpruefungLeiste lp={lp} />}
    <div
      data-testid="anchor-collect-panel"
      className="border border-cyan-500/40 bg-card rounded-sm p-5 grid lg:grid-cols-5 gap-6"
    >
      {/* ── Frame + Klick-Canvas ──
          🆕 2026-08-23 · `lg:sticky` ab der Zweispalten-Breite: die rechte
          Spalte ist mehrere Tausend Pixel hoch (Shot-Liste allein 1655 px).
          Ohne das nimmt jedes Scrollen dorthin das Bild mit aus dem
          Sichtfeld — genau das Hin-und-Her-Schieben.

          `self-start` ist nötig, weil der Grid-Default die Spalte auf die
          Höhe der Nachbarspalte streckt und sticky dann keinen Spielraum
          hat. `max-h`/`overflow-y-auto` sind die Absicherung: ist der
          Arbeitsblock höher als das Fenster (Bild allein 742 px), scrollt
          er INNEN statt unerreichbar festzuhängen — bei sticky wäre der
          untere Teil sonst nicht mehr anfahrbar. Die Klick-Koordinaten
          bleiben korrekt, sie kommen aus getBoundingClientRect().

          ⚠ FALLE, die mich am 23.08.2026 zweimal erwischt hat: `overflow-y`
          auf einem Wert ungleich `visible` zwingt laut CSS-Spezifikation
          auch `overflow-x` von `visible` auf `auto`. Diese Spalte schneidet
          also HORIZONTAL ab, obwohl nur die Y-Achse gemeint war. Alles
          hier drin muss in die Spaltenbreite passen oder umbrechen
          (`flex-wrap`) — sonst verschwindet es lautlos nach rechts. Prüfen
          mit `spalte.scrollWidth > spalte.clientWidth`. */}
      <div className="lg:col-span-3 space-y-3 lg:sticky lg:top-4 lg:self-start lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto lg:pr-1">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-cyan-300">
              Data-Flywheel · Anker-Sammelmodus
            </div>
            <h3 className="font-display font-black uppercase text-lg tracking-wider">
              Anker {collected.length}/{TARGET_ANCHORS}
            </h3>
          </div>
          <div
            data-testid="anchor-collect-eligible-count"
            className="font-mono text-[10px] text-emerald-300 border border-emerald-500/30 bg-emerald-500/10 rounded-sm px-2 py-1"
          >
            {nEligible} Training-Samples verfügbar
          </div>
        </div>

        {/* 2026-09-01 · Naechster-Schritt-Engine (Prozess-Soll Punkt 2):
            Statuskette + EIN empfohlener Schritt, statt Knopf-Raten.
            refreshKey haengt an der Ankerzahl — jede Anker-Mutation
            laedt den Status neu, ohne eigenes Polling. */}
        <PipelinePanel seqId={sequenceId} refreshKey={collected.length} />
        {/* Konsens-Review (Prozess 4a): rendert nur bei
            unbestaetigten Auffaelligkeiten — wie das NP-Panel. */}
        <KonsensPanel seqId={sequenceId} refreshKey={collected.length} />
        {/* Feld-Fit-Vorschlag (Prozess 3): rendert nur bei offener
            Abweichung mit echter Beweislage (strenge Schwelle). */}
        <FieldFitVorschlag seqId={sequenceId} refreshKey={collected.length} />
        {/* 🆕 2026-09-02 · Netz-Selfcal (Stufe 4): Kamera-Posen für
            ankerlose Wide-Shots aus reinen Netz-Beobachtungen — Job
            bewusst anstoßen, Ergebnis übernehmen/ablehnen. */}
        <NetzSelfcalVorschlag seqId={sequenceId} refreshKey={collected.length} />

        {/* 🆕 2026-08-23 · Zoom-Rahmen. Der Transform sitzt auf dem INNEREN
            Wrapper und umfasst Bild + Overlays gemeinsam — dadurch liefert
            img.getBoundingClientRect() schon die transformierte Lage und
            evtToNatural() bleibt unverändert richtig. Zoom-Bedienung und
            Lupe liegen AUSSERHALB des Transforms, damit sie nicht
            mitskalieren. Siehe FrameZoom.jsx. */}
        <div ref={viewportRef}
             className="relative border border-border rounded-sm overflow-hidden bg-secondary"
             style={{ cursor: zoomView.cursor, minHeight: 220 }}
             onMouseDown={(e) => {
               if (zoomView.isPanEvent(e)) { zoomView.startPan(e); return; }
               if (isFreeDraw) onFreeMouseDown(e);
               else if (isP3P) onP3PMouseDown(e);
             }}
             onMouseMove={(e) => {
               trackCursor(e);
               if (zoomView.panning) return;
               if (isFreeDraw) onFreeMouseMove(e);
               else if (isP3P) onP3PMouseMove(e);
             }}
             onMouseUp={zoomView.panning ? undefined : (isFreeDraw ? onFreeMouseUp : isP3P ? onP3PMouseUp : undefined)}
             onMouseLeave={(e) => {
               setCursorPos(null);
               if (zoomView.panning) return;
               if (isFreeDraw) onFreeMouseUp(e);
               else if (isP3P) onP3PMouseUp(e);
             }}
             onContextMenu={isFreeDraw ? onFreeContextMenu : isP3P ? onP3PContextMenu : undefined}>
          <div style={zoomView.contentStyle} className="relative">
          <img
            ref={imgRef}
            data-testid="anchor-collect-frame"
            src={frameUrl(sequenceId, loadTime)}
            alt="frame"
            onLoad={onImgLoad}
            onClick={isFreeDraw ? undefined : handleClick}
            className="block w-full h-auto select-none"
            draggable={false}
          />
          {/* 🆕 Freies Zeichnen: Segmente + Drag-Preview */}
          {isFreeDraw && (
            <FreeDrawOverlay
              segments={segments} drag={drag}
              imgDims={imgDims} activeCls={activeCls}
              zoom={zoomView.zoom}
            />
          )}
          {/* 🆕 P3P: Landmarken-Marker + projiziertes Feld-Grid der
              aktuell gewählten Lösung (Ambiguitäts-Toggle) */}
          {isP3P && (
            <P3POverlay
              points={p3pPoints} landmarks={p3pLandmarks}
              gridLines={p3pGridLines} lines={p3pLines} imgDims={imgDims}
              zoom={zoomView.zoom}
            />
          )}
          {/* Linien-Segmente je 2er-Klickpaar (Combo-Modus) */}
          {combo && imgDims.w > 1 && (
            <svg
              className="absolute inset-0 w-full h-full pointer-events-none"
              viewBox={`0 0 ${imgDims.w} ${imgDims.h}`}
              preserveAspectRatio="none"
            >
              {Array.from({ length: Math.floor(points.length / 2) }).map((_, li) => (
                <line
                  key={li}
                  x1={points[li * 2].x} y1={points[li * 2].y}
                  x2={points[li * 2 + 1].x} y2={points[li * 2 + 1].y}
                  stroke={LINE_COLORS[li % LINE_COLORS.length]}
                  strokeWidth={Math.max(2, imgDims.w / 480) / zoomView.zoom}
                  strokeDasharray="8 6"
                />
              ))}
            </svg>
          )}
          {points.map((p, i) => {
            const col = combo
              ? LINE_COLORS[Math.floor(i / 2) % LINE_COLORS.length]
              : "#67e8f9";
            return (
              <div
                key={i}
                data-testid={`anchor-collect-point-${i}`}
                className="absolute w-5 h-5 -ml-2.5 -mt-2.5 rounded-full border-2 flex items-center justify-center font-mono text-[10px] pointer-events-none"
                style={{
                  left: `${(p.x / imgDims.w) * 100}%`,
                  top: `${(p.y / imgDims.h) * 100}%`,
                  borderColor: col,
                  backgroundColor: `${col}44`,
                  color: "#f0fdff",
                  // 🆕 2026-08-23 · gegen den Zoom-Transform skalieren,
                  // sonst wird der Marker bei 8× so gross, dass er den
                  // Punkt verdeckt, den er markiert.
                  transform: `scale(${1 / zoomView.zoom})`,
                  transformOrigin: "center",
                }}
              >{i + 1}</div>
            );
          })}
          {/* 🆕 Auftrag LP1, R2 · erkannte und Soll-Linien der Prüfliste */}
          {lp && <LinienpruefungEbene lp={lp} imgDims={imgDims} zoom={zoomView.zoom} />}
          </div>
          {/* Ab hier außerhalb des Transforms: skaliert nicht mit. */}
          <FrameZoomControls
            zoom={zoomView.zoom}
            onIn={zoomView.zoomIn}
            onOut={zoomView.zoomOut}
            onReset={zoomView.reset}
            loupe={loupeOn}
            onLoupeToggle={() => setLoupeOn((v) => !v)}
          />
          {loupeOn && !zoomView.panning && (isFreeDraw || isP3P || combo) && (
            <FrameLoupe
              src={frameUrl(sequenceId, loadTime)}
              imgDims={imgDims}
              natural={cursorPos?.natural}
              view={cursorPos?.view}
              viewportSize={cursorPos?.viewport}
              zoom={zoomView.zoom}
            />
          )}
        </div>

        {/* 🆕 2026-08-23 · Werkzeugleiste DIREKT unter dem Bild.
            Vorher standen Linienauswahl, Punktstand und Speichern in der
            rechten Spalte — dort aber UNTER der Arbeitsliste (405 px) und
            dem Selfcal-Panel (126 px), also unterhalb der Fensterkante.
            Der Arbeitsablauf ist Klasse wählen → im Bild ziehen →
            wiederholen → speichern; Auge und Maus sind dabei am Bild.
            Also gehören genau diese drei Dinge ans Bild, und zwar
            zusammen mit ihm feststehend (sticky), damit die langen
            Info-Panels der rechten Spalte darunter weglaufen können,
            ohne das Bild mitzunehmen. Alles Lesbare (Hilfetexte,
            Warnungen, Reprojektionsfehler) bleibt rechts. */}
        <div
          data-testid="anchor-tool-rail"
          className="border border-cyan-500/30 bg-secondary/60 rounded-sm p-2 space-y-2"
        >
          {isFreeDraw && (
            <LineClassPitch
              active={activeCls}
              onSelect={setActiveCls}
              drawnClasses={activeSegments.map((s) => s.cls)}
              classes={drawClasses}
            />
          )}
          {/* Die P3P-Landmarken-Palette bleibt absichtlich RECHTS: sie ist
              nach Gruppen sortiert 408 px hoch und würde das Bild wieder
              wegdrücken — und P3P schaltet die Landmarke nach jedem Klick
              selbst weiter (Auto-Advance), ist also kein Pro-Klick-
              Bedienelement wie die Linienklassen. */}
          <div className="flex items-center gap-2">
            <div
              data-testid="anchor-collect-point-status"
              className={`flex-1 border rounded-sm px-2 py-1 font-mono text-[10px] truncate ${
                canSave
                  ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-300"
                  : "border-amber-500/40 bg-amber-500/5 text-amber-300"
              }`}
              title={statusText}
            >
              {statusText}
            </div>
            {isFreeDraw && (
              <Button
                data-testid="anchor-collect-proposal" variant="outline" size="sm"
                onClick={holeVorschlag} disabled={vorschlagLaeuft}
                className="font-mono text-[10px] shrink-0 border-violet-500/50 text-violet-300"
                title="Das Modell schlägt die sechs Feldlinien für diesen Frame vor. Sie kommen als Geister — passiv, bis du sie anfasst."
              >
                {vorschlagLaeuft ? "rechnet …" : "Vorschlag"}
              </Button>
            )}
            {isFreeDraw && (
              <Button
                data-testid="anchor-collect-scan-shot" variant="outline" size="sm"
                onClick={findeBesteFrames} disabled={scanLaeuft || !currentShot}
                className="font-mono text-[10px] shrink-0 border-violet-500/50 text-violet-300"
                title="Tastet zehn Frames dieses Shots ab und sortiert sie nach Güte. Nützlicher als der Einzelvorschlag: an einem beliebigen Frame trifft das Modell nur in 42 % der Fälle, aus zehn Proben fast immer."
              >
                {scanLaeuft ? (scanMeldung || "scannt …") : "Beste Frames"}
              </Button>
            )}
            <Button
              data-testid="anchor-collect-reset" variant="outline" size="sm"
              onClick={resetDraft}
              className="font-mono text-[10px] shrink-0"
              title="Alle Klicks/Segmente dieses Entwurfs verwerfen"
            >
              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset
            </Button>
            <Button
              data-testid="anchor-collect-save" size="sm"
              disabled={!canSave} onClick={onSave}
              className="bg-cyan-500 text-black hover:bg-cyan-400 font-display font-bold uppercase tracking-widest disabled:opacity-40 shrink-0"
              title={canSave
                ? "Anker speichern"
                : "Noch nicht speicherbar — der Punktstand links sagt, was fehlt"}
            >
              <Check className="w-3.5 h-3.5 mr-1" /> Speichern
            </Button>
          </div>
        </div>

        {/* Zeit-Scrubber + Kamera-Abschnitte-Leiste. Beide liegen im selben
            flex-1-Container → EXAKT gleiche Breite: Klickposition auf der
            Leiste = Zeitposition auf dem Slider. */}
        {/* 🆕 2026-08-23 · Zwei Zeilen statt einer. Vorher standen beide
            Knopfgruppen (je `shrink-0`), der Slider und die Zeitanzeige in
            EINER Reihe: gemessen 871 px nötig gegen 532 px vorhandene
            Spaltenbreite. Der Überstand fiel nur nicht auf, solange er
            sichtbar aus der Spalte ragte — seit die Sticky-Spalte ein
            `overflow` hat, wird er abgeschnitten, und die Zeitanzeige war
            weg. `flex-wrap` + `order-last basis-full` am Slider-Block
            gibt ihm eine eigene, volle Zeile; die Knöpfe und die Uhr
            teilen sich die erste. Nebeneffekt, der erwünscht ist: der
            Slider wird breiter und damit genauer. */}
        <div className="flex items-start gap-2 flex-wrap">
          <div className="flex items-center gap-2 h-8 shrink-0">
            <Button data-testid="anchor-seek-prev-shot" size="sm" variant="ghost"
                    onClick={() => seekShot(-1)}
                    title="Zum vorherigen Shot springen (alle Einstellungen, auch ohne Anker)"
                    className="font-mono text-[10px] text-emerald-300">
              <ChevronLeft className="w-3 h-3" />S
            </Button>
            <Button data-testid="anchor-seek-prev-anchor" size="sm" variant="ghost"
                    onClick={() => seekAnchor(-1)}
                    title="Zum vorherigen Anker springen"
                    className="font-mono text-[10px] text-cyan-300">
              <ChevronLeft className="w-3 h-3" />⚓
            </Button>
            <Button data-testid="anchor-seek-minus-5s" size="sm" variant="ghost"
                    onClick={() => setT(time - 5, true)} className="font-mono text-[10px]">
              <ChevronLeft className="w-3 h-3" />5s
            </Button>
            <Button data-testid="anchor-seek-minus-1s" size="sm" variant="ghost"
                    onClick={() => setT(time - 1, true)} className="font-mono text-[10px]">
              <ChevronLeft className="w-3 h-3" />1s
            </Button>
            <Button data-testid="anchor-seek-minus-1f" size="sm" variant="ghost"
                    onClick={() => setT(time - 1 / fps, true)} className="font-mono text-[10px]">
              <ChevronLeft className="w-3 h-3" />1f
            </Button>
          </div>
          <div className="order-last basis-full min-w-0 space-y-1.5">
            <div className="h-8 flex items-center">
              <Slider
                data-testid="anchor-seek-slider"
                value={[time]} min={0} max={duration} step={1 / fps}
                onValueChange={([v]) => setT(v)}
                className="flex-1"
              />
            </div>
            {/* 🆕 Kamera-Segmentierungs-Pass: Klick springt zur Mitte eines
                Abschnitts ohne Anker (Frame-gerastert via setT). */}
            <CameraCoverageBar
              variant="inline"
              sequenceId={sequenceId}
              duration={duration}
              onSeek={(t) => setT(t, true)}
            />
            {/* 🆕 Aktueller Shot direkt am Video: sehen, prüfen,
                klassifizieren — ohne zur Shot-Liste zu scrollen.
                🆕 2026-08-23 · `flex-wrap`: die Leiste trägt seit dem
                Teilen-Umbau Shot-Nummer, Zeitfenster, Klasse, drei
                Werkzeugknöpfe, die Schnitt-Hinweise UND sechs
                Klassen-Knöpfe. Ohne Umbruch ragt sie aus der Spalte und
                wird vom `overflow` der Sticky-Spalte abgeschnitten. */}
            {currentShot && (
              <div
                data-testid="current-shot-bar"
                className="flex items-center gap-2 gap-y-1 flex-wrap font-mono text-[10px] border border-border rounded-sm px-2 py-1 bg-secondary/40"
              >
                <span className="text-cyan-300 shrink-0">Shot {currentShot.shot_id}</span>
                <span className="text-muted-foreground shrink-0 tabular-nums">
                  {Number(currentShot.start_s).toFixed(2)}–{Number(currentShot.end_s).toFixed(2)} s
                </span>
                <span
                  data-testid="current-shot-class"
                  className="text-muted-foreground truncate"
                >
                  · {currentShot.class_manual
                    ? `${currentShot.class_manual} ✋${currentShot.class_auto?.label
                        ? ` · auto: ${currentShot.class_auto.label} ${Math.round((currentShot.class_auto.confidence || 0) * 100)} %`
                        : ""}`
                    : currentShot.class_auto?.label
                    ? `${currentShot.class_auto.label} ${Math.round((currentShot.class_auto.confidence || 0) * 100)} %`
                    : "nicht klassifiziert"}
                </span>
                {/* 🆕 2026-08-23 · Verborgene Schnitte: hier teilen, hier
                    gründlich suchen, Teilung zurücknehmen. Der Abspielkopf
                    ist der Ort dafür — die Shot-Liste unten sieht ihn nicht.
                    Hintergrund: modules/shot_split.py, modules/shot_cuts.py */}
                <span className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    data-testid="split-shot-here"
                    disabled={splitBusy || !canSplitHere}
                    onClick={() => onSplitHere(currentShot)}
                    className={`border rounded-sm px-1.5 py-0.5 ${canSplitHere
                      ? "border-amber-400/60 text-amber-300 hover:bg-amber-400/10"
                      : "border-border text-muted-foreground/40 cursor-not-allowed"}`}
                    title={canSplitHere
                      ? `Shot ${currentShot.shot_id} bei ${time.toFixed(2)} s teilen — für einen Schnitt, den der Detektor übersehen hat. Die Teilung wird als Zeitpunkt gespeichert und übersteht jeden neuen Shot-Scan. Die Klassen der beiden Hälften sind danach leer (die alte galt für den ganzen Shot) und die alte Entscheidung wartet in der Prüfliste.`
                      : `Zu dicht an der Shot-Grenze — beide Hälften müssen mindestens ${MIN_SPLIT_PART_S} s lang sein.`}
                  >
                    ✂ hier teilen
                  </button>
                  {currentShot.split_start && (
                    <button
                      type="button"
                      data-testid="unsplit-shot"
                      disabled={splitBusy}
                      onClick={() => onUnsplit(currentShot)}
                      className="border border-border rounded-sm px-1.5 py-0.5 text-muted-foreground hover:text-amber-300 hover:border-amber-400/60"
                      title={`Die manuelle Teilung bei ${Number(currentShot.start_s).toFixed(2)} s zurücknehmen — dieser Shot verschmilzt wieder mit dem Vorgänger.`}
                    >
                      ↩ Teilung zurück
                    </button>
                  )}
                  {/* 🆕 2026-08-31 · Zusammenlegen. Sichtbar nur, wenn die
                      Anfangsgrenze vom Detektor kommt — bei einer
                      Handteilung ist »Teilung zurück« der richtige Weg,
                      und beide gleichzeitig zu speichern wuerde die Grenze
                      unloeschbar machen. */}
                  {!currentShot.split_start && (
                    <button
                      type="button"
                      data-testid="merge-prev-shot"
                      disabled={splitBusy}
                      onClick={() => onMergePrev(currentShot)}
                      className="border border-border rounded-sm px-1.5 py-0.5 text-muted-foreground hover:text-amber-300 hover:border-amber-400/60"
                      title={`Shot ${currentShot.shot_id} mit dem Vorgänger zusammenlegen — für einen Schnitt, den der Detektor erfunden hat. Gestrichen wird die Grenze bei ${Number(currentShot.start_s).toFixed(2)} s. Die Streichung wird als Zeitpunkt gespeichert und übersteht jeden neuen Shot-Scan.`}
                    >
                      ⇤ mit Vorgänger
                    </button>
                  )}
                  {currentShot.merged_inside && (
                    <button
                      type="button"
                      data-testid="unmerge-shot"
                      disabled={splitBusy}
                      onClick={() => onUnmerge(currentShot)}
                      className="border border-border rounded-sm px-1.5 py-0.5 text-muted-foreground hover:text-amber-300 hover:border-amber-400/60"
                      title={`Die gestrichene Grenze bei ${Number(currentShot.merged_inside[0]).toFixed(2)} s zurückholen — dieser Shot wird dort wieder geteilt.${currentShot.merged_inside.length > 1 ? ` Es stecken ${currentShot.merged_inside.length} Streichungen darin; die früheste kommt zuerst zurück.` : ""}`}
                    >
                      ↩ Zusammenlegen zurück
                    </button>
                  )}
                  <button
                    type="button"
                    data-testid="scan-cuts-here"
                    disabled={splitBusy}
                    onClick={() => onScanCuts(currentShot)}
                    className="border border-border rounded-sm px-1.5 py-0.5 text-muted-foreground hover:text-cyan-300 hover:border-cyan-400/60"
                    title={`Diesen Shot gründlich nach verborgenen Schnitten durchsuchen (Histogramm-Schwelle 0,05 statt 0,10 beim Shot-Scan). Findet mehr, dafür mit Fehlalarmen — gemessen 2 von 2 echten Schnitten bei 4 Fehlalarmen auf 60 s. Dauert etwa ${Math.max(5, Math.round((currentShot.end_s - currentShot.start_s) / 5))} s.${currentShot.deep_scanned ? ` Zuletzt gesucht: Schwelle ${currentShot.deep_scanned.threshold}.` : " Noch nicht gründlich durchsucht."}`}
                  >
                    🔎 gründlich{currentShot.deep_scanned ? " ✓" : ""}
                  </button>
                  {currentShot.cut_hints?.length > 0 && currentShot.cut_hints.map((h) => (
                    <button
                      key={`hint-${h.t}`}
                      type="button"
                      data-testid={`cut-hint-${currentShot.shot_id}-${h.t}`}
                      onClick={() => {
                        setT(Number(h.t), true);
                        toast.info(`Kandidat bei ${Number(h.t).toFixed(2)} s — ansehen, dann »hier teilen«`);
                      }}
                      className="border border-amber-400/50 bg-amber-400/10 text-amber-300 rounded-sm px-1.5 py-0.5 hover:bg-amber-400/20"
                      title={`Möglicher verborgener Schnitt bei ${Number(h.t).toFixed(2)} s (Histogramm-Schwelle ${h.threshold}). Ein Vorschlag, kein Befund — ansehen und selbst entscheiden. Klick springt hin.`}
                    >
                      ⚑ {Number(h.t).toFixed(1)} s
                    </button>
                  ))}
                </span>
                <span className="ml-auto flex items-center gap-1 shrink-0">
                  {[["wide", "W"], ["close", "C"], ["replay", "R"], ["ads", "Ads"], ["np", "NP"], ["wide_np", "W-NP"], ["drone", "Dr"], [null, currentShot.class_auto?.label ? `Auto (${currentShot.class_auto.label})` : "Auto"]].map(([lbl, txt]) => (
                    <button
                      key={`cs-${txt}`}
                      type="button"
                      data-testid={`current-shot-class-${lbl || "auto"}`}
                      onClick={async () => {
                        try {
                          await setShotClass(sequenceId, currentShot.shot_id, lbl);
                          await loadShots();
                          toast.success(lbl
                            ? `Shot ${currentShot.shot_id} → ${lbl} (Gold-Label)`
                            : `Shot ${currentShot.shot_id} → Auto-Klassifikation`);
                        } catch (e) {
                          toast.error(fehlerText(e) || "Override fehlgeschlagen");
                        }
                      }}
                      title={lbl
                        ? `Als ${lbl} markieren (manuelles Gold-Label) — ${KLASSEN_DEFINITION[lbl]}`
                        : "Override entfernen — Auto-Klassifikation gilt wieder"}
                      className={`border rounded-sm px-1.5 py-0.5 ${(lbl === null ? !currentShot.class_manual : currentShot.class_manual === lbl)
                        ? "border-cyan-400 text-cyan-200"
                        : "border-border text-muted-foreground hover:text-foreground"}`}
                    >
                      {txt}
                    </button>
                  ))}
                </span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 h-8 shrink-0">
            <Button data-testid="anchor-seek-plus-1f" size="sm" variant="ghost"
                    onClick={() => setT(time + 1 / fps, true)} className="font-mono text-[10px]">
              1f<ChevronRight className="w-3 h-3" />
            </Button>
            <Button data-testid="anchor-seek-plus-1s" size="sm" variant="ghost"
                    onClick={() => setT(time + 1, true)} className="font-mono text-[10px]">
              1s<ChevronRight className="w-3 h-3" />
            </Button>
            <Button data-testid="anchor-seek-plus-5s" size="sm" variant="ghost"
                    onClick={() => setT(time + 5, true)} className="font-mono text-[10px]">
              5s<ChevronRight className="w-3 h-3" />
            </Button>
            <Button data-testid="anchor-seek-next-anchor" size="sm" variant="ghost"
                    onClick={() => seekAnchor(1)}
                    title="Zum nächsten Anker springen"
                    className="font-mono text-[10px] text-cyan-300">
              ⚓<ChevronRight className="w-3 h-3" />
            </Button>
            <Button data-testid="anchor-seek-next-shot" size="sm" variant="ghost"
                    onClick={() => seekShot(1)}
                    title="Zum nächsten Shot springen (alle Einstellungen, auch ohne Anker)"
                    className="font-mono text-[10px] text-emerald-300">
              S<ChevronRight className="w-3 h-3" />
            </Button>
            {/* 🆕 2026-08-23 · Zeitanzeige. Vorher `w-16 text-right` mit
                nur mm:ss.ss — bei „10:11.23" braucht der Text 67 px, es
                waren 64 da, also wurde links abgeschnitten. Jetzt ohne
                feste Breite (`whitespace-nowrap`) und MIT der absoluten
                Sekundenzahl: intern hängt alles an absoluter Zeit
                (`at_time_s`, manuelle Teilungen, Schnitt-Hinweise), also
                ist das die Zahl, die man weitergibt. Klick kopiert sie. */}
            <button
              type="button"
              data-testid="anchor-collect-time"
              onClick={async () => {
                const s = time.toFixed(2);
                try {
                  await navigator.clipboard.writeText(s);
                  toast.success(`${s} s kopiert`);
                } catch {
                  toast.info(`Aktueller Zeitpunkt: ${s} s`);
                }
              }}
              title={`Aktueller Zeitpunkt — ${fmtTime(time)} entspricht ${time.toFixed(2)} s ab Videoanfang. Klick kopiert die Sekundenzahl; das ist der Wert, auf den Anker, Teilungen und Schnitt-Hinweise sich beziehen.`}
              className="font-mono text-[11px] tabular-nums text-cyan-300 whitespace-nowrap shrink-0 rounded-sm px-1 hover:bg-cyan-400/10"
            >
              {/* 2026-09-02 · Reihenfolge harmonisiert (Nutzerwunsch):
                  ueberall Sekunden zuerst, mm:ss dahinter — dieselbe
                  Konvention wie bothTimes/bothSpans in lib/videoTime
                  (Sekunden sind der kopierbare, massgebliche Wert). */}
              <span data-testid="anchor-collect-time-seconds">{time.toFixed(2)} s</span>
              <span className="text-cyan-300/60"> · </span>
              {fmtTime(time)}
            </button>
          </div>
        </div>
      </div>

      {/* ── Steuerung + Liste ── */}
      <div className="lg:col-span-2 space-y-4">
        {/* 🆕 Anker-Arbeitsliste: welche wide-Shots brauchen noch Klicks?
            Bewertet pro Shot den Selfcal-RMS statt der Einzel-Ampeln —
            einzelne Anker widersprechen sich erwartungsgemäß (Prinzip 6),
            erst der Bündelausgleich zählt. Klick springt in den Shot. */}
        <AnchorWorklist
          shots={shotList}
          anchors={anchors}
          selfcal={selfcal}
          kameraKonsens={kameraKonsens}
          selfcalAuto={selfcalAuto}
          onJump={(t) => setT(t, true)}
        />
        {/* 🆕 Shot-Selbstkalibrierung: EINE Stativ-Position pro Shot,
            Floating Pan/Tilt/FOV pro Anker — alle Klicks gemeinsam
            optimiert (Bündelausgleich). Ergebnis fließt als Pose-
            Kandidat in den 3D-Flugkurven-Fit. */}
        <div
          data-testid="shot-selfcal-panel"
          className="border border-cyan-500/40 bg-cyan-500/5 rounded-sm p-3 space-y-2"
        >
          <div className="flex items-center justify-between gap-2">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-cyan-300">
                Shot-Selbstkalibrierung
              </div>
              <div className="font-mono text-[10px] text-muted-foreground">
                Bündelausgleich: 1 Kameraposition/Shot · min. 3 joint-Anker
                · läuft nach jedem Anker automatisch
              </div>
              {selfcalAuto?.status === "running" && (
                <div
                  data-testid="shot-selfcal-auto-running"
                  className="font-mono text-[10px] text-cyan-300 animate-pulse"
                >
                  ⟳ Auto-Selbstkalibrierung läuft…
                </div>
              )}
            </div>
            <Button
              data-testid="shot-selfcal-btn"
              size="sm" variant="outline"
              disabled={selfcalBusy}
              onClick={runSelfcal}
              className="font-mono text-[10px] uppercase tracking-widest whitespace-nowrap"
              title="Optimiert alle Anker-Klicks jedes Shots GEMEINSAM (Stativ-Position konstant, Pan/Tilt/FOV floaten) — die gelöste Kamera fließt als Pose-Kandidat in 3D-Flugkurven"
            >
              <Crosshair className="w-3.5 h-3.5 mr-1.5" />
              {selfcalBusy ? "rechnet…" : "Selbstkalibrieren"}
            </Button>
          </div>
          {selfcal && Object.keys(selfcal).length > 0 && (
            <div
              data-testid="shot-selfcal-result"
              className="space-y-1 pt-1 border-t border-cyan-500/20"
            >
              {Object.entries(selfcal)
                .sort(([a], [b]) => Number(a) - Number(b))
                .map(([sid, e]) => {
                  const fovs = (e.anchors || [])
                    .filter((x) => !x.outlier && x.fov_deg != null)
                    .map((x) => x.fov_deg);
                  const nOut = (e.anchors || []).filter((x) => x.outlier).length;
                  const rmsIn = e.rms_inlier_px ?? e.rms_px;
                  const nIn = e.n_inliers
                    ?? (e.anchors || []).filter((x) => !x.outlier).length;
                  return (
                    <div
                      key={sid}
                      data-testid={`shot-selfcal-shot-${sid}`}
                      className="font-mono text-[10.5px] flex flex-wrap items-baseline gap-x-2"
                    >
                      <span className="text-cyan-300 font-bold">Shot {sid}</span>
                      <span>{nIn}/{e.n_anchors} Inlier</span>
                      <span
                        title={`Inlier-RMS (Klick-Residuen der ${nIn} konsistenten Anker)${e.rms_px != null ? ` · RMS inkl. Outlier: ${e.rms_px} px` : ""}`}
                        className={
                        rmsIn <= 10 ? "text-emerald-300"
                        : rmsIn <= 30 ? "text-amber-300" : "text-rose-300"
                      }>
                        RMS {rmsIn} px
                      </span>
                      <span className="text-muted-foreground">
                        Kamera ({(e.camera_centre_world || []).join(", ")}) m
                      </span>
                      {fovs.length > 0 && (
                        <span className="text-muted-foreground">
                          FOV {Math.min(...fovs).toFixed(0)}–{Math.max(...fovs).toFixed(0)}°
                        </span>
                      )}
                      {e.n_recovered > 0 && (
                        <span
                          className="text-cyan-300"
                          title="Anker, die im ersten Bündelausgleich Outlier waren und über die Nachbar-Pose zurückgeholt wurden"
                        >
                          {e.n_recovered} recovered
                        </span>
                      )}
                      {nOut > 0 && (
                        <span className="text-rose-300">{nOut} Outlier</span>
                      )}
                    </div>
                  );
                })}
            </div>
          )}
          {selfcalReasons && (
            <div
              data-testid="shot-selfcal-reasons"
              className="font-mono text-[10px] text-amber-300 leading-snug"
            >
              {selfcalReasons.map((r, i) => <div key={i}>{r}</div>)}
            </div>
          )}
          {/* 🆕 2026-08 · FOV-Abgleich: Override weicht stark vom
              Selfcal-Median ab → Ein-Klick-Übernahme. */}
          {fovHint?.mismatch && (
            <div
              data-testid="selfcal-fov-warning"
              className="border border-amber-500/40 bg-amber-500/10 rounded-sm p-2 space-y-1.5"
            >
              <div className="font-mono text-[10px] text-amber-300 leading-snug">
                ⚠ FOV-Override {fovHint.override_deg}° weicht {fovHint.delta_deg}° von der
                Selfcal-Schätzung ab ({fovHint.selfcal_median_deg}°, Median über{" "}
                {fovHint.n_anchor_fovs} Anker-FOVs). Ein falscher Override macht
                Einzel-Anker-Solves geometrisch instabil.
              </div>
              <Button
                data-testid="selfcal-fov-accept"
                size="sm" variant="outline"
                disabled={fovAccepting || selfcalBusy}
                onClick={acceptSelfcalFov}
                className="font-mono text-[10px] uppercase tracking-widest border-amber-500/50 text-amber-200 hover:bg-amber-500/20"
              >
                {fovAccepting ? "übernehme…" : `${fovHint.selfcal_median_deg}° übernehmen & neu kalibrieren`}
              </Button>
            </div>
          )}
        </div>

        <div>
          <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Preset / Linien-Kombination
          </Label>
          <Select value={presetId} onValueChange={(v) => {
            setPresetId(v); setPoints([]); setSegments([]); setDrag(null);
            setP3pPoints([]); setP3pLines([]); setP3pLineDrag(null); setReprojCheck(null);
            // Radix-Typeahead-Leak: fokussierter Trigger frisst sonst die
            // Hotkeys (S/G/B/M) und springt ungewollt zu anderen Presets.
            setTimeout(() => document.activeElement?.blur?.(), 50);
          }}>
            <SelectTrigger data-testid="anchor-collect-preset" className="mt-1.5 bg-secondary border-border">
              <SelectValue placeholder="Preset wählen…" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="p3p" data-testid="anchor-collect-preset-p3p">
                <Crosshair className="w-3 h-3 inline mr-1.5 text-emerald-300" />
                3-Punkt-Initialisierung · P3P
              </SelectItem>
              <SelectItem value="free_draw" data-testid="anchor-collect-preset-free-draw">
                <Pencil className="w-3 h-3 inline mr-1.5 text-cyan-300" />
                Freies Zeichnen · Click-and-Drag
              </SelectItem>
              <div className="px-2 py-1 mt-1 border-t border-border font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
                Flächen-Presets
              </div>
              {areaPresets.map((p) => (
                <SelectItem key={p.id} value={p.id} data-testid={`anchor-collect-preset-${p.id}`}>
                  {p.labelDe}
                </SelectItem>
              ))}
              {combos.length > 0 && (
                <div className="px-2 py-1 mt-1 border-t border-border font-mono text-[9px] uppercase tracking-widest text-cyan-300">
                  Linien-Kombinationen
                </div>
              )}
              {combos.map((c) => (
                <SelectItem key={c.id} value={`combo:${c.id}`} data-testid={`anchor-collect-combo-${c.id}`}>
                  {c.label_de} · {c.n_clicks} Klicks
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {!hasAnyH && (
            <div
              data-testid="anchor-collect-no-h-hint"
              className="mt-1.5 font-mono text-[10px] text-emerald-300/80"
            >
              Noch kein Anker mit Homographie — »Freies Zeichnen«: 2–3
              sichtbare Linien nachziehen (oder P3P). Alle Modi sind
              freigeschaltet.
            </div>
          )}
        </div>

        {requiresSide && (
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-widest text-amber-400">
              Feld-Seite · Pflichtfeld
            </Label>
            <Select value={side} onValueChange={setSide}>
              <SelectTrigger data-testid="anchor-collect-side" className="mt-1.5 bg-secondary border-border">
                <SelectValue placeholder="Links oder Rechts…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="left" data-testid="anchor-collect-side-left">Linke Hälfte</SelectItem>
                <SelectItem value="right" data-testid="anchor-collect-side-right">Rechte Hälfte</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {/* 🆕 P3P · Landmarken-Palette + FOV-Kalibrier-Check.
            Bleibt hier, anders als die Linienklassen: 408 px hoch und
            durch Auto-Advance kein Pro-Klick-Bedienelement. */}
        {isP3P && (
          <div className="space-y-2">
            <Label className="font-mono text-[10px] uppercase tracking-widest text-emerald-300">
              Landmarke wählen → im Bild anklicken (3 Punkte)
            </Label>
            <P3PLandmarkChips
              landmarks={p3pLandmarks}
              active={p3pActive}
              usedIds={p3pPoints.map((p) => p.landmark_id)}
              onSelect={setP3pActive}
            />
            {/* 🆕 FOV-Kalibrier-Check: effektives FOV + Freitext-Korrektur */}
            <div className="flex items-center gap-2 flex-wrap">
              {/* 🆕 FOV-Scope: Shot (nur aktueller Shot) vs. ganze Sequenz */}
              <div className="flex rounded-sm border border-border overflow-hidden shrink-0">
                {["shot", "seq"].map((sc) => (
                  <button
                    key={sc}
                    data-testid={`fov-scope-${sc}`}
                    onClick={() => setFovScope(sc)}
                    className={`px-2 py-1.5 font-mono text-[10px] uppercase tracking-widest transition-colors ${
                      fovScope === sc
                        ? "bg-cyan-400/20 text-cyan-300"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                    title={sc === "shot"
                      ? "FOV gilt nur für den Shot am aktuellen Zeitpunkt (höchste Stufe der Hierarchie)"
                      : "FOV-Override für die GANZE Sequenz (Stufe unter Shot-FOV)"}
                  >
                    {sc === "shot" ? "Shot" : "Sequenz"}
                  </button>
                ))}
              </div>
              <input
                data-testid="p3p-fov-input"
                type="number"
                min="20"
                max="160"
                step="0.1"
                value={fovInput}
                onChange={(e) => setFovInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") applyFov(); }}
                placeholder="FOV °"
                className="w-24 bg-secondary border border-border rounded-sm px-2 py-1.5 text-[12px] font-mono focus:border-primary focus:outline-none"
              />
              <Button
                data-testid="p3p-fov-apply"
                size="sm"
                variant="outline"
                onClick={applyFov}
                title="Leeres Feld = manuellen Override löschen (zurück zu Profil/EXIF)"
                className="font-mono text-[10px] uppercase tracking-widest"
              >
                FOV setzen
              </Button>
              <Button
                data-testid="p3p-fov-reset"
                size="sm"
                variant="ghost"
                onClick={resetFov}
                title="Verpatzte Selbstkalibrierung + Override löschen → zurück zu EXIF/Profil-Standard"
                className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-rose-300"
              >
                <RotateCcw className="w-3 h-3 mr-1" /> Reset
              </Button>
              <span
                data-testid="p3p-fov-current"
                className="font-mono text-[10px] text-muted-foreground"
              >
                aktiv: {p3pCamera ? `${p3pCamera.fov_deg}°` : "…"} ·{" "}
                {fovSourceLabel(p3pCamera?.source)}
              </span>
            </div>
            {/* 🆕 Shot-FOV-Status am aktuellen Zeitpunkt */}
            {currentShot ? (
              <div
                data-testid="p3p-shot-fov-status"
                className="flex items-center gap-2 flex-wrap font-mono text-[10px] text-muted-foreground"
              >
                <span className="text-cyan-300">Shot {currentShot.shot_id}</span>
                <span>
                  {Number(currentShot.start_s).toFixed(2)}–{Number(currentShot.end_s).toFixed(2)} s ·{" "}
                  {currentShot.fov_deg != null
                    ? `${currentShot.fov_deg}° (${fovSourceLabel(currentShot.fov_source)}${
                        currentShot.fov_source === "shot_curve"
                          ? ` · ${currentShot.n_curve_samples} Stützstellen` : ""})`
                    : "kein Shot-FOV → Sequenz-Kaskade"}
                </span>
                {currentShot.suggestion_deg != null && currentShot.fov_deg == null && (
                  <button
                    data-testid="shot-fov-suggestion-btn"
                    onClick={() => acceptShotFovSuggestion(currentShot)}
                    className="border border-amber-400/50 text-amber-300 rounded-sm px-1.5 py-0.5 hover:bg-amber-400/10 transition-colors"
                    title={`Median aus ${currentShot.n_suggestion_samples} exakt bestimmten DLT-Schätzungen dieses Shots — nur Vorschlag, nie automatisch aktiv`}
                  >
                    Vorschlag {currentShot.suggestion_deg}° übernehmen
                  </button>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2 font-mono text-[10px] text-muted-foreground">
                <span>Keine Shots erkannt.</span>
                <button
                  data-testid="detect-shots-btn"
                  onClick={onDetectShots}
                  className="border border-border rounded-sm px-1.5 py-0.5 hover:text-foreground transition-colors"
                  title="PySceneDetect-Scan: zerlegt das Video in Shots (Kameraeinstellungen) — harte Grenzen für FOV & H-Priors"
                >
                  Shots scannen
                </button>
              </div>
            )}
            {/* 🆕 P3P-Ambiguität: Lösungs-Umschalter (>1 plausible Pose) */}
            {p3pSolutions.length > 1 && (
              <div className="flex items-center gap-2 flex-wrap" data-testid="p3p-solutions-toggle">
                <span className="font-mono text-[10px] uppercase tracking-widest text-amber-300">
                  {p3pSolutions.length} mögliche Posen — Grid vergleichen:
                </span>
                {p3pSolutions.map((s, i) => (
                  <button
                    key={i}
                    data-testid={`p3p-solution-${i}`}
                    onClick={() => setP3pSolIdx(i)}
                    className={`border rounded-sm px-2 py-1 font-mono text-[10px] transition-colors ${
                      p3pSolIdx === i
                        ? "border-emerald-400 bg-emerald-400/20 text-emerald-300"
                        : "border-border text-muted-foreground hover:text-foreground"
                    }`}
                    title={`Kamera bei (${s.camera_pos_m?.join(", ")}) m${s.orientation_ok ? "" : " · Orientierung verdächtig"}`}
                  >
                    Lösung {i + 1} · {s.camera_height_m} m{s.orientation_ok ? "" : " ⚠"}
                  </button>
                ))}
              </div>
            )}
            <div
              data-testid="p3p-help"
              className="border-l-4 border-emerald-400 bg-emerald-400/10 rounded-sm p-2.5 text-[11px] leading-relaxed text-foreground/85"
            >
              3 Boden-Punkte reichen für die initiale Kamerapose (P3P) —{" "}
              <span className="text-emerald-300">ein 4. Punkt macht das
              Grid FOV-UNABHÄNGIG (DLT)</span> und kalibriert das FOV der
              Kamera automatisch. Punkte wählen, die ein möglichst großes
              Dreieck aufspannen. Rechtsklick/Backspace löscht den
              letzten Klick; gesetzte Punkte lassen sich{" "}
              <span className="text-emerald-300">per Drag nachziehen</span>.
              {" "}Sobald das Grid steht:{" "}
              <span className="text-cyan-300">Grid-Linien direkt greifen
              und auf die echten Rasenlinien ziehen</span> (Mitte =
              verschieben, Endgriff = rotieren, Rechtsklick auf Linie =
              Constraint löschen) — der Punkt+Linien-Solver zieht das
              gesamte Feld nach. Sitzt es auf dem Rasen → speichern.
            </div>
            {p3pLines.length > 0 && (
              <div
                data-testid="p3p-line-constraints-status"
                className="font-mono text-[10px] text-cyan-300"
              >
                {p3pLines.length} Linien-Constraint{p3pLines.length > 1 ? "s" : ""} aktiv
                ({p3pLines.map((s) => s.cls).join(", ")}) — zählt zusätzlich
                als Segment-Trainingslabel.
              </div>
            )}
          </div>
        )}

        {/* 🆕 Freies Zeichnen · Bedien-Hinweise.
            Die Klassen-Palette selbst sitzt seit 23.08.2026 in der
            Werkzeugleiste am Bild (data-testid="anchor-tool-rail") —
            hier bleibt nur, was man liest statt anklickt. */}
        {isFreeDraw && (
          <div className="space-y-2">
            <div
              data-testid="free-draw-help"
              className="border-l-4 border-cyan-400 bg-cyan-400/10 rounded-sm p-2.5 text-[11px] leading-relaxed text-foreground/85"
            >
              Click-and-Drag zeichnet ein Segment der aktiven Klasse ·
              1 Segment pro Klasse (neu ersetzt alt) · Rechtsklick oder
              Backspace löscht das letzte Segment · Esc bricht den Drag ab.
              Beim Frame-Sprung werden Segmente zu passiven{" "}
              <span className="text-cyan-300">Geistern</span>: sie zählen
              NICHT für Solver &amp; Speichern — erst Antippen/Nachrücken
              aktiviert sie wieder. Zwei Sorten, im Bild unterscheidbar:{" "}
              <span className="text-cyan-300">lang gestrichelt mit gefüllten
              Griffen und ⟲</span> = deine eigene Linie, über die Kamerapose
              mitgezogen ·{" "}
              <span className="text-violet-300">punktiert mit hohlen Griffen
              und ?</span> = Vorschlag des Modells, ungeprüft. Ausgewanderte
              Linien einfach ignorieren.
            </div>
            {scanErgebnis && (
              <div
                data-testid="shot-scan-ergebnis"
                className="border-l-4 border-violet-400 bg-violet-400/10 rounded-sm p-2.5 text-[11px] leading-relaxed"
              >
                <div className="font-mono text-[10px] uppercase tracking-widest text-violet-300 mb-1">
                  Shot {scanErgebnis.shot_id} · {scanErgebnis.bilanz?.text}
                </div>
                <div className="space-y-0.5">
                  {(scanErgebnis.kandidaten || []).map((k) => {
                    const farbe = k.stufe === 0 ? "text-emerald-300"
                      : k.stufe === 1 ? "text-amber-300"
                        : k.stufe === 2 ? "text-rose-300" : "text-muted-foreground";
                    const px = k.vergleich?.max_px;
                    const urteil = k.stufe === 0 ? `${Number(px).toFixed(1)} px — belastbar`
                      : k.stufe === 1 ? `${k.segments?.length || 0} Linien — nicht beurteilbar`
                        : k.stufe === 2 ? `${px != null ? Number(px).toFixed(0) : "?"} px daneben`
                          : (k.grund || "keine Homographie");
                    return (
                      <button
                        key={k.t_s}
                        data-testid={`shot-scan-kandidat-${k.t_s}`}
                        onClick={() => setT(k.t_s, true)}
                        className="w-full text-left font-mono text-[10px] hover:bg-violet-400/10 rounded-sm px-1 py-0.5 flex items-center gap-2"
                        title="Springt zu diesem Frame — dort dann den Vorschlag-Knopf drücken"
                      >
                        <span className="tabular-nums text-muted-foreground w-14 shrink-0">
                          {Number(k.t_s).toFixed(2)}s
                        </span>
                        <span className={`${farbe} truncate`}>{urteil}</span>
                      </button>
                    );
                  })}
                </div>
                <div className="font-mono text-[9px] text-muted-foreground mt-1.5">
                  Klick springt zum Frame. Gespeichert wird nichts — die
                  Kandidaten sind Vorschläge, kein Anker.
                </div>
              </div>
            )}
            {activeSegments.length >= 1 && !(hasVertical && hasSideline) && (
              <div
                data-testid="free-draw-seg-only-hint"
                className="border-l-4 border-amber-400 bg-amber-400/10 rounded-sm p-2.5 text-[11px] leading-relaxed text-amber-200"
              >
                Nur parallele Linien — für die Homographie (Pfad 2) mindestens
                1 Quer-Linie (Goal/Back) UND 1 Seitenlinie zeichnen. Die
                Segment-Labels zählen trotzdem fürs Training (seg_only).
              </div>
            )}
          </div>
        )}

        {guidance && !combo && (
          <div className="border-l-4 border-amber-400 bg-amber-400/10 rounded-sm p-2.5 text-[12px] leading-relaxed">
            <div className="font-display font-bold text-amber-200 text-[12px]">{guidance.headline}</div>
            <div className="mt-1 text-foreground/85">{guidance.body}</div>
          </div>
        )}

        {/* 🆕 v1.2 · Combo-Guidance: aktive Linie + Klick-Hint */}
        {combo && points.length < expectedClicks && (
          <div
            data-testid="anchor-collect-combo-hint"
            className="border-l-4 rounded-sm p-2.5 text-[12px] leading-relaxed"
            style={{
              borderColor: LINE_COLORS[Math.floor(points.length / 2) % LINE_COLORS.length],
              backgroundColor: `${LINE_COLORS[Math.floor(points.length / 2) % LINE_COLORS.length]}18`,
            }}
          >
            <div className="font-display font-bold text-[12px]"
                 style={{ color: LINE_COLORS[Math.floor(points.length / 2) % LINE_COLORS.length] }}>
              Linie {Math.floor(points.length / 2) + 1}/{combo.lines.length} · Punkt {(points.length % 2) + 1}/2
            </div>
            <div className="mt-1 text-foreground/85">
              {combo.lines[Math.floor(points.length / 2)]?.hint}
            </div>
          </div>
        )}

        {/* v1.3 · Echtzeit-Reprojektions-Fehler — rein informativ,
            blockiert den Speichern-Button NICHT mehr (Dual-Pfad).
            🆕 P3P: 3 Punkte werden IMMER exakt gelöst (0 px trivial) —
            Text-Warnung statt irreführender px-Zahl, Grid ist das Gate. */}
        {((combo && points.length === expectedClicks) || ((isFreeDraw || isP3P) && reprojCheck)) && (
          <div
            data-testid="anchor-collect-reproj-badge"
            className={`border rounded-sm p-2.5 font-mono text-[12px] ${
              reprojCheck?.error
                ? "border-amber-500/50 bg-amber-500/10 text-amber-300"
                : isP3P
                  ? (p3pWarnSel
                      ? "border-amber-500/50 bg-amber-500/10 text-amber-300"
                      : mixedLevel === "rot"
                        ? "border-rose-500/60 bg-rose-500/10 text-rose-300"
                        : mixedLevel === "gelb"
                          ? "border-amber-500/50 bg-amber-500/10 text-amber-300"
                          : "border-emerald-500/50 bg-emerald-500/10 text-emerald-300")
                  : reprojErr == null
                    ? "border-border text-muted-foreground"
                    : reprojErr > 15
                      ? "border-rose-500/60 bg-rose-500/10 text-rose-300"
                      : reprojErr >= 10
                        ? "border-amber-500/50 bg-amber-500/10 text-amber-300"
                        : "border-emerald-500/50 bg-emerald-500/10 text-emerald-300"
            }`}
          >
            {reprojCheck?.error
              ? `⚠ ${reprojCheck.error}${isP3P ? "" : ` — Klicks werden trotzdem als Segment-Label gespeichert (ohne Homographie)${fovInfo}`}`
              : isP3P
                ? (p3pWarnSel
                    ? `⚠ ${p3pWarnSel}`
                    : reprojCheck?.mixed
                      ? `${mixedLevel === "rot" ? "✖" : mixedLevel === "gelb" ? "⚠" : "✓"} Punkt+Linien-DLT gelöst · ${reprojCheck.mixed.n_points} Punkte + ${reprojCheck.mixed.n_lines} Linien · ${reprojCheck.reproj_error_px} px${
                          mixedLevel === "rot"
                            ? " — Klicks & Linien widersprechen sich STARK: Punkte oder Linien nachziehen"
                            : mixedLevel === "gelb"
                              ? " — leichter Widerspruch zwischen Klicks & Linien, kurz prüfen"
                              : ""
                        } · FOV-FREI${reprojCheck.mixed.fov_estimate_deg ? ` · FOV selbstkalibriert ≈ ${reprojCheck.mixed.fov_estimate_deg}°` : " · FOV-Schätzung n. möglich (zu wenig Perspektive)"} · GRID VISUELL PRÜFEN`
                      : reprojCheck?.dlt
                      ? `✓ DLT exakt gelöst · ${reprojCheck.dlt.n_points} Punkte · FOV-FREI${reprojCheck.dlt.fov_estimate_deg ? ` · FOV selbstkalibriert ≈ ${reprojCheck.dlt.fov_estimate_deg}°` : " · FOV-Schätzung n. möglich (zu wenig Perspektive)"} · GRID VISUELL PRÜFEN: sitzt es auf dem Rasen?`
                      : `✓ P3P-Pose gelöst · Kamera ≈ ${p3pHeightSel ?? "?"} m (rechnerisch, FOV-abhängig — kein Messwert) · GRID VISUELL PRÜFEN — Tipp: 4. Punkt klicken → FOV-unabhängig (DLT)`)
                : reprojErr == null
                  ? "Reprojektions-Fehler wird berechnet…"
                  : reprojErr > 15
                    ? `⚠ ${reprojErr} px — Klicks prüfen · Segment-Label wird exportiert, Homographie-Overlay übersprungen${fovInfo}`
                    : reprojErr >= 10
                      ? `${reprojErr} px — Segment-Label wird exportiert · Homographie-Overlay übersprungen (Gate 10 px)${fovInfo}`
                      : `✓ ${reprojErr} px — Segment- + Homographie-Label exportierbar${fovInfo}`}
          </div>
        )}

        {/* 🆕 Nachtrag 6 (F4): in der Klickliste die Ampel des gerade
            gespeicherten Ankers, mit „Diesen Anker löschen". */}
        {klickliste && klickAmpel && klickAmpel.nr === klickliste.nr && (
          <div
            data-testid="klickliste-ampel"
            data-stufe={klickAmpel.stufe}
            className={`border rounded-sm p-2 font-mono text-[11px] space-y-2 ${
              klickAmpel.stufe === "gruen"
                ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-300"
                : klickAmpel.stufe === "gelb"
                  ? "border-amber-500/50 bg-amber-500/10 text-amber-300"
                  : klickAmpel.stufe === "unpruefbar"
                    ? "border-orange-500/60 bg-orange-500/10 text-orange-300"
                    : klickAmpel.stufe === "ohne"
                      ? "border-border text-muted-foreground"
                      : "border-rose-500/60 bg-rose-500/10 text-rose-300"
            }`}
          >
            <div>Gespeichert · {klickAmpel.text}</div>
            {klickAmpel.anchorId && (
              <Button
                data-testid="klickliste-ampel-loeschen" variant="outline" size="sm"
                className="font-mono text-[10px]"
                onClick={async () => {
                  await onDelete(klickAmpel.anchorId);
                  setKlickAmpel(null);
                }}
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Diesen Anker löschen
              </Button>
            )}
          </div>
        )}

        {/* 🆕 Anker-Qualitäts-Ampel: metrische 3D-Fähigkeit der gelösten
            H (Pose-Roundtrip) — grün = 3D-Flugkurven-tauglich.
            Bei gelb/rot: Handlungshinweise + Direkt-Button zur Shot-Selbstkal. */}
        {reprojCheck && !reprojCheck.error && reprojCheck.quality && (() => {
          const q = reprojCheck.quality;
          const isGreen = q.grade === "gruen";
          const isYellow = q.grade === "gelb";
          const nC = (isP3P ? (p3pPoints?.length || 0) + (p3pLines?.length || 0) : 0);
          const hints = qualityHints(q, { nConstraints: nC });
          return (
            <div
              data-testid="anchor-quality-ampel"
              data-grade={q.grade}
              className={`border rounded-sm p-2 font-mono text-[11px] space-y-2 ${
                isGreen
                  ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-300"
                  : isYellow
                    ? "border-amber-500/50 bg-amber-500/10 text-amber-300"
                    : "border-rose-500/60 bg-rose-500/10 text-rose-300"
              }`}
            >
              <div className="flex items-center gap-2">
                <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${
                  isGreen ? "bg-emerald-400"
                  : isYellow ? "bg-amber-400" : "bg-rose-500"
                }`} />
                <span>
                  3D-Ampel: {q.label_de}
                  {q.pose_consistency_px != null
                    ? ` · Pose-Roundtrip ${q.pose_consistency_px} px (Gate ${q.gate_green_px} px)`
                    : " · keine physikalische Pose ableitbar"}
                  {q.fov_deg != null
                    ? ` · FOV ${q.fov_deg}° (${fovSourceLabel(q.fov_source)})`
                    : ""}
                </span>
              </div>
              {hints.length > 0 && (
                <ul
                  data-testid="anchor-quality-hints"
                  className="text-[10.5px] leading-snug list-disc pl-5 space-y-0.5 opacity-95"
                >
                  {hints.map((h, i) => (
                    <li key={i} data-testid={`anchor-quality-hint-${i}`}>{h}</li>
                  ))}
                </ul>
              )}
              {!isGreen && (
                <div className="flex items-center gap-2 pt-1 border-t border-current/20">
                  <Button
                    data-testid="anchor-quality-selfcal-btn"
                    size="sm"
                    variant="outline"
                    disabled={selfcalBusy}
                    onClick={runSelfcal}
                    className="font-mono text-[10px] uppercase tracking-widest h-7 px-2 border-current/50 hover:bg-current/10"
                    title="Löst alle Anker dieses Shots gemeinsam (Bündelausgleich). Hebt gelbe Anker oft auf grün — braucht min. 3 joint-Anker im Shot."
                  >
                    <Crosshair className="w-3 h-3 mr-1.5" />
                    {selfcalBusy ? "rechnet…" : "🔧 Shot Selbstkalibrieren"}
                  </Button>
                  <span className="text-[10px] opacity-70">
                    hebt gelb → grün, wenn ≥ 3 Anker im Shot vorhanden sind
                  </span>
                </div>
              )}
            </div>
          );
        })()}

        {/* 🆕 Anti-„0.0 px"-Falle: exakt bestimmte Systeme haben KEINE
            Fehler-Kontrolle — Klick-/Maßfehler verbiegen die H unsichtbar */}
        {isP3P && p3pExact && (
          <div
            data-testid="p3p-exact-hint"
            className="border-l-4 border-amber-400 bg-amber-400/10 rounded-sm p-2.5 text-[11px] leading-relaxed text-amber-200"
          >
            Exakt bestimmtes System ({p3pPoints.length} Punkte
            {p3pLines.length ? ` + ${p3pLines.length} Linie${p3pLines.length > 1 ? "n" : ""}` : ""}):
            Die Lösung geht IMMER exakt durch deine Klicks — 0 px Fehler ist
            hier bedeutungslos. <span className="font-bold">+1 Punkt oder
            Linie hinzufügen</span>, erst dann zeigt der Fehler echte
            Widersprüche (Klickfehler, falsche Feldmaße).
          </div>
        )}
        {isP3P && p3pTopHeavy && (
          <div
            data-testid="p3p-coverage-hint"
            className="border-l-4 border-amber-400 bg-amber-400/10 rounded-sm p-2.5 text-[11px] leading-relaxed text-amber-200"
          >
            Alle Constraints liegen in der oberen Bildhälfte — der untere
            Bereich ist reine Extrapolation (kleine Fehler oben kippen die
            untere Sideline). <span className="font-bold">Untere Sideline
            direkt als Linie greifen und nachziehen</span> — ihre Ecken
            müssen dafür nicht sichtbar sein.
          </div>
        )}

        {/* 🆕 Feldmaß-Selbstkalibrierung: W/E/B aus überbestimmten
            Klicks schätzen — Turnierfelder sind oft NICHT regelkonform
            gesteckt und der Admin kennt die echten Maße nicht. */}
        {isP3P && (
          <div className="space-y-2">
            <Button
              data-testid="field-fit-btn"
              size="sm" variant="outline" className="w-full"
              disabled={fieldFitBusy || p3pConstraintCount < fitMinConstraints
                || !reprojCheck || !!reprojCheck?.error}
              onClick={() => runFieldFit(false, false)}
            >
              <Ruler className="w-3.5 h-3.5 mr-1.5" />
              {fieldFitBusy
                ? "Maß-Fit läuft…"
                : p3pConstraintCount < fitMinConstraints
                  ? `Feldmaße schätzen (${p3pConstraintCount}/${fitMinConstraints} Constraints)`
                  : "Feldmaße aus Klicks schätzen"}
            </Button>
            {fittableAnchors >= 1 && (
              <Button
                data-testid="field-fit-all-btn"
                size="sm" variant="outline" className="w-full"
                disabled={fieldFitBusy}
                onClick={() => runFieldFit(false, true)}
              >
                <Ruler className="w-3.5 h-3.5 mr-1.5" />
                Feldmaße aus ALLEN Ankern schätzen ({fittableAnchors}
                {p3pConstraintCount > 0 ? " + aktuelle Klicks" : ""})
              </Button>
            )}
            <div
              data-testid="field-fit-hint"
              className="text-[10px] text-muted-foreground leading-snug"
            >
              Punkte + gezogene Grid-Linien zählen zusammen
              {fovTrusted
                ? ` — dank bekanntem FOV reichen ${fitMinConstraints}.`
                : "."}{" "}
              Die letzte Constraint sollte NICHT durch schon geklickte
              Punkte laufen (bringt sonst kaum neue Information) — z. B.
              die gegenüberliegende Sideline ziehen.
            </div>
            {fieldFit && (
              <div
                data-testid="field-fit-result"
                className="border border-cyan-500/40 bg-cyan-500/5 rounded-sm p-2.5 text-[11px] space-y-1.5"
              >
                <div className="font-display font-bold text-[12px] text-cyan-300">
                  Geschätzte Feldmaße (Länge {sequence?.game_config?.field_length_m ?? "?"} m bleibt Referenz)
                  {fieldFit.n_frames > 1 ? ` · aus ${fieldFit.n_frames} Frames` : ""}
                </div>
                {(fieldFit.dropped_frames || []).length > 0 && (
                  <div
                    data-testid="field-fit-dropped"
                    className="text-[10px] text-amber-300"
                  >
                    {fieldFit.dropped_frames.length} Anker übersprungen:{" "}
                    {fieldFit.dropped_frames.map((d) => d.frame).join(", ")}
                  </div>
                )}
                {[
                  ["Feldbreite", "field_width_m"],
                  ["Endzonen-Tiefe", "endzone_depth_m"],
                  ["Brick-Distanz", "brick_distance_m"],
                ].map(([label, key]) => (
                  <div key={key} className="flex justify-between font-mono text-[10.5px]">
                    <span className="text-muted-foreground">{label}</span>
                    <span>
                      {fieldFit.initial[key]} m → <span className="text-cyan-300 font-bold">{fieldFit.fitted[key]} m</span>
                      {" "}({fieldFit.changed_pct[key] > 0 ? "+" : ""}{fieldFit.changed_pct[key]} %)
                    </span>
                  </div>
                ))}
                {fieldFit.width_identifiable === false ? (
                  <div
                    data-testid="field-fit-width-hint"
                    className="border-l-2 border-amber-400 bg-amber-400/10 rounded-sm p-1.5 text-[10px] leading-snug text-amber-200"
                  >
                    {fieldFit.fov_mode === "per_frame"
                      ? "Feldbreite ist bei variablem Kamera-Zoom (Broadcast) nicht bestimmbar — der Zoom absorbiert die Breiten-Skala exakt. Sie bleibt beim konfigurierten Wert; Endzonen-Tiefe + Brick sind davon unabhängig geschätzt."
                      : "Feldbreite ist ohne verlässliches Kamera-FOV aus einem Einzelbild nicht bestimmbar (projektive Skalenfreiheit) — sie bleibt beim konfigurierten Wert. FOV-Profil, EXIF oder manuelles FOV setzen macht sie schätzbar. Endzonen-Tiefe + Brick sind davon unabhängig geschätzt."}
                  </div>
                ) : (
                  <div
                    data-testid="field-fit-width-hint"
                    className="text-[10px] text-muted-foreground"
                  >
                    Feldbreite via Kamera-FOV {fieldFit.fov_used_deg}° ({fieldFit.fov_source})
                  </div>
                )}
                <div className="flex justify-between font-mono text-[10.5px]">
                  <span className="text-muted-foreground">Restfehler</span>
                  <span>
                    {fieldFit.reproj_before_px} px → <span className={fieldFit.reproj_after_px < fieldFit.reproj_before_px ? "text-emerald-300 font-bold" : "text-amber-300 font-bold"}>{fieldFit.reproj_after_px} px</span>
                  </span>
                </div>
                <div className="flex gap-2 pt-1">
                  <Button
                    data-testid="field-fit-apply" size="sm" className="flex-1"
                    disabled={fieldFitBusy}
                    onClick={() => runFieldFit(true)}
                  >
                    <Check className="w-3.5 h-3.5 mr-1.5" /> Übernehmen
                  </Button>
                  <Button
                    data-testid="field-fit-dismiss" size="sm" variant="ghost"
                    className="flex-1" onClick={() => setFieldFit(null)}
                  >
                    Verwerfen
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* 🆕 2026-08 · Anker-Feld-Abdeckung: projizierte Klicks aller
            Anker auf dem Feld + 5×3-Raster (EZ | Drittel | EZ × unten/
            Mitte/oben). Rote Zellen = dort fehlen Anker. */}
        {coverage && (coverage.anchors || []).length > 0 && (() => {
          const L = coverage.field?.length_m || 100;
          const W = coverage.field?.width_m || 37;
          const S = 6; // SVG-Skala px/m
          return (
            <div
              data-testid="anchor-coverage-panel"
              className="border border-emerald-500/30 bg-emerald-500/5 rounded-sm p-3 space-y-2"
            >
              <div className="flex items-baseline justify-between gap-2">
                <div className="font-mono text-[10px] uppercase tracking-widest text-emerald-300">
                  Anker-Feld-Abdeckung
                </div>
                <div
                  data-testid="anchor-coverage-uncovered-count"
                  className={`font-mono text-[10px] ${coverage.n_uncovered > 0 ? "text-rose-300" : "text-emerald-300"}`}
                >
                  {coverage.n_uncovered > 0
                    ? `${coverage.n_uncovered}/15 Zonen ohne Anker`
                    : "Alle 15 Zonen abgedeckt ✓"}
                </div>
              </div>
              <svg
                data-testid="anchor-coverage-svg"
                viewBox={`0 0 ${L * S} ${W * S}`}
                className="w-full rounded-sm"
                style={{ background: "#0a1f15" }}
              >
                {(coverage.cells || []).map((cell) => (
                  <rect
                    key={`${cell.col}-${cell.row}`}
                    data-testid={`anchor-coverage-cell-${cell.col}-${cell.row}`}
                    data-covered={cell.covered ? "1" : "0"}
                    x={cell.x0 * S}
                    y={(W - cell.y1) * S}
                    width={(cell.x1 - cell.x0) * S}
                    height={(cell.y1 - cell.y0) * S}
                    fill={cell.covered ? "rgba(52,211,153,0.10)" : "rgba(244,63,94,0.16)"}
                    stroke={cell.covered ? "rgba(52,211,153,0.25)" : "rgba(244,63,94,0.45)"}
                    strokeWidth="1"
                    strokeDasharray={cell.covered ? undefined : "5 3"}
                  >
                    <title>{`${cell.label}: ${cell.covered ? `${cell.n_points} Klick(s)` : "kein Anker — hier Linien/Punkte klicken"}`}</title>
                  </rect>
                ))}
                {/* Goallines zur Orientierung */}
                <line x1={(coverage.field?.endzone_m || 18) * S} y1="0"
                      x2={(coverage.field?.endzone_m || 18) * S} y2={W * S}
                      stroke="rgba(0,230,150,0.5)" strokeWidth="1.5" />
                <line x1={(L - (coverage.field?.endzone_m || 18)) * S} y1="0"
                      x2={(L - (coverage.field?.endzone_m || 18)) * S} y2={W * S}
                      stroke="rgba(0,230,150,0.5)" strokeWidth="1.5" />
                {(coverage.anchors || []).flatMap((a) =>
                  (a.points || []).map((p, i) => (
                    <circle
                      key={`${a.anchor_id}-${i}`}
                      cx={Math.min(Math.max(p.x, 0), L) * S}
                      cy={(W - Math.min(Math.max(p.y, 0), W)) * S}
                      r="4" fill="rgba(34,211,238,0.9)"
                      stroke="rgba(255,255,255,0.4)" strokeWidth="0.8"
                    >
                      <title>{`Anker @ ${Number(a.at_time_s).toFixed(2)}s → (${p.x} m, ${p.y} m)`}</title>
                    </circle>
                  )))}
              </svg>
              <div className="font-mono text-[10px] text-muted-foreground leading-snug">
                Punkte = projizierte Anker-Klicks (beste H) · rote Zellen = ohne Abdeckung —
                dort als nächstes Anker setzen (Sidelines/Endzonen zuerst).
              </div>
            </div>
          );
        })()}

        {/* 🆕 2026-08 · Shot-Classifier (Prototyp, VISION Stufe 2):
            Wide → Kalibrierung/3D · Close → nur Tracking · Replay →
            für Stats verwerfen. Manuelles Override gewinnt immer. */}
        {shotList.length > 0 && (
          <div
            data-testid="shot-class-panel"
            className="border border-fuchsia-500/30 bg-fuchsia-500/5 rounded-sm p-3 space-y-2"
          >
            <div className="flex items-center justify-between gap-2">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-fuchsia-300">
                  Shot-Klassen (Prototyp)
                </div>
                <div className="font-mono text-[10px] text-muted-foreground">
                  🟢 Wide → Kalibrierung/3D · 🟡 Close → nur Tracking · 🔴 Replay → Stats verwerfen
                </div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <Button
                  data-testid="auto-calibrate-all-btn"
                  size="sm" variant="outline"
                  disabled={autoCalBusy || trackBusy}
                  onClick={onAutoCalibrate}
                  className="font-mono text-[10px] uppercase tracking-widest whitespace-nowrap text-cyan-300 border-cyan-500/40 hover:bg-cyan-500/10"
                  title="Ein-Klick-Pipeline: Shots klassifizieren → DL-Linienerkennung setzt pro Wide-Shot einen Anker (5 Kandidaten-Frames, alle Ehrlichkeits-Gates) → Kamera-Tracking → Pass-Neuberechnung. Manuelle Anker gewinnen immer lokal; dl_auto füllt nur Lücken."
                >
                  {autoCalBusy ? (autoCalProg || "kalibriert…") : "⚡ Auto-Kalibrierung"}
                </Button>
                <Button
                  data-testid="track-camera-btn"
                  size="sm" variant="outline"
                  disabled={trackBusy}
                  onClick={onTrackCamera}
                  className="font-mono text-[10px] uppercase tracking-widest whitespace-nowrap"
                  title="Optical-Flow-Spur pro Wide-Shot (Hintergrund-Job): flüssige Homographien zwischen den Ankern — Referenz ist die Anker-Stützstelle im Shot"
                >
                  {trackBusy ? "trackt…" : "📹 Kamera tracken"}
                </Button>
                <Button
                  data-testid="shot-classify-btn"
                  size="sm" variant="outline"
                  disabled={classifyBusy}
                  onClick={onClassifyShots}
                  className="font-mono text-[10px] uppercase tracking-widest whitespace-nowrap"
                  title="Heuristik pro Shot: Feldgrün + Feldlinien + Spielergrößen (YOLOv8n) · Replay über Zeitlupen-Frame-Duplikate + Scorebug"
                >
                  {classifyBusy ? "klassifiziert…" : "🎬 Shots klassifizieren"}
                </Button>
              </div>
            </div>
            <div className="space-y-1">
              {/* 🆕 NP-Sammel-Aktion: Auto-NP-Funde gebündelt mit
                  Standbild-Kacheln — in einem Rutsch bestätigen. */}
              {npFinds.length > 0 && (
                <div
                  data-testid="np-review-panel"
                  className="border border-slate-400/40 bg-slate-500/10 rounded-sm p-2 mb-2 space-y-2"
                >
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-slate-300">
                      ⏸ Auto-NP-Funde · {npPending.length} offen / {npFinds.length} gesamt
                    </span>
                    {npPending.length > 0 && (
                      <button
                        type="button"
                        data-testid="np-confirm-all-btn"
                        disabled={npBusy}
                        onClick={confirmAllNp}
                        className="ml-auto font-mono text-[10px] uppercase tracking-widest border border-emerald-400/50 text-emerald-300 hover:bg-emerald-400/10 rounded-sm px-2 py-0.5 disabled:opacity-40"
                        title={`Alle offenen Auto-NP-Funde als „np“ bestätigen — manuelle Gold-Labels, überleben jede Re-Klassifikation`}
                      >
                        {npBusy ? "läuft…" : `✓ Alle ${npPending.length} bestätigen`}
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {npFinds.map((s) => {
                      const mid = (Number(s.start_s) + Number(s.end_s)) / 2;
                      const sug = s.np_suggestion;
                      const decided = !!s.class_manual;
                      return (
                        <div
                          key={`np-${s.shot_id}`}
                          data-testid={`np-review-card-${s.shot_id}`}
                          className={`border rounded-sm overflow-hidden ${decided ? "border-border opacity-70" : "border-slate-400/50"}`}
                        >
                          <PreviewTile
                            testId={`np-review-tile-${s.shot_id}`}
                            src={frameUrl(sequenceId, mid)}
                            alt={`Shot ${s.shot_id} Standbild`}
                            caption={`Shot ${s.shot_id} · ${Number(s.start_s).toFixed(2)}–${Number(s.end_s).toFixed(2)} s — Auto-NP: Bewegung ${sug.motion_med}, ${sug.duration_s} s, keine Pässe (vorher: ${sug.prev_label})`}
                            tileClass="w-full h-auto"
                          />
                          <div className="flex items-center gap-1.5 px-1.5 py-1 font-mono text-[10px]">
                            <button
                              type="button"
                              data-testid={`np-review-jump-${s.shot_id}`}
                              onClick={() => jumpToVideo(mid, `Shot ${s.shot_id} @ ${mid.toFixed(1)} s — NP prüfen`)}
                              className="text-cyan-300 hover:text-cyan-100 shrink-0"
                              title="Im Video zu diesem Shot springen"
                            >▶</button>
                            <span className="text-slate-300 shrink-0">Shot {s.shot_id}</span>
                            <span
                              className="tabular-nums text-muted-foreground truncate"
                              title={`Bewegung ${sug.motion_med} · Dauer ${sug.duration_s} s · vorher ${sug.prev_label}`}
                            >
                              {sug.duration_s}s · mot {sug.motion_med}
                            </span>
                            {decided ? (
                              <span
                                data-testid={`np-review-decided-${s.shot_id}`}
                                className="ml-auto shrink-0 border border-border rounded-sm px-1 text-muted-foreground"
                                title={`Bereits manuell entschieden: ${s.class_manual} ✋ — Override gewinnt gegen Auto-NP; Änderung über das Dropdown in der Shot-Liste`}
                              >
                                ✋ {s.class_manual}
                              </span>
                            ) : (
                              <span className="ml-auto flex items-center gap-1 shrink-0">
                                <button
                                  type="button"
                                  data-testid={`np-confirm-${s.shot_id}`}
                                  disabled={npBusy}
                                  onClick={() => confirmNp(s.shot_id, "np")}
                                  className="border border-emerald-400/50 text-emerald-300 hover:bg-emerald-400/10 rounded-sm px-1.5 py-0.5 disabled:opacity-40"
                                  title={`NP bestätigen — manuelles Gold-Label „np“ (kein Tracking, keine Stats)`}
                                >✓ NP</button>
                                <button
                                  type="button"
                                  data-testid={`np-reject-${s.shot_id}`}
                                  disabled={npBusy}
                                  onClick={() => confirmNp(s.shot_id, sug.prev_label || "wide")}
                                  className="border border-rose-400/50 text-rose-300 hover:bg-rose-400/10 rounded-sm px-1.5 py-0.5 disabled:opacity-40"
                                  title={`Kein NP — zurück auf „${sug.prev_label || "wide"}" als manuelles Gold-Label`}
                                >✕</button>
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="font-mono text-[9px] text-muted-foreground">
                    Auto-NP = kein Pass-Release + geringe Bewegung + ≥ 8 s (Timeout/Aufstellung).
                    ✓/✕ setzt ein manuelles Gold-Label — überlebt Re-Läufe und füttert den Trainingspool.
                  </div>
                </div>
              )}
              {shotList.map((s) => {
                const eff = s.class_effective;
                const meta = eff ? SHOT_CLASS_META[eff] : null;
                return (
                  <div
                    key={s.shot_id}
                    data-testid={`shot-class-row-${s.shot_id}`}
                    className="flex items-center gap-2 font-mono text-[10px]"
                  >
                    <span className="text-cyan-300 shrink-0 w-14">Shot {s.shot_id}</span>
                    <button
                      type="button"
                      data-testid={`shot-jump-${s.shot_id}`}
                      onClick={() => jumpToVideo(
                        (Number(s.start_s) + Number(s.end_s)) / 2,
                        `Shot ${s.shot_id} @ ${((Number(s.start_s) + Number(s.end_s)) / 2).toFixed(2)} s — Anker prüfen, Klasse direkt am Video setzen`,
                      )}
                      className="text-cyan-400/70 hover:text-cyan-200 shrink-0"
                      title="Ins Video springen (Shot-Mitte) — Anker sehen/anpassen, Klasse oben am Video setzen"
                    >
                      ▶
                    </button>
                    <span className="text-muted-foreground shrink-0 tabular-nums">
                      {/* Zwei Stellen (Alexander, 23.09.2026): bei 30 fps
                          hat jedes Bild eine eigene Zeit mit zwei Stellen
                          — so passt die Liste zu den Messtabellen und zu
                          den 1f-Spruengen (0,03 s). */}
                      {bothSpans(s.start_s, s.end_s, 2)}
                    </span>
                    <span
                      data-testid={`shot-class-badge-${s.shot_id}`}
                      data-class={eff || "none"}
                      title={shotClassTooltip(s)}
                      className={`border rounded-sm px-1.5 py-0.5 ${meta ? meta.cls : "text-muted-foreground border-border"}`}
                    >
                      {meta
                        ? `${meta.dot} ${meta.label}${s.class_manual ? " ✋" : ""}${
                            !s.class_manual && s.class_auto?.confidence != null
                              ? ` ${Math.round(s.class_auto.confidence * 100)} %` : ""}`
                        : "— nicht klassifiziert"}
                    </span>
                    {/* 🆕 2026-08-22 · Prüfliste: hier lag einmal eine manuelle
                        Entscheidung, die die neue Segmentierung nicht mehr
                        eindeutig zuordnen konnte (modules/shot_remap.py).
                        Absichtlich NICHT geraten — der Mensch entscheidet neu. */}
                    {s.needs_review?.length > 0 && (
                      <button
                        type="button"
                        data-testid={`shot-needs-review-${s.shot_id}`}
                        onClick={() => jumpToVideo(
                          (Number(s.start_s) + Number(s.end_s)) / 2,
                          `Shot ${s.shot_id}: frühere Entscheidung prüfen — Klasse neu setzen`,
                        )}
                        className="shrink-0 border border-amber-400/50 bg-amber-400/10 text-amber-300 rounded-sm px-1.5 py-0.5 hover:bg-amber-400/20"
                        title={[
                          "Zu prüfen — die Shot-Grenzen haben sich hier geändert, deshalb wurde die alte Entscheidung NICHT übernommen (kein geratenes Gold-Label):",
                          ...s.needs_review.map((q) => `• ${REVIEW_FIELD_LABEL[q.field] || q.field} „${typeof q.value === "object" ? JSON.stringify(q.value) : q.value}“ für ${Number(q.start_s).toFixed(2)}–${Number(q.end_s).toFixed(2)} s (${q.reason})`),
                          "Neu setzen erledigt den Eintrag. Klick springt in die Shot-Mitte.",
                        ].join("\n")}
                      >
                        ⚠ zu prüfen{s.needs_review.length > 1 ? ` (${s.needs_review.length})` : ""}
                      </button>
                    )}
                    {/* 🆕 2026-08-23 · Hinweis auf einen verborgenen Schnitt.
                        Klick springt zum Kandidaten; geteilt wird oben am
                        Abspielkopf, damit man das Bild vorher sieht. */}
                    {s.cut_hints?.length > 0 && (
                      <button
                        type="button"
                        data-testid={`shot-cut-hints-${s.shot_id}`}
                        onClick={() => {
                          setT(Number(s.cut_hints[0].t), true);
                          toast.info(`Kandidat bei ${Number(s.cut_hints[0].t).toFixed(2)} s — ansehen, dann oben »hier teilen«`);
                        }}
                        className="shrink-0 border border-amber-400/50 bg-amber-400/10 text-amber-300 rounded-sm px-1.5 py-0.5 hover:bg-amber-400/20"
                        title={[
                          `Möglicher verborgener Schnitt in diesem Shot — ein Vorschlag des Histogramm-Detektors, kein Befund:`,
                          ...s.cut_hints.map((h) => `• ${Number(h.t).toFixed(2)} s (Schwelle ${h.threshold})`),
                          "Klick springt zum ersten Kandidaten. Geteilt wird oben am Abspielkopf, wenn das Bild es bestätigt.",
                        ].join("\n")}
                      >
                        ⚑ Schnitt?{s.cut_hints.length > 1 ? ` (${s.cut_hints.length})` : ""}
                      </button>
                    )}
                    {s.split_start && (
                      <span
                        data-testid={`shot-split-start-${s.shot_id}`}
                        className="shrink-0 text-amber-300/70"
                        title="Die Anfangsgrenze dieses Shots wurde von Hand gesetzt, nicht vom Detektor. Sie übersteht einen neuen Shot-Scan und lässt sich am Abspielkopf zurücknehmen."
                      >
                        ✂
                      </span>
                    )}
                    {s.replay_of && (
                      <span
                        className="relative shrink-0"
                        onMouseEnter={() => setReplayPreviewSid(s.shot_id)}
                        onMouseLeave={() => setReplayPreviewSid(null)}
                      >
                        <button
                          type="button"
                          data-testid={`shot-replay-link-${s.shot_id}`}
                          data-uncertain={s.replay_of.uncertain ? "true" : "false"}
                          data-confirmed={s.replay_of.confirmed ? "true" : "false"}
                          onClick={() => {
                            setT(Number(s.replay_of.t_abs), true);
                            toast.info(`Sprung zum Live-Pass @ ${Number(s.replay_of.t_abs).toFixed(1)} s`);
                          }}
                          className={`${s.replay_of.confirmed
                            ? "text-emerald-300 hover:text-emerald-200"
                            : s.replay_of.uncertain
                            ? "text-amber-300 hover:text-amber-200"
                            : "text-rose-300/90 hover:text-rose-200"} underline decoration-dotted underline-offset-2 cursor-pointer`}
                          title={`Replay-Duplikat-Finder: Dieser Shot wiederholt vermutlich den Live-Pass ${s.replay_of.gap_s} s davor${s.replay_of.length_m ? ` (${Number(s.replay_of.length_m).toFixed(1)} m)` : ""} · Match: ${s.replay_of.match_method || "zeitbasiert"}${s.replay_of.visual_score != null ? ` (${s.replay_of.visual_score} Bildmerkmale)` : ""}${s.replay_of.confirmed ? " · ✓ manuell bestätigt (Gold-Label)" : s.replay_of.uncertain ? " · ⚠ visuell NICHT bestätigt — bitte manuell prüfen" : ""} · Klick springt im Video zum Live-Pass @ ${Number(s.replay_of.t_abs).toFixed(1)} s`}
                        >
                          {s.replay_of.confirmed ? "✓ " : s.replay_of.uncertain ? "⚠ " : ""}↩︎ wdh. Pass ({s.replay_of.gap_s} s davor)
                        </button>
                        <button
                          type="button"
                          data-testid={`replay-confirm-btn-${s.shot_id}`}
                          onClick={() => onConfirmReplay(s.shot_id, !s.replay_of.confirmed)}
                          className={`ml-1 border rounded-sm px-1 leading-tight ${s.replay_of.confirmed
                            ? "border-emerald-400/50 text-emerald-300 hover:bg-emerald-400/10"
                            : "border-border text-muted-foreground hover:text-emerald-300 hover:border-emerald-400/50"}`}
                          title={s.replay_of.confirmed
                            ? "Bestätigung zurücknehmen — Link wird wieder automatisch berechnet, Gold-Label entfällt"
                            : "Replay-Link nach manueller Prüfung bestätigen — überlebt Re-Läufe und fließt als Gold-Label in den CNN-Trainingspool"}
                        >
                          {s.replay_of.confirmed ? "✕" : "✓"}
                        </button>
                        {replayPreviewSid === s.shot_id && (
                          <span
                            data-testid={`replay-preview-${s.shot_id}`}
                            className="absolute left-0 bottom-full mb-1 z-50 block border border-border bg-card rounded-sm shadow-xl p-1 w-56"
                          >
                            <img
                              src={frameUrl(sequenceId, Number(s.replay_of.t_abs) + 0.4)}
                              alt="Standbild des verlinkten Live-Passes"
                              className="w-full rounded-[2px] bg-secondary min-h-[60px]"
                            />
                            <span className="block font-mono text-[9px] text-muted-foreground pt-0.5">
                              Live-Pass @ {Number(s.replay_of.t_abs).toFixed(1)} s · Klick springt hin
                            </span>
                          </span>
                        )}
                      </span>
                    )}
                    {s.track && (
                      <span
                        data-testid={`shot-track-coverage-${s.shot_id}`}
                        className="text-cyan-300 shrink-0"
                        title={`Kamera-Track: ${Math.round((s.track.coverage || 0) * 100)} % des Shots abgedeckt · ${s.track.n_refs || 1} Referenz${(s.track.n_refs || 1) === 1 ? "" : "en"} · ${s.track.n_entries} Stützstellen`}
                      >
                        📹 {Math.round((s.track.coverage || 0) * 100)} %
                      </span>
                    )}
                    <select
                      data-testid={`shot-class-override-${s.shot_id}`}
                      value={s.class_manual || ""}
                      onChange={(e) => onOverrideShotClass(s.shot_id, e.target.value)}
                      className="ml-auto bg-secondary border border-border rounded-sm px-1 py-0.5 text-[10px] text-foreground/80"
                      title="Manuelles Override — gewinnt immer gegen Auto. Übersteht eine Re-Klassifikation, und seit 22.08.2026 auch einen neuen Shot-Scan, solange die Shot-Grenzen gleich bleiben. Wird der Shot geteilt oder verschmolzen, wandert die Entscheidung in die Prüfliste (⚠ zu prüfen) statt geraten zu werden."
                    >
                      <option value="">
                        {s.class_auto?.label
                          ? `Auto (${s.class_auto.label} ${Math.round((s.class_auto.confidence || 0) * 100)} %)`
                          : "Auto"}
                      </option>
                      <option value="wide">Wide</option>
                      <option value="close">Close</option>
                      <option value="replay">Replay</option>
                      <option value="ads">Ads</option>
                      <option value="np">NP (kein Spiel)</option>
                      <option value="wide_np">Wide ohne Spiel</option>
                      <option value="drone">Drohne (2. Kamera)</option>
                      <option value="unsicher">Unsicher</option>
                    </select>
                  </div>
                );
              })}
            </div>
            {/* 🆕 Auto-Kalibrierungs-Report: ehrliche Bilanz + Sprung-Links
                zu Shots, die noch einen manuellen Anker brauchen */}
            {shotsInfo?.autocal && (
              <div
                data-testid="autocal-report"
                className="border border-cyan-500/30 bg-cyan-500/5 rounded-sm p-2 space-y-1"
              >
                <div className="font-mono text-[10px] uppercase tracking-widest text-cyan-300">
                  ⚡ Auto-Kalibrierung · {shotsInfo.autocal.n_calibrated}/{shotsInfo.autocal.n_wide} Wide-Shots kalibriert · {shotsInfo.autocal.n_skipped} übersprungen · {shotsInfo.autocal.n_open} brauchen dich
                  {shotsInfo.autocal.n_dl_anchors > 0 ? ` · ${shotsInfo.autocal.n_dl_anchors} DL-Anker` : ""}
                  {shotsInfo.autocal.n_dl_rejected > 0 ? ` · ${shotsInfo.autocal.n_dl_rejected} verworfen (Spur-Widerspruch)` : ""}
                  {shotsInfo.autocal.n_manual_only > 0 ? ` · ✓ ${shotsInfo.autocal.n_manual_only} manuell` : ""}
                  {shotsInfo.autocal.n_seg_only > 0 ? ` · ${shotsInfo.autocal.n_seg_only} ohne Lösung` : ""}
                </div>
                {shotsInfo.label_harvest?.n_written > 0 && (
                  <div
                    data-testid="label-harvest-status"
                    className="font-mono text-[10px] text-emerald-300/80"
                  >
                    🌱 Gold-Label-Datensatz: {shotsInfo.label_harvest.n_written} Frames
                    {shotsInfo.label_harvest.n_track_samples > 0 ? ` (davon ${shotsInfo.label_harvest.n_track_samples} track-propagiert)` : ""}
                    {" "}· wächst automatisch mit jedem manuellen Anker
                  </div>
                )}
                {(() => {
                  const byStatus = (st) => (shotsInfo.autocal.shots || []).filter((e) => e.status === st);
                  const manualDone = byStatus("manuell");
                  const segOnly = byStatus("seg_only");
                  const todo = byStatus("offen");
                  return (
                    <>
                      {manualDone.length > 0 && (
                        <div
                          data-testid="autocal-done-line"
                          className="font-mono text-[10px] text-emerald-300"
                        >
                          ✓ manuell erledigt: Shot {manualDone.map((e) => e.shot_id).join(", ")}
                        </div>
                      )}
                      {segOnly.map((e) => (
                        <div
                          key={`ac-seg-${e.shot_id}`}
                          data-testid={`autocal-segonly-${e.shot_id}`}
                          className="flex items-center gap-2 font-mono text-[10px]"
                        >
                          <span className="text-amber-300/90">
                            Shot {e.shot_id} · {Number(e.start_s).toFixed(2)}–{Number(e.end_s).toFixed(2)} s · Anker gezeichnet, aber noch keine Lösung (seg_only) — weitere Linienart nachziehen oder unten »seg_only-Anker neu lösen«
                          </span>
                          <button
                            type="button"
                            data-testid={`autocal-jump-${e.shot_id}`}
                            onClick={() => {
                              const mid = (Number(e.start_s) + Number(e.end_s)) / 2;
                              jumpToVideo(mid, `@ ${mid.toFixed(1)} s — Linien prüfen/ergänzen, dann unten »seg_only-Anker neu lösen«`);
                            }}
                            className="ml-auto border border-amber-400/50 rounded-sm px-1.5 py-0.5 text-amber-200 hover:bg-amber-400/10 whitespace-nowrap"
                          >
                            → hinspringen
                          </button>
                        </div>
                      ))}
                      {todo.slice(0, 8).map((e) => (
                        <div
                          key={`ac-open-${e.shot_id}`}
                          className="flex items-center gap-2 font-mono text-[10px]"
                        >
                          <span className="text-muted-foreground">
                            Shot {e.shot_id} · {Number(e.start_s).toFixed(2)}–{Number(e.end_s).toFixed(2)} s · DL fand in 0/5 Frames Linien
                          </span>
                          <button
                            type="button"
                            data-testid={`autocal-jump-${e.shot_id}`}
                            onClick={() => {
                              const mid = (Number(e.start_s) + Number(e.end_s)) / 2;
                              jumpToVideo(mid, `@ ${mid.toFixed(1)} s — hier manuell einen Anker setzen`);
                            }}
                            className="ml-auto border border-cyan-400/50 rounded-sm px-1.5 py-0.5 text-cyan-200 hover:bg-cyan-400/10 whitespace-nowrap"
                          >
                            → Anker setzen
                          </button>
                          <button
                            type="button"
                            data-testid={`autocal-not-wide-${e.shot_id}`}
                            onClick={async () => {
                              try {
                                await setShotClass(sequenceId, e.shot_id, "close");
                                await loadShots();
                                toast.success(`Shot ${e.shot_id} → Close markiert — Gold-Label für den Trainingspool`);
                              } catch (err) {
                                toast.error(fehlerText(err) || "Umklassifizierung fehlgeschlagen");
                              }
                            }}
                            className="border border-amber-400/50 rounded-sm px-1.5 py-0.5 text-amber-200 hover:bg-amber-400/10 whitespace-nowrap"
                            title="0/5 DL-Treffer ist oft ein Zeichen für Nahaufnahme/Diskussion statt Wide — Klick setzt das manuelle Override auf Close (Gold-Label fürs CNN-Training, Shot verschwindet aus dieser Liste)"
                          >
                            ✕ kein Wide
                          </button>
                        </div>
                      ))}
                      {todo.length > 8 && (
                        <div className="font-mono text-[9px] text-muted-foreground">
                          … +{todo.length - 8} weitere offene Shots
                        </div>
                      )}
                    </>
                  );
                })()}
                {shotsInfo.autocal.n_monitoring > 0 && (
                  <div className="font-mono text-[9px] text-muted-foreground">
                    Monitoring: {shotsInfo.autocal.n_monitoring} Shot(s) mit manuellem Anker — DL rechnete mit (manuell gewinnt)
                    {(() => {
                      const devs = (shotsInfo.autocal.shots || [])
                        .filter((e) => e.status === "monitoring" && e.deviation_px != null);
                      return devs.length
                        ? ` · Abweichung Ø ${(devs.reduce((a, e) => a + e.deviation_px, 0) / devs.length).toFixed(1)} px`
                        : "";
                    })()}
                  </div>
                )}
              </div>
            )}
            {/* 🆕 Ampel-Warnhinweis: rote Track-Segmente → Ein-Klick-Sprung
                zur Segmentmitte, dort Anker setzen + Tracking neu ausführen */}
            {(() => {
              const redSegs = shotList.flatMap((s) =>
                ((s.track?.segments) || [])
                  .filter((g) => !Array.isArray(g) && g.grade === "rot")
                  .map((g) => ({ ...g, shot_id: s.shot_id })));
              if (!redSegs.length) return null;
              return (
                <div
                  data-testid="track-quality-warning"
                  className="border border-rose-500/40 bg-rose-500/5 rounded-sm p-2 space-y-1"
                >
                  <div className="font-mono text-[10px] uppercase tracking-widest text-rose-300">
                    ⚠ Track-Qualität rot — Anker nachziehen empfohlen
                  </div>
                  {redSegs.map((g, i) => (
                    <div
                      key={`redseg-${g.shot_id}-${i}`}
                      className="flex items-center gap-2 font-mono text-[10px]"
                    >
                      <span className="text-muted-foreground">
                        Shot {g.shot_id} · {Number(g.t0).toFixed(1)}–{Number(g.t1).toFixed(1)} s
                        {g.med_inliers != null ? ` · nur ${g.med_inliers} Inlier (median)` : ""}
                      </span>
                      <button
                        type="button"
                        data-testid={`track-quality-fix-${g.shot_id}-${i}`}
                        onClick={() => {
                          const mid = (Number(g.t0) + Number(g.t1)) / 2;
                          jumpToVideo(mid, `@ ${mid.toFixed(1)} s — hier einen Kalibrier-Anker setzen, danach »Kamera tracken« neu ausführen`);
                        }}
                        className="ml-auto border border-rose-400/50 rounded-sm px-1.5 py-0.5 text-rose-200 hover:bg-rose-400/10 whitespace-nowrap"
                        title="Springt zur Mitte des schwachen Segments — dort einen zusätzlichen Kalibrier-Anker setzen, dann Tracking neu ausführen"
                      >
                        → hinspringen & Anker setzen
                      </button>
                    </div>
                  ))}
                </div>
              );
            })()}
            <div className="font-mono text-[10px] text-muted-foreground leading-snug">
              Heuristik-Prototyp: Feldgrün-Anteil + Feldlinien (Hough) + Spielergrößen
              (YOLOv8n) · Replay via Zeitlupen-Frame-Duplikate · ehrliches
              »Unsicher« statt raten. Dein manuelles Label gewinnt immer.
            </div>
          </div>
        )}

        {/* 🆕 2026-08 · Adaptives Feldmodell — Grid-Search + Konsens.
            Nur für adaptive Profile (Adult_Outdoor). Sequenz-weit. */}
        {sequence?.game_config?.field_profile_id === "Adult_Outdoor" && (
          <div
            data-testid="adapt-field-dims-panel"
            className="border border-amber-500/40 bg-amber-500/5 rounded-sm p-3 space-y-2"
          >
            <div className="flex items-center justify-between gap-2">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-amber-300">
                  Adaptives Feldmodell
                </div>
                <div className="font-mono text-[10px] text-muted-foreground">
                  Preset {sequence?.game_config?.field_length_m ?? "?"} × {sequence?.game_config?.field_width_m ?? "?"} m ·
                  W ∈ [30…37] m · L_mid ∈ [50…64] m
                </div>
              </div>
              <Button
                data-testid="adapt-field-dims-btn"
                size="sm" variant="outline"
                disabled={adaptBusy || adaptApplying}
                onClick={() => runAdaptFieldDims(false)}
                className="font-mono text-[10px] uppercase tracking-widest whitespace-nowrap"
                title="Coarse-to-Fine Grid-Search + Median-Konsens über alle Free-Line-Anker der Sequenz"
              >
                <Ruler className="w-3.5 h-3.5 mr-1.5" />
                {adaptBusy ? "läuft…" : "Feldmaße schätzen"}
              </Button>
            </div>
            {adaptProg && (
              <div
                data-testid="adapt-field-dims-progress"
                className="font-mono text-[10px] text-amber-200"
              >
                {adaptProg.pct}% · {adaptProg.message}
              </div>
            )}
            {adaptResult && !adaptBusy && !adaptApplying && (
              <div
                data-testid="adapt-field-dims-result"
                className="space-y-1.5 pt-1 border-t border-amber-500/20"
              >
                {adaptResult.consensus ? (
                  <>
                    <div
                      data-testid="adapt-field-dims-consensus"
                      className="font-mono text-[11px] text-cyan-200"
                    >
                      Konsens · <span className="font-bold">W = {adaptResult.consensus.W} m</span>
                      {" · "}
                      <span className="font-bold">L = {adaptResult.consensus.L} m</span>
                      {" (L_mid = "}{adaptResult.consensus.L_mid}{" m)"}
                    </div>
                    <div className="font-mono text-[10px] text-muted-foreground">
                      {adaptResult.n_suggestions_valid}/{adaptResult.n_anchors_input} Anker · W-Median {adaptResult.W_median != null ? `${adaptResult.W_median.toFixed(2)} m` : "—"} · L_mid-Median {adaptResult.L_mid_median != null ? `${adaptResult.L_mid_median.toFixed(2)} m` : "—"} · Reproj-Median {adaptResult.reproj_median_px != null ? `${adaptResult.reproj_median_px.toFixed(2)} px` : "—"}
                    </div>
                    {(adaptResult.consensus.dims_adapted || []).length < 2 && (
                      <div
                        data-testid="adapt-field-dims-partial"
                        className="font-mono text-[10px] text-amber-200"
                      >
                        ⚠ Nur {(adaptResult.consensus.dims_adapted || []).join(", ") || "—"} beobachtbar — die andere Dimension bleibt beim Preset (Linien-Kombinationen decken sie nicht ab).
                      </div>
                    )}
                  </>
                ) : (
                  <div
                    data-testid="adapt-field-dims-no-consensus"
                    className="font-mono text-[10px] text-rose-300"
                  >
                    Kein Konsens: {adaptResult.reason || adaptResult.reason_no_consensus || "unbekannt"}
                    {adaptResult.n_suggestions_valid != null && ` (${adaptResult.n_suggestions_valid}/${adaptResult.n_anchors_input} Vorschläge gültig)`}
                  </div>
                )}
                {adaptResult.boundary_hint && (
                  <div
                    data-testid="adapt-field-dims-boundary-hint"
                    className="border-l-2 border-amber-400 bg-amber-400/10 rounded-sm p-1.5 font-mono text-[10px] leading-snug text-amber-200"
                  >
                    ⚠ {adaptResult.boundary_hint}
                  </div>
                )}
                {adaptResult.fov_hint?.warning && (
                  <div
                    data-testid="adapt-field-dims-fov-warning"
                    className="border-l-2 border-rose-400 bg-rose-400/10 rounded-sm p-1.5 font-mono text-[10px] leading-snug text-rose-200 space-y-1.5"
                  >
                    <div>⚠ {adaptResult.fov_hint.warning}</div>
                    {adaptResult.fov_hint.dlt_estimate_median_deg != null && (
                      <Button
                        data-testid="adapt-field-dims-fov-accept"
                        size="sm" variant="outline"
                        onClick={() => acceptFovOverrideAndRerun(adaptResult.fov_hint.dlt_estimate_median_deg)}
                        disabled={adaptBusy || adaptApplying}
                        className="w-full font-mono text-[10px] uppercase tracking-widest border-rose-400/60 text-rose-200 hover:bg-rose-400/10"
                        title="Übernimmt den DLT-Median als camera_fov_override und startet den Grid-Search sofort neu."
                      >
                        DLT-Schätzung {adaptResult.fov_hint.dlt_estimate_median_deg}° übernehmen & Job neu starten
                      </Button>
                    )}
                  </div>
                )}
                {adaptResult.consensus && !adaptResult.applied && (
                  <div className="flex gap-2 pt-1">
                    <Button
                      data-testid="adapt-field-dims-apply"
                      size="sm" className="flex-1"
                      disabled={adaptApplying || adaptBusy}
                      onClick={() => runAdaptFieldDims(true)}
                    >
                      <Check className="w-3.5 h-3.5 mr-1.5" />
                      {adaptApplying ? "wird übernommen…" : "Übernehmen"}
                    </Button>
                    <Button
                      data-testid="adapt-field-dims-dismiss"
                      size="sm" variant="ghost" className="flex-1"
                      onClick={() => setAdaptResult(null)}
                      disabled={adaptApplying || adaptBusy}
                    >
                      Verwerfen
                    </Button>
                  </div>
                )}
                {adaptResult.applied && (
                  <div
                    data-testid="adapt-field-dims-applied"
                    className="font-mono text-[10px] text-emerald-300"
                  >
                    ✓ Konsens angewendet · {adaptResult.n_anchors_resolved || 0} Anker neu gelöst
                    {(adaptResult.skipped || []).length > 0 && ` · ${adaptResult.skipped.length} übersprungen`}
                  </div>
                )}
              </div>
            )}
          </div>
        )}


        {/* 🆕 seg_only-Anker neu lösen — nach FOV-/Feldmaß-Änderung
            laufen sie erneut durch den Kandidaten-Wettbewerb */}
        {segOnlyResolvable > 0 && (
          <Button
            data-testid="resolve-seg-only-btn"
            size="sm" variant="outline" className="w-full"
            disabled={resolvingSegOnly}
            onClick={onResolveSegOnly}
            title="Schickt alle seg_only-Anker erneut durch den Solver — mit dem AKTUELL gesetzten FOV und den aktuellen Feldmaßen. Nur Lösungen unter dem 30-px-Gate werden joint."
          >
            <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
            {resolvingSegOnly
              ? "Neu-Lösen läuft…"
              : `seg_only-Anker neu lösen (${segOnlyResolvable})`}
          </Button>
        )}

        {/* Anker-Liste / 🆕 Anker-Galerie (Raster): alle Kacheln
            nebeneinander — Ausreißer (Raster nicht auf den Linien)
            springen beim Durchscrollen sofort ins Auge. */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Gesetzte Anker
            </div>
            <div className="flex items-center gap-1">
              {[["liste", "Liste"], ["raster", "Raster"]].map(([v, lbl]) => (
                <button
                  key={v}
                  type="button"
                  data-testid={`anchor-view-${v}`}
                  onClick={() => setAnchorView(v)}
                  className={`font-mono text-[9px] uppercase tracking-widest border rounded-sm px-1.5 py-0.5 transition-colors ${
                    anchorView === v
                      ? "border-cyan-400/60 bg-cyan-400/10 text-cyan-300"
                      : "border-border text-muted-foreground hover:text-foreground"
                  }`}
                  title={v === "raster"
                    ? "Galerie-Raster: alle Anker-Kacheln nebeneinander — falsche Feldraster fallen sofort auf"
                    : "Kompakte Listenansicht mit allen Details"}
                >{lbl}</button>
              ))}
            </div>
          </div>
          {anchorView === "raster" && (
            <>
              {/* 🆕 Sortierung: die schlechtesten Anker zuerst */}
              <div className="flex items-center gap-1 mb-1.5 font-mono text-[9px] uppercase tracking-widest">
                <span className="text-muted-foreground mr-0.5">Sortierung:</span>
                {[["zeit", "Zeit"], ["fehler", "Fehler-px"], ["ampel", "Ampel"]].map(([v, lbl]) => (
                  <button
                    key={`gs-${v}`}
                    type="button"
                    data-testid={`gallery-sort-${v}`}
                    onClick={() => setGallerySort(v)}
                    className={`border rounded-sm px-1.5 py-0.5 transition-colors ${
                      gallerySort === v
                        ? "border-cyan-400/60 bg-cyan-400/10 text-cyan-300"
                        : "border-border text-muted-foreground hover:text-foreground"
                    }`}
                    title={v === "fehler"
                      ? "Höchster Reprojektionsfehler zuerst — die schlechtesten Anker ganz oben"
                      : v === "ampel"
                      ? "Rot → Gelb → Grün (bei Gleichstand: höherer Fehler zuerst)"
                      : "Chronologisch nach Anker-Zeitpunkt"}
                  >{lbl}</button>
                ))}
              </div>
              <div
                data-testid="anchor-gallery"
                className="grid grid-cols-2 gap-2 max-h-[560px] overflow-y-auto pr-1"
              >
                {galleryList.map((a, gi) => (
                  <div
                    key={a.id}
                    data-testid={`anchor-gallery-card-${gi}`}
                    className="border border-border rounded-sm bg-secondary/40 overflow-hidden"
                  >
                    <PreviewTile
                      testId={`anchor-gallery-tile-${gi}`}
                      src={anchorPreviewUrl(sequenceId, a.id)}
                      alt={`Anker @ ${Number(a.at_time_s || 0).toFixed(2)} s`}
                      caption={`Anker @ ${Number(a.at_time_s || 0).toFixed(2)} s (${a.source || "?"}) — Raster = projizierte Kalibrierung; liegt es nicht auf den Feldlinien, Anker löschen`}
                      tileClass="w-full h-auto"
                    />
                    <div className="flex items-center gap-1.5 px-1.5 py-1 font-mono text-[10px]">
                      <button
                        type="button"
                        data-testid={`anchor-gallery-jump-${gi}`}
                        onClick={() => jumpToVideo(
                          Number(a.at_time_s || 0),
                          `Anker @ ${Number(a.at_time_s || 0).toFixed(2)} s im Video`,
                        )}
                        className="text-cyan-300 hover:text-cyan-100 shrink-0"
                        title="Im Video zu diesem Anker springen"
                      >▶</button>
                      <span className="tabular-nums text-muted-foreground shrink-0">
                        {bothTimes(a.at_time_s)}
                      </span>
                      {a.quality?.grade && (
                        <span
                          data-testid={`anchor-gallery-quality-${gi}`}
                          data-grade={a.quality.grade}
                          title={`3D-Ampel: ${a.quality.label_de || a.quality.grade}`}
                          className={`w-2 h-2 rounded-full shrink-0 ${
                            a.quality.grade === "gruen" ? "bg-emerald-400"
                            : a.quality.grade === "gelb" ? "bg-amber-400"
                            : "bg-rose-500"
                          }`}
                        />
                      )}
                      <span className="truncate text-muted-foreground">
                        {a.source === "dl_auto" ? "DL-Auto" : a.combo_id || a.preset_id}
                      </span>
                      {a.reproj_error_px != null && (
                        <span className={`shrink-0 ${a.reproj_error_px < 10 ? "text-emerald-300" : "text-rose-300"}`}>
                          {a.reproj_error_px}px
                        </span>
                      )}
                      {a.deletable && (
                        <button
                          data-testid={`anchor-gallery-delete-${gi}`}
                          onClick={() => onDelete(a.id)}
                          className="ml-auto text-rose-300 hover:text-rose-100 shrink-0"
                          title="Anker löschen"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {galleryList.length === 0 && (
                  <div className="col-span-2 text-[12px] text-muted-foreground italic">
                    Keine Anker mit Vorschau (Homographie) vorhanden.
                  </div>
                )}
              </div>
              {anchors.filter((a) => !(a.id && a.has_homography)).length > 0 && (
                <div
                  data-testid="anchor-gallery-hidden-hint"
                  className="font-mono text-[9px] text-muted-foreground mt-1"
                >
                  {anchors.filter((a) => !(a.id && a.has_homography)).length} Anker ohne
                  Vorschaubild (Basis/seg_only) — in der Listenansicht sichtbar.
                </div>
              )}
            </>
          )}
          {anchorView === "liste" && (
          <div data-testid="anchor-collect-list" className="space-y-1 max-h-96 overflow-y-auto pr-1">
            {nDubletten > 0 && (
              <div
                data-testid="anchor-dubletten-hinweis"
                className="border-l-4 border-amber-400 bg-amber-400/10 rounded-sm p-2 text-[11px] leading-relaxed text-amber-200"
              >
                <b>{nDubletten} überzählige{nDubletten === 1 ? "r" : ""} Anker</b>{" "}
                auf schon belegten Frames. Sie bringen nichts Neues, zählen aber
                im Bündelausgleich doppelt und landen mehrfach in den
                Trainingsdaten — unten mit{" "}
                <span className="font-mono">2/2</span> markiert. Den mit dem
                größeren Reprojektionsfehler löschen.
              </div>
            )}
            {anchors.length === 0 && (
              <div className="text-[12px] text-muted-foreground italic">Noch keine Anker.</div>
            )}
            {anchors.map((a, i) => (
              <div
                key={a.id || i}
                data-testid={`anchor-collect-item-${i}`}
                data-anchor-status={a.status || "joint"}
                className={`flex items-center gap-2 border rounded-sm px-2 py-1.5 text-[12px] ${
                  a.has_homography && a.status !== "visual_marker_only"
                    ? "border-emerald-400/50 ring-1 ring-emerald-400/30"
                    : "border-zinc-500/50 ring-1 ring-zinc-500/30"
                }`}
              >
                <Anchor className={`w-3.5 h-3.5 shrink-0 ${
                  a.source === "anchor_collect" ? "text-cyan-300"
                  : a.kind === "base" ? "text-amber-300" : "text-emerald-300"
                }`} />
                {/* 🆕 2026-08 · Anker-Kontroll-Kachel: Standbild mit
                    projiziertem Feldraster — falsche (DL-)Anker per
                    Blick erkennen und direkt löschen. Klick vergrößert. */}
                {a.id && a.has_homography && (
                  <PreviewTile
                    testId={`anchor-preview-tile-${i}`}
                    src={anchorPreviewUrl(sequenceId, a.id)}
                    alt={`Anker-Vorschau t=${Number(a.at_time_s || 0).toFixed(2)} s`}
                    caption={`Anker @ ${Number(a.at_time_s || 0).toFixed(2)} s (${a.source || "?"}) — Raster = projizierte Kalibrierung; liegt es nicht auf den Feldlinien, Anker löschen`}
                    tileClass="h-14 w-auto"
                  />
                )}
                <span className="font-mono tabular-nums text-muted-foreground">
                  t={Number(a.at_time_s || 0).toFixed(2)}s
                </span>
                {/* 🆕 2026-08-30 · Mehrere Anker auf demselben Frame. Sie
                    verdoppeln im Bündelausgleich das Gewicht EINES
                    Zeitpunkts und erzeugen im Trainingsexport denselben
                    Frame mehrfach. */}
                {a.dublette && (
                  <span
                    data-testid={`anchor-dublette-${i}`}
                    className="shrink-0 font-mono text-[10px] px-1 rounded-sm border border-amber-400/60 bg-amber-400/10 text-amber-300"
                    title={`${a.dublette.anzahl} Anker auf diesem Frame — dieser ist Nr. ${a.dublette.rang}. Doppelte zählen im Solver doppelt und landen doppelt in den Trainingsdaten.`}
                  >
                    {a.dublette.rang}/{a.dublette.anzahl}
                  </span>
                )}
                <span className="truncate flex-1">
                  {a.combo_id || a.preset_id}{a.side ? ` (${a.side})` : ""}
                  {a.reproj_error_px != null && (
                    <span className={`ml-1 font-mono text-[10px] ${
                      a.reproj_error_px < 10 ? "text-emerald-300" : "text-rose-300"
                    }`}>
                      {a.reproj_error_px}px
                    </span>
                  )}
                </span>
                {a.quality?.grade && (
                  <span
                    data-testid={`anchor-collect-item-quality-${i}`}
                    data-grade={a.quality.grade}
                    title={
                      `3D-Ampel: ${a.quality.label_de}` +
                      (a.quality.pose_consistency_px != null
                        ? ` · Pose-Roundtrip ${a.quality.pose_consistency_px} px (Gate ${a.quality.gate_green_px} px)`
                        : "") +
                      (a.quality.fov_deg != null
                        ? ` · FOV ${a.quality.fov_deg}°`
                        : "") +
                      (a.quality.grade !== "gruen"
                        ? "\n\nEmpfehlungen:\n" +
                          qualityHints(a.quality, { selfcal: a.selfcal }).map((h) => `• ${h}`).join("\n")
                        : "")
                    }
                    className={`w-2 h-2 rounded-full shrink-0 ${
                      a.quality.grade === "gruen" ? "bg-emerald-400"
                      : a.quality.grade === "gelb" ? "bg-amber-400"
                      : "bg-rose-500"
                    }`}
                  />
                )}
                {a.selfcal?.three_d_ready && (
                  <span
                    data-testid={`anchor-collect-item-selfcal3d-${i}`}
                    className="font-mono text-[9px] px-1 rounded-sm border border-emerald-400/50 text-emerald-300 shrink-0"
                    title={`Selfcal-Inlier · Klick-Residuum ${a.selfcal.residual_rms_px} px — 3D-Flugkurven laufen über die Shot-Selbstkalibrierungs-Kamera, auch wenn die Einzel-H nicht metrisch ist`}
                  >
                    3D✓
                  </span>
                )}
                {(a.shot_id != null || a.fov?.value_deg != null) && (
                  <span
                    data-testid={`anchor-collect-item-shotfov-${i}`}
                    className="font-mono text-[9px] px-1 rounded-sm border border-cyan-400/40 text-cyan-300/90 shrink-0"
                    title={`${a.shot_id != null ? `Shot ${a.shot_id}` : "Shot unbekannt"}${
                      a.fov?.value_deg != null
                        ? ` · Anker-FOV ${a.fov.value_deg}° (Quelle: ${a.fov.source})`
                        : " · kein Anker-FOV"}`}
                  >
                    {a.shot_id != null ? `S${a.shot_id}` : "S?"}
                    {a.fov?.value_deg != null ? `·${Math.round(a.fov.value_deg)}°` : ""}
                  </span>
                )}
                {a.source === "anchor_collect" && (
                  <span
                    data-testid={`anchor-collect-item-badge-${i}`}
                    className="font-mono text-[9px] uppercase px-1 rounded-sm bg-amber-500/20 text-amber-200 border border-amber-400/50"
                    title={`Aus dem Anker-Sammelmodus${a.combo_id ? ` · ${a.combo_id}` : ""}`}
                  >
                    ⚓ Anker
                  </span>
                )}
                <span className={`font-mono text-[9px] uppercase px-1 rounded-sm border ${
                  a.has_homography
                    ? "border-emerald-500/40 text-emerald-300"
                    : "border-border text-muted-foreground"
                }`}>
                  {a.kind === "base" ? "BASIS" : a.status}
                </span>
                {a.deletable && (
                  <button
                    data-testid={`anchor-collect-delete-${i}`}
                    onClick={() => onDelete(a.id)}
                    className="text-rose-300 hover:text-rose-100"
                    title="Anker löschen"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
          )}
        </div>

        <Button
          data-testid="anchor-collect-export"
          disabled={exporting || nEligible === 0}
          onClick={onExport}
          className="w-full bg-emerald-500 text-black hover:bg-emerald-400 font-display font-bold uppercase tracking-widest disabled:opacity-40"
        >
          <UploadCloud className="w-4 h-4 mr-2" />
          {exporting ? (exportMsg || "Export läuft…") : "An Trainingspool übergeben"}
        </Button>
      </div>
    </div>
    </>
  );
}
